/**
 * Procedural geometry for the cleanroom and its tools.
 *
 * Everything here is built from primitives and canvas-drawn textures - no
 * external model or image assets. That is a deployment decision as much as an
 * aesthetic one: the project promises that nothing is gated behind hardware and
 * that the whole environment runs on ordinary classroom computers, which means
 * it has to load fast on a school network and work offline.
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import { BALLROOM, onHallChange } from './hall'
import { SUBFAB } from './subfabLayout'
import {
  aisleDirection,
  bayNumber,
  BULKHEAD_Z,
  FACE_PROUD,
  facingYaw,
  FAB,
  OUTER_AISLE_HALF,
  outerAisleZ,
  SECOND_ROW_PIVOT_Z,
  secondTag,
  TOOLS,
  toolFaceZ,
  toolFrontZ,
  toolSize,
  type Tool,
  type ToolKind,
} from './layout'

/** Shared material cache so the whole fab draws from a handful of materials. */
export interface FabMaterials {
  floor: THREE.MeshStandardMaterial
  wall: THREE.MeshStandardMaterial
  ceiling: THREE.MeshStandardMaterial
  ffu: THREE.MeshStandardMaterial
  toolBody: THREE.MeshStandardMaterial
  toolPanel: THREE.MeshStandardMaterial
  chamber: THREE.MeshStandardMaterial
  glass: THREE.MeshPhysicalMaterial
  foup: THREE.MeshStandardMaterial
  screen: THREE.MeshBasicMaterial
  stripe: THREE.MeshStandardMaterial
  steel: THREE.MeshStandardMaterial
  /** Painted overhead pipework: the runs, their hangers, pendant rods. */
  conduit: THREE.MeshStandardMaterial
  /** The darker drops and pendant bodies that hang off it. */
  conduitDark: THREE.MeshStandardMaterial
  /** Levelling pads. The one saturated colour on the whole floor. */
  levellingPad: THREE.MeshStandardMaterial
  /** The bright band along each side wall, high up. Unlit, like the ceiling. */
  stripLight: THREE.MeshBasicMaterial
  lightPanel: THREE.MeshBasicMaterial
}

export function canvasTexture(
  w: number,
  h: number,
  draw: (ctx: CanvasRenderingContext2D) => void,
  repeat: [number, number] = [1, 1],
): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  draw(ctx)
  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping
  tex.repeat.set(repeat[0], repeat[1])
  tex.anisotropy = 8
  return tex
}

/**
 * Where the hall's environment probe is taken from (FabScene.refreshEnvironment).
 *
 * Exported because a parallax-corrected reflection has to know it: the probe
 * is a direction-only cube, and turning it back into a position in the room
 * needs the position it was captured at.
 */
export const ENV_PROBE = new THREE.Vector3(0, 1.7, 0)

/**
 * The box the probe stands for: the aisle, floor to filter ceiling, tool face
 * to tool face.
 *
 * Not the bulkhead line but the FACE line - `BULKHEAD_Z - FACE_PROUD`. A
 * reflection is being asked which surface it lands on, and down the whole
 * length of this hall the surface it lands on is a tool front standing proud
 * of the wall, not the wall.
 */
const ENV_BOX_MIN = new THREE.Vector3(-FAB.length / 2, 0, -(BULKHEAD_Z - FACE_PROUD))
const ENV_BOX_MAX = new THREE.Vector3(FAB.length / 2, FAB.ceilingHeight, BULKHEAD_Z - FACE_PROUD)

/**
 * WHAT A LIVE HALL SWITCH HAS TO REDO IN THIS FILE. The hall's length and
 * depth change with the layout (layout.ts `applyHallLayout`), and a handful
 * of this file's module-level numbers are derived from them - the env box
 * the floor's reflections are projected onto, the ceiling module, the ends
 * of the hoist loop and the robot lanes, the floor's tile repeat, the
 * stocker's place. Each is a `let` (or a vector mutated in place) refreshed
 * here, after layout.ts has re-laid the row and before the hall is rebuilt
 * against them; nothing below may cache them at import.
 */
function refreshHallDerived(): void {
  ENV_BOX_MIN.set(-FAB.length / 2, 0, -(BULKHEAD_Z - FACE_PROUD))
  ENV_BOX_MAX.set(FAB.length / 2, FAB.ceilingHeight, BULKHEAD_Z - FACE_PROUD)
  const width = FAB.bayDepth * 2 + FAB.aisleHalfWidth * 2
  CEILING_MODULE_Z = width / 10
  CEILING_GRID_Z = width / 40
  FLOOR_REPEAT = [FAB.length / FLOOR_TILE, width / FLOOR_TILE]
  OHT_END = FAB.length / 2 - 9
  AMR_END = FAB.length / 2 - 8
  STOCKER.x = Math.min(...TOOLS.filter((t) => t.position[2] !== 0).map((t) => t.position[0])) - 5
}
onHallChange(refreshHallDerived)

/**
 * PARALLAX-CORRECT THE ENVIRONMENT PROBE, and with it the floor.
 *
 * A cube probe is a direction and nothing else. Sample it from the middle of
 * the hall and it is right; sample it from anywhere else and it is still the
 * middle of the hall, because a cube map has no idea where the shading point
 * is. On a curved metal part nobody can tell. On the FLOOR - a plane you look
 * across at a grazing angle, where a 1 degree error in the reflected
 * direction moves the thing you see by metres - it is the whole difference
 * between a floor and a mirror with nothing in it.
 *
 * That is what the aisle looked like: four bright pools where the luminaires
 * happened to sit overhead, and otherwise an empty grey sheen. The probe was
 * never the problem - it was taken from mid-aisle and it HAS the tool rows in
 * it, both of them, at their true angular size. It was being asked the wrong
 * question. What a production-bay photograph shows in its floor is the two
 * rows of tool fronts pulled into vertical streaks running away down the
 * hall, and that is the single strongest cue that the room is long.
 *
 * So: intersect the reflected ray with the box the probe stands for, and look
 * up the point it hits rather than the direction it left in. Standard
 * box-projected cube mapping, about twenty lines of it, and it costs one
 * ray-box intersection per fragment on one material. The alternative - a real
 * planar reflection pass - renders the hall twice, which the integrated
 * graphics this project promises cannot afford.
 *
 * The shading point is CLAMPED into the box first. The floor is one mesh
 * spanning the whole building, including the strip behind each bulkhead, and
 * a point outside the box projects to nonsense; clamped, the chase floor
 * simply reflects what the aisle floor at the bulkhead does, which down there
 * under a grate deck nobody will ever look at.
 */
/**
 * `directSpecular` scales the specular this material takes from the scene's
 * LIGHTS - not from the environment. The downlights are point sources
 * standing in for strip fittings: their diffuse pools on the floor are
 * right, but the image a point leaves in a polished floor is a dot, and the
 * strips' true image - long rows running down the hall - is already in the
 * environment capture, box-projected onto this very floor. So on the floor
 * the point term is cut to a fifth: the highlight it shows is mostly the
 * room mirrored in it, with enough of the point term left to keep a small
 * pool under each lamp. (An anisotropic stretch of that pool was tried and
 * taken out again - see the floor material's note: it curves.)
 */
export function boxProjectEnvironment(material: THREE.Material, directSpecular = 1): void {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.envBoxMin = { value: ENV_BOX_MIN }
    shader.uniforms.envBoxMax = { value: ENV_BOX_MAX }
    shader.uniforms.envProbe = { value: ENV_PROBE }
    shader.uniforms.directSpecular = { value: directSpecular }
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vEnvWorldPos;')
      .replace(
        '#include <worldpos_vertex>',
        '#include <worldpos_vertex>\n#ifdef USE_ENVMAP\n\tvEnvWorldPos = worldPosition.xyz;\n#endif',
      )
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vEnvWorldPos;
uniform vec3 envBoxMin;
uniform vec3 envBoxMax;
uniform vec3 envProbe;
vec3 boxProjectEnv( vec3 dir ) {
	vec3 p = clamp( vEnvWorldPos, envBoxMin + 0.01, envBoxMax - 0.01 );
	vec3 d = normalize( dir );
	vec3 inv = 1.0 / d;
	vec3 tFar = ( envBoxMax - p ) * inv;
	vec3 tNear = ( envBoxMin - p ) * inv;
	vec3 t = mix( tNear, tFar, step( vec3( 0.0 ), d ) );
	return ( p + d * min( min( t.x, t.y ), t.z ) ) - envProbe;
}`,
      )
      .replace('#include <envmap_physical_pars_fragment>', IBL_BOX_CHUNK)
    if (directSpecular !== 1) {
      // After the light loops have summed into `reflectedLight`, before AO
      // and the environment are applied: only the lights' term is scaled.
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform float directSpecular;')
        .replace(
          '#include <aomap_fragment>',
          `reflectedLight.directSpecular *= directSpecular;
#ifdef USE_CLEARCOAT
	clearcoatSpecularDirect *= directSpecular;
#endif
#include <aomap_fragment>`,
        )
      // The same silent-failure mode as the IBL splice: a renamed include
      // would compile fine and quietly hand the dots back.
      if (import.meta.env.DEV && !shader.fragmentShader.includes('reflectedLight.directSpecular *= directSpecular')) {
        console.error('boxProjectEnvironment: three moved aomap_fragment; direct specular is uncut')
      }
    }
  }
  // The patched program must not be shared with an unpatched one built from
  // the same defines - three keys its cache on those alone.
  material.customProgramCacheKey = () => (directSpecular === 1 ? 'box-env' : 'box-env-ds')
  material.needsUpdate = true
}

/**
 * The IBL lookup, with the projection spliced into it.
 *
 * PATCH THE CHUNK, NOT THE SHADER. `onBeforeCompile` runs BEFORE three
 * resolves `#include`, so at that point `shader.fragmentShader` still reads
 * `#include <envmap_physical_pars_fragment>` and the line this needs to
 * change is not in the string yet. Searching for it there finds nothing,
 * silently: the shader compiles, the uniforms bind, the program is cached,
 * and the picture does not change by a single pixel. It cost a build to find
 * out, which is the whole reason this note is here rather than in a commit
 * message.
 */
const IBL_BOX_CHUNK = THREE.ShaderChunk.envmap_physical_pars_fragment.replace(
  'reflectVec = inverseTransformDirection( reflectVec, viewMatrix );',
  'reflectVec = boxProjectEnv( inverseTransformDirection( reflectVec, viewMatrix ) );',
)

if (import.meta.env.DEV && IBL_BOX_CHUNK === THREE.ShaderChunk.envmap_physical_pars_fragment) {
  console.error('boxProjectEnvironment: three changed getIBLRadiance; the floor is unprojected')
}

/**
 * Raised access floor: 600 mm perforated tiles on a pedestal grid, the
 * airflow return path of a real cleanroom.
 *
 * The dimensions are the whole point. The first version tiled this at two
 * metres with eight fist-sized holes per tile, and that single scale error
 * did more than anything else to make the hall read as a model: a floor is
 * the one surface a walking student never stops looking at, and a raised
 * floor is 2 ft tiles with a field of fingertip perforations, a solid
 * margin at each edge, and a seam you can see. Drawn at that scale, the
 * holes merge into a grey field at aisle distance (the mipmaps do that)
 * and resolve into holes at your feet, which is exactly what the real thing
 * does.
 */
const FLOOR_TILE = 0.6

function raisedFloorTexture(): THREE.CanvasTexture {
  return canvasTexture(
    256,
    256,
    (ctx) => {
      ctx.fillStyle = '#d3d7db'
      ctx.fillRect(0, 0, 256, 256)
      // Tile seam, with the slight shadow the bevelled edge casts.
      ctx.strokeStyle = '#b0b6bd'
      ctx.lineWidth = 3
      ctx.strokeRect(1.5, 1.5, 253, 253)
      // Perforations: a 14 x 14 field of 14 mm holes inside a solid margin.
      // Faint. Real perforations are 14 mm holes seen from 1.7 m, and at
      // #6f767e they were a hard dot screen that read as matte grit - which
      // fought every bit of gloss the material asked for. A polished fab
      // floor's texture is the panel joint; the holes are barely there.
      ctx.fillStyle = '#bcc2c8'
      const n = 14
      const margin = 24
      const pitch = (256 - 2 * margin) / (n - 1)
      for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
          ctx.beginPath()
          ctx.arc(margin + i * pitch, margin + j * pitch, 3, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    },
    FLOOR_REPEAT,
  )
}

// A `let`, refreshed on a hall switch (see `refreshHallDerived`).
let FLOOR_REPEAT: [number, number] = [
  FAB.length / FLOOR_TILE,
  (FAB.bayDepth * 2 + FAB.aisleHalfWidth * 2) / FLOOR_TILE,
]

/**
 * How SHINY the floor is, tile by tile - the map that makes it a floor.
 *
 * Every seam in this building used to be painted on. The tile joint, the
 * panel reveal, the perforation field: all of them lived in the albedo and
 * nowhere else, which means they were a picture of a floor lying flat on a
 * surface that reflected the room as one unbroken sheet. Move your head and
 * the reflection slid across the joints as though they were not there,
 * because to the light they were not.
 *
 * A gloss map is what breaks that, and it is nearly free - same UVs as the
 * albedo, one more fetch, no tangent frame. A waxed tile face is polished; the
 * bevelled joint between two tiles is where the wax never gets and the wheels
 * always go, so it is matte; the perforations are open holes and reflect
 * nothing much at all. Under a moving eye the reflected luminaire rows now
 * break at every 600 mm joint, which is the thing a photograph of a real bay
 * floor does that a rendered one does not.
 *
 * Drives BOTH the base roughness and the clearcoat's, from the one texture -
 * three multiplies each scalar by this green channel, so the two scalars are
 * set so the tile FACE still lands on the 0.16 / 0.08 the floor was tuned to
 * and everything else goes rougher from there.
 */
function raisedFloorSurface(): THREE.CanvasTexture {
  return canvasTexture(
    256,
    256,
    (ctx) => {
      // Tile face: 0.381 of the scalar, which is the polish it always had.
      ctx.fillStyle = '#616161'
      ctx.fillRect(0, 0, 256, 256)
      // The joint, and the 40 mm of tile either side of it that a cart wheel
      // and a boot have taken the shine off.
      ctx.strokeStyle = '#a8a8a8'
      ctx.lineWidth = 16
      ctx.strokeRect(0, 0, 256, 256)
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 3
      ctx.strokeRect(1.5, 1.5, 253, 253)
      // The perforation field, matte.
      ctx.fillStyle = '#aaaaaa'
      const n = 14
      const margin = 24
      const pitch = (256 - 2 * margin) / (n - 1)
      for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
          ctx.beginPath()
          ctx.arc(margin + i * pitch, margin + j * pitch, 3, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    },
    FLOOR_REPEAT,
  )
}

/**
 * The pitch of the REAL lights - the point lights in `buildLighting` - along
 * and across the hall. Not the ceiling's visual grid (see CEILING_GRID_*
 * below): the two were once the same number, and splitting them is what
 * lets the ceiling be drawn at true filter-module scale without touching
 * the lamp count the per-tier light budget was measured against.
 */
export const CEILING_MODULE_X = 2.4
export let CEILING_MODULE_Z = (FAB.bayDepth * 2 + FAB.aisleHalfWidth * 2) / 10

/**
 * Where the row of luminaires over each bay floor runs, and with it the
 * downlight that stands in for it. Between the walkway line and the tool
 * faces - lighting the strip a technician actually works on - and inboard
 * of the hoist track's ceiling hangers, which is the constraint that sets
 * it: a fixture row and a hanger row in the same 0.4 m of ceiling read as
 * one confused object.
 */
export const BAY_ROW_Z = BULKHEAD_Z - 1.2

/**
 * Fan filter unit ceiling: a continuous grid of HEPA faces at the size they
 * actually come in - 4 ft by 2 ft, the long side along the hall. The
 * across-hall pitch is stretched a centimetre so a whole number of modules
 * spans the width; a half-module at the wall would be the one thing anyone
 * noticed.
 */
export const CEILING_GRID_X = 1.2
export let CEILING_GRID_Z = (FAB.bayDepth * 2 + FAB.aisleHalfWidth * 2) / 40

function ffuTexture(): THREE.CanvasTexture {
  return canvasTexture(
    256,
    128,
    (ctx) => {
      // The filter face: a white pleated media pack behind a wire guard,
      // which from below is a flat off-white with a faint weave.
      ctx.fillStyle = '#eef1f4'
      ctx.fillRect(0, 0, 256, 128)
      ctx.strokeStyle = 'rgba(120, 128, 138, 0.10)'
      ctx.lineWidth = 1
      for (let x = 8; x < 256; x += 8) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, 128)
        ctx.stroke()
      }
      // The gasketed tee grid the modules sit in.
      ctx.strokeStyle = '#9aa2ac'
      ctx.lineWidth = 6
      ctx.strokeRect(3, 3, 250, 122)
      // A shadowed reveal where the module meets its grid.
      ctx.strokeStyle = 'rgba(60, 68, 78, 0.22)'
      ctx.lineWidth = 3
      ctx.strokeRect(8, 8, 240, 112)
    },
    [FAB.length / CEILING_GRID_X, (FAB.bayDepth * 2 + FAB.aisleHalfWidth * 2) / CEILING_GRID_Z],
  )
}

/**
 * Sheet-metal skins for the tool cabinets: the seams between panels and the
 * skirt line under the top. A white box with no seams is a block of
 * plastic; a white box with seams is a cabinet someone can open, which is
 * what a technician sees when they look at a tool. Box UVs put the same
 * face on every side, so the seams read as vertical panel joints on all of
 * them, and the top is covered by chambers anyway.
 */
function panelSkinTexture(): THREE.CanvasTexture {
  return canvasTexture(256, 256, (ctx) => {
    ctx.fillStyle = '#f2f4f7'
    ctx.fillRect(0, 0, 256, 256)
    ctx.strokeStyle = 'rgba(70, 80, 92, 0.22)'
    ctx.lineWidth = 2
    for (const x of [85, 171]) {
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x, 256)
      ctx.stroke()
    }
    // The skirt line: a horizontal joint below the top cover.
    ctx.beginPath()
    ctx.moveTo(0, 36)
    ctx.lineTo(256, 36)
    ctx.stroke()
    // Latches on each panel, at hand height.
    ctx.fillStyle = 'rgba(70, 80, 92, 0.35)'
    for (const x of [72, 158, 244]) ctx.fillRect(x - 2, 140, 4, 12)
  })
}

/**
 * The same skin, as GLOSS - which is what makes it read as several panels
 * rather than one printed box.
 *
 * The seams above are drawn in albedo only: a grey line painted on a surface
 * with one uniform finish across the whole of it. Light does not care about a
 * painted line, so every tool front in the hall answered the room as a single
 * continuous sheet, and the seams sat on it like a decal - which, up close,
 * is exactly what a tool front stopped short of looking real by.
 *
 * Three things, all cheap, all on the albedo's own UVs:
 *
 * A SEAM IS A GAP. The reveal between two skins is a shadow line that never
 * gets wiped, and it is the matte-est thing on the cabinet. Painted grey it
 * still caught the same highlight as the panel; here it stops catching one.
 *
 * NO TWO PANELS MATCH. Powder-coated sheet steel comes off the line in
 * batches and goes back on a tool in whatever order it came off, so adjacent
 * skins are half a gloss step apart - one flatter, one a touch livelier. It
 * is a small difference and it is most of what says "assembled".
 *
 * THE GRAIN RUNS ACROSS. Rolled sheet has a direction, and at a grazing angle
 * down the aisle it is what stops a two-metre panel from being an untextured
 * white field.
 *
 * Values are fractions of `toolBody.roughness`, which is raised to the seam's
 * value so the panel faces still land on the 0.45 they were tuned to.
 */
function panelSkinSurface(): THREE.CanvasTexture {
  return canvasTexture(256, 256, (ctx) => {
    // Three skins, each its own batch: 0.42, 0.48 and 0.45 absolute.
    const columns: Array<[number, number, string]> = [
      [0, 85, '#8f8f8f'],
      [85, 86, '#a3a3a3'],
      [171, 85, '#999999'],
    ]
    for (const [x, w, grey] of columns) {
      ctx.fillStyle = grey
      ctx.fillRect(x, 0, w, 256)
    }
    // Rolling grain, a gloss level either way.
    ctx.globalAlpha = 0.035
    for (let y = 0; y < 256; y += 2) {
      ctx.fillStyle = y % 4 === 0 ? '#ffffff' : '#000000'
      ctx.fillRect(0, y, 256, 1)
    }
    ctx.globalAlpha = 1
    // The reveals, matte to the top of the scalar.
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 2
    for (const x of [85, 171]) {
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x, 256)
      ctx.stroke()
    }
    ctx.beginPath()
    ctx.moveTo(0, 36)
    ctx.lineTo(256, 36)
    ctx.stroke()
    // Latch hardware: plated, and the shiniest thing on the panel.
    ctx.fillStyle = '#555555'
    for (const x of [72, 158, 244]) ctx.fillRect(x - 2, 140, 4, 12)
  })
}

/**
 * Cleanroom wall panelling: the seam every metre and a half where one
 * prefabricated panel meets the next.
 *
 * Deliberately faint. Reference photography shows the ribbing clearly, but a
 * photograph has a lens and a contrast curve; drawn at the contrast the eye
 * reads off the photo it comes out as corduroy, and the hall stops looking
 * like a room and starts looking like a texture. This is the value where the
 * wall reads as panelled rather than painted and nothing more.
 *
 * One repeat count serves walls of two different lengths, so the seam spacing
 * is honest on the long walls - the ones anybody stands and looks at - and
 * merely fine on the two ends.
 */
function wallPanelTexture(): THREE.CanvasTexture {
  return canvasTexture(
    64,
    8,
    (ctx) => {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, 64, 8)
      ctx.fillStyle = 'rgba(120, 132, 146, 0.20)'
      ctx.fillRect(62, 0, 2, 8)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
      ctx.fillRect(0, 0, 2, 8)
    },
    WALL_REPEAT,
  )
}

const WALL_REPEAT: [number, number] = [96, 1]

/**
 * The wall as GLOSS - and the biggest single surface in the building.
 *
 * A CLEANROOM WALL IS NOT MATTE. It is powder-coated or vinyl-faced steel,
 * wiped down every shift, and it has a soft satin sheen you can see the tool
 * rows in if you stand at the right angle. This one was flat 0.9 everywhere,
 * which is emulsion on plaster - and being the surface that fills most of
 * every frame down the aisle, it is what set the whole hall's finish. The
 * seams were painted on it in albedo alone, so the sheen ran across them as
 * though the panels were one continuous sheet.
 *
 * The joint between two panels is the exception and stays matte: it is a
 * gasketed reveal in shadow. So the sheen now stops and starts every 1.4 m
 * down the wall, which is the length of one panel, which is the thing the
 * eye uses to judge how long the room is.
 */
function wallPanelSurface(): THREE.CanvasTexture {
  return canvasTexture(
    64,
    8,
    (ctx) => {
      // Panel face: 0.756 of the scalar, so 0.68 - satin, not gloss.
      ctx.fillStyle = '#c1c1c1'
      ctx.fillRect(0, 0, 64, 8)
      // The gasketed joint, matte to the top of the scalar.
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(62, 0, 2, 8)
      // The panel's own leading edge, where the wipe-down actually reaches.
      ctx.fillStyle = '#b6b6b6'
      ctx.fillRect(0, 0, 2, 8)
    },
    WALL_REPEAT,
  )
}

/**
 * The face of a polishing pad or lapping plate: the conditioner's spiral
 * groove and a few radial marks. Exists so the disc's rotation can be SEEN -
 * an unmarked cylinder turning about its own axis is indistinguishable from
 * one standing still. Radial UVs on a CylinderGeometry cap run from the
 * centre outward, so the texture is drawn in polar terms.
 */
function padGrooveTexture(): THREE.CanvasTexture {
  return canvasTexture(256, 256, (ctx) => {
    ctx.fillStyle = '#b9bcc2'
    ctx.fillRect(0, 0, 256, 256)
    ctx.strokeStyle = 'rgba(60, 66, 74, 0.55)'
    ctx.lineWidth = 2
    for (let r = 12; r < 128; r += 9) {
      ctx.beginPath()
      ctx.arc(128, 128, r, 0, Math.PI * 2)
      ctx.stroke()
    }
    ctx.strokeStyle = 'rgba(40, 44, 50, 0.7)'
    ctx.lineWidth = 3
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2
      ctx.beginPath()
      ctx.moveTo(128 + Math.cos(a) * 14, 128 + Math.sin(a) * 14)
      ctx.lineTo(128 + Math.cos(a) * 126, 128 + Math.sin(a) * 126)
      ctx.stroke()
    }
  })
}

/** Hazard striping for the aisle edges and the subfab access. */
function stripeTexture(): THREE.CanvasTexture {
  // Authored in sRGB like every painted mark; decoded as linear, the yellow
  // came out beige and the black grey, which read as tape under a haze.
  const tex = canvasTexture(
    128,
    32,
    (ctx) => {
      ctx.fillStyle = '#2b2f36'
      ctx.fillRect(0, 0, 128, 32)
      ctx.fillStyle = '#f5c542'
      for (let i = -32; i < 160; i += 32) {
        ctx.beginPath()
        ctx.moveTo(i, 32)
        ctx.lineTo(i + 16, 32)
        ctx.lineTo(i + 32, 0)
        ctx.lineTo(i + 16, 0)
        ctx.closePath()
        ctx.fill()
      }
    },
    [40, 1],
  )
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

export function createMaterials(): FabMaterials {
  // One texture, both gloss channels: see `raisedFloorSurface`.
  const floorSurface = raisedFloorSurface()
  const mats: FabMaterials = {
    // Polished, not matte: production-bay floors are waxed vinyl or epoxy, and
    // the reference photography is dominated by ceiling glare streaking across
    // them. A clearcoat layer gives real specular streaks from the bay
    // downlights for free; an actual planar-reflection pass would render the
    // scene twice, which the integrated graphics this project promises cannot
    // afford.
    floor: new THREE.MeshPhysicalMaterial({
      map: raisedFloorTexture(),
      roughnessMap: floorSurface,
      clearcoatRoughnessMap: floorSurface,
      // Waxed vinyl, not a mirror. The base lobe is what carries the
      // environment reflection (the clearcoat alone reflects only ~4% at
      // normal incidence). The reflection is DIFFUSE on purpose: the hall's
      // own environment capture, blurred by this roughness into a soft
      // brightening under the luminaire rows and a soft pool under each
      // downlight. A sharp planar mirror of the fixture array was tried,
      // as instanced geometry under the floor, and read as painted-on
      // streaks rather than a floor - the sheen is what a real waxed floor
      // shows from eye height, and it is what the reference photography
      // shows once you stop looking for the fixtures in it.
      // POLISHED. This was 0.42 / 0.45 - a waxed floor that scattered every
      // reflection into a soft brightening. A production bay's floor mirrors
      // the tool fronts down its whole length; that is most of what makes a
      // fab photograph look like a fab. Sharpened until the luminaire rows
      // and the downlights come back as discrete highlights.
      //
      // IT CAN BE SHARP NOW. This used to say a sharp reflection was
      // impossible - the environment being a single cube probe taken from
      // mid-hall, so a mirror would show the hall's centre wherever you stood
      // and slide as you walked. That was true of the probe as it was being
      // SAMPLED, not of the probe: see `boxProjectEnvironment`, which
      // intersects the reflected ray with the room and looks up where it
      // lands. The tool rows are in that capture and they stay put now.
      //
      // The two scalars below are the SEAM's values, not the tile face's.
      // `roughnessMap`/`clearcoatRoughnessMap` multiply into them, and the
      // tile face is 0.381 of the way up, which puts it back on the 0.16 /
      // 0.08 this floor was tuned to.
      //
      // SHARPER AGAIN, AND ISOTROPIC. For a while the floor was anisotropic
      // along the hall, to stretch the downlights' round highlights into
      // the streaks the strip fittings above them would leave. It did, and
      // it was wrong: an anisotropic lobe turns a POINT light's highlight
      // into an arc that bends from the lamp's row toward wherever you are
      // standing - the brushed-metal look, curves that swing across the
      // aisle as you walk - and a vinyl floor is not brushed. The streak
      // a real floor shows is the IMAGE of a straight fixture, and that
      // comes from the environment capture, box-projected, not from the
      // lobe. So the lobe is round, and narrower than it was (face 0.076 /
      // 0.038 through the map): narrow enough that the tool fronts and the
      // people mirror in it, which is the look of the reference, and the
      // point lamps' own highlights - cut to a fifth in
      // `boxProjectEnvironment` below - stay small pools rather than
      // becoming the picture.
      roughness: 0.2,
      metalness: 0.10,
      clearcoat: 1.0,
      clearcoatRoughness: 0.1,
      // Raised with the scene's environment cut (lighting.ts): a smaller
      // pot, and the floor's sheen is exactly what should still be
      // spending out of it.
      envMapIntensity: 4.0,
    }),
    wall: new THREE.MeshStandardMaterial({
      map: wallPanelTexture(),
      // ALBEDO IS NOT BRIGHTNESS. This was 0xeef1f4 - a linear reflectance
      // of 0.88, which is about what fresh snow manages and rather more
      // than any wall panel ever built. White architectural paint is 0.75
      // to 0.85, and the difference matters here because a room made
      // entirely of near-perfect reflectors has nowhere left to go: every
      // surface saturates at the top of the tone curve, they all land on
      // the same white, and any multiplicative effect - ambient occlusion
      // above all - has nothing left to darken.
      color: 0xdfe3e7,
      // The JOINT's roughness; `wallPanelSurface` takes the panel faces down
      // to 0.68, which is the satin a wiped-down cleanroom panel actually has.
      roughness: 0.9,
      roughnessMap: wallPanelSurface(),
      metalness: 0.0,
    }),
    ceiling: new THREE.MeshStandardMaterial({ color: 0xd2d7dc, roughness: 0.95 }),
    ffu: ((): THREE.MeshStandardMaterial => {
      // The same texture drives diffuse AND emissive, so the panel faces glow
      // while the grid seams between them stay dark - the ceiling reads as an
      // array of lit fixtures rather than a uniformly luminous plane, and it
      // is those bright rectangles the polished floor picks up.
      const tex = ffuTexture()
      return new THREE.MeshStandardMaterial({
        map: tex,
        // The last perfect reflector in the building, and the one that showed
        // most: the filter ceiling is what a long bay's perspective piles up
        // at the vanishing point, so at albedo 1.0 - plus its own emissive on
        // top - the far half of every view down the hall went to paper white.
        // Filter media is white cloth over a white frame; 0.72 is what that
        // reflects. It is still the brightest surface in the room, which is
        // correct - it is the room's light source - it just stops clipping.
        color: 0xd8dde2,
        emissive: 0xdfe8f2,
        emissiveMap: tex,
        emissiveIntensity: 0.35,
        roughness: 1,
      })
    })(),
    toolBody: new THREE.MeshStandardMaterial({
      map: panelSkinTexture(),
      roughnessMap: panelSkinSurface(),
      // Painted sheet steel, not a white hole. At 0xffffff this was a
      // PERFECT diffuse reflector - nothing in the universe is - and every
      // tool in the hall sat pinned against the top of the tone curve with
      // its neighbours. See the wall's note above.
      color: 0xd9dde1,
      // The SEAM's roughness. `panelSkinSurface` scales it back down: the
      // three panel faces land on 0.42, 0.48 and 0.45, which is where this
      // was before, and only the reveals between them go all the way matte.
      roughness: 0.75,
      metalness: 0.15,
    }),
    // The two below, and the floor, take a BIGGER SHARE of a smaller
    // environment (see lighting.ts): the env is a reflection budget now
    // rather than a fill light, and a polished floor and a metal chamber
    // are the things that should be spending it. Cutting the scene's
    // envIntensity without this took the floor's sheen down with the
    // flat fill it was meant to remove.
    toolPanel: new THREE.MeshStandardMaterial({ color: 0xd5dae1, roughness: 0.35, metalness: 0.35, envMapIntensity: 2.0 }),
    chamber: new THREE.MeshStandardMaterial({ color: 0x9aa3ad, roughness: 0.3, metalness: 0.85, envMapIntensity: 2.4 }),
    // Plain transparent glass, NOT transmission. Transmission is the
    // physically better model and it was here first, but three implements
    // it by rendering every opaque object in the scene a second time into
    // a buffer the glass can refract - every frame the dome is anywhere in
    // the frustum, which from either end of the hall it always is. That
    // doubled the draw calls of the whole floor (857 to 430, measured) for
    // a refraction blur on one bell jar. Alpha blending plus the hall's
    // environment reflection is what the eye actually reads on quartz.
    glass: new THREE.MeshPhysicalMaterial({
      color: 0xbfe3f2,
      opacity: 0.32,
      transparent: true,
      roughness: 0.06,
      metalness: 0,
      clearcoat: 1,
      clearcoatRoughness: 0.05,
      envMapIntensity: 3.2,
      depthWrite: false,
    }),
    // Moulded polycarbonate, the blue-grey most 300 mm carriers come in.
    // The old orange cubes were easy to spot and looked like nothing a
    // technician has ever lifted.
    // A shade deeper than the port doors behind them, which were made the
    // same near-white and swallowed every docked carrier.
    // The carrier shell. Smoky charcoal polycarbonate, which is both what
    // most 300 mm carriers are moulded in and the only way they read: at
    // 0x96a6b6 a FOUP was the same value as the wall behind it, so a bay
    // with a carrier on every port looked like a bay with none, and the
    // most-handled object in the building was invisible from the aisle.
    // It is also the one dark thing at eye level in a white room, which is
    // what makes a fab photograph the way it does.
    foup: new THREE.MeshStandardMaterial({ color: 0x59636f, roughness: 0.34, metalness: 0.05 }),
    screen: new THREE.MeshBasicMaterial({ color: 0x0b1622 }),
    // The ceiling luminaires. Unlit on purpose: a luminaire is a light
    // source, not a lit surface, so it renders at full brightness regardless
    // of what is shining on it - which is also what lets a few hundred of
    // them cost nothing. Tinted per lighting preset by
    // `FabScene.applyLighting`.
    lightPanel: new THREE.MeshBasicMaterial({ color: 0xffffff }),
    stripe: new THREE.MeshStandardMaterial({ map: stripeTexture(), roughness: 0.8 }),
    steel: new THREE.MeshStandardMaterial({ color: 0xb8bfc7, roughness: 0.25, metalness: 0.9, envMapIntensity: 2.6 }),
    // Painted rather than bright: the reference bay's overhead reads as a
    // matte run against a white ceiling, not as polished pipe. Mirror-finish
    // here would fight the luminaires for the eye.
    //
    // Darker than it first looked, though. Sampled off the photograph the run
    // is nearly the ceiling's own grey - but a ceiling is lit from inside and
    // a pipe is not, so matching the swatch made it vanish against the walls.
    // This is the value that reads at aisle distance, which is the only place
    // anyone stands.
    conduit: new THREE.MeshStandardMaterial({ color: 0xa9afb6, roughness: 0.55, metalness: 0.25 }),
    conduitDark: new THREE.MeshStandardMaterial({ color: 0x545a62, roughness: 0.6, metalness: 0.3 }),
    levellingPad: new THREE.MeshStandardMaterial({ color: 0xe8b83a, roughness: 0.75 }),
    // Overdriven past white for the same reason the ceiling panels are: an
    // unlit material caps at its colour, and ACES then compresses the excess
    // the way a camera compresses a real luminaire.
    stripLight: new THREE.MeshBasicMaterial({
      // Overdriven well past white, for the same reason the ceiling panels are:
      // an unlit material caps at its own colour, and a lit white wall behind
      // it is nearly that bright already - at 1.0 the strip simply vanished.
      // ACES then compresses the excess the way a camera compresses a real
      // luminaire, which is what makes it read as a light rather than paint.
      color: new THREE.Color(2.4, 2.45, 2.55),
      fog: false,
    }),
  }
  // The floor and nothing else. Everything else that spends the environment
  // is small, curved or both, and a cube probe's parallax error does not
  // read on a bezel or a chamber lid the way it reads across forty metres of
  // polished plane.
  boxProjectEnvironment(mats.floor, 0.2)
  return mats
}

export const box = (w: number, h: number, d: number) => new THREE.BoxGeometry(w, h, d)

/** The EMO mushroom's red, one material for every tool. */
/** * The inside of an enclosure, seen through its window: near-black, matte, * and unlit by anything in the hall. A mini-environment is a sealed box * with its own small lamp; what a pane shows from the aisle is a dark * volume with a silhouette in it, and painting it any lighter turns the * glass back into a flat grey panel stuck on a wall. */export const CAVITY_DARK = new THREE.MeshStandardMaterial({ color: 0x20252b, roughness: 0.95 })
export const EMO_RED = new THREE.MeshStandardMaterial({ color: 0xd8262c, roughness: 0.45 })

/**
 * THE BAND ACROSS A TOOL'S SKIN, and why it is nearly grey.
 *
 * Vendors do brand a tool with a colour, so the band is right - but it used
 * to take `tool.accent`, which is a per-tool identity colour picked to tell
 * the STATUS TOWERS apart, and it painted the mainframe with it. A row of
 * twenty tools came out pink, lilac, cyan, mint, amber: a paint chart, not
 * a fab. And it was EMISSIVE at 0.35, so every stripe glowed - nothing on a
 * tool skin is a light source, and the glow is what made them read as
 * plastic under a black light rather than as paint on sheet steel.
 *
 * A fab buys from a handful of suppliers, and a bay reads as two or three
 * liveries repeating, not as twenty. So: three muted colours, assigned by
 * what a real fab would have bought from whom - the exposure and implant
 * tools from one house, the vacuum process tools from another, metrology,
 * sort and assembly from a third. Same reason the asset tags are numbered
 * by install order: the floor should reward a student who notices how it
 * was actually put together.
 *
 * Shared materials rather than one per tool, so each tool's band folds into
 * its merged mesh instead of costing a draw of its own.
 */
const VENDOR_LIVERY = [
  // Exposure and implant: the blue-grey the big lithography houses use.
  new THREE.MeshStandardMaterial({ color: 0x7d93a8, roughness: 0.55 }),
  // Vacuum process: graphite, the darkest thing on a white tool.
  new THREE.MeshStandardMaterial({ color: 0x5b636d, roughness: 0.55 }),
  // Metrology, sort and assembly: barely off the skin it sits on.
  new THREE.MeshStandardMaterial({ color: 0xa9b1b9, roughness: 0.55 }),
] as const

const VENDOR_OF: Partial<Record<ToolKind, number>> = {
  litho: 0,
  patterning: 0,
  implanter: 0,
  etcher: 1,
  deposition: 1,
  epi: 1,
  furnace: 1,
  cmp: 1,
  plating: 1,
  wetbench: 1,
  crystal: 1,
  facilities: 1,
  gas: 1,
  waferprep: 2,
  metrology: 2,
  inspection: 2,
  probe: 2,
  test: 2,
  dicing: 2,
  bonding: 2,
  packaging: 2,
  gowning: 2,
  euv: 0,
  ebeam: 2,
  cryostat: 2,
}

/**
 * Where the key light stands relative to whatever it is lighting. Nearly
 * overhead: a cleanroom is lit by its ceiling, and the first version's
 * 35-degree slant threw the long diagonal shadows of a sunlit yard across
 * the floor - the one thing a fab never has. Enough lean remains for a
 * cabinet's shadow to show on one side, which is what says "standing" and
 * not "floating".
 */
export const KEY_OFFSET = new THREE.Vector3(2.5, 16, 1.8)

// ------------------------------------------------------------- partitions
//
// The walls between bays. A real fab is bay-and-chase, and even where a
// ballroom layout keeps the chase open, the bays are walled off from each
// other from the back wall to just short of the aisle, with a window at eye
// height so a technician can see the next bay's tools. Without them this
// hall was one 136 m room, and a student could not have said where one bay
// ended; with them "bay 07" is a place you are standing in. The walls stop
// behind the hoist rail, which needs its run clear.

/** One partition: its x, and the side of the aisle it stands on. */
export interface Partition {
  x: number
  side: 1 | -1
  /**
   * Set on Fab B's outer-aisle dividers: the z every wall-line measure is
   * mirrored about (SECOND_ROW_PIVOT_Z), so a divider built for the first
   * row stands at the second row's face with everything on it turned to
   * face the outer aisle. See `partitionZ`.
   */
  mirror?: number
}

/** World z of a wall-line measure `v` (a BULKHEAD_Z-based depth) on this divider's side. */
export const partitionZ = (p: Partition, v: number): number => p.side * (p.mirror === undefined ? v : 2 * p.mirror - v)

/** Where the walls go: between each adjacent pair of bays, both sides. */
export function partitions(): Partition[] {
  const xs = [...new Set(TOOLS.filter((t) => t.position[2] !== 0).map((t) => t.position[0]))].sort(
    (a, b) => a - b,
  )
  const out: Partition[] = []
  for (let i = 0; i + 1 < xs.length; i++) {
    const x = (xs[i] + xs[i + 1]) / 2
    out.push({ x, side: 1 }, { x, side: -1 })
    // Fab B's outer aisles: the same bay boundaries, at the second row.
    if (BALLROOM) out.push({ x, side: 1, mirror: SECOND_ROW_PIVOT_Z }, { x, side: -1, mirror: SECOND_ROW_PIVOT_Z })
  }
  return out
}

/**
 * How far a bay divider stands into the hall from the bulkhead line.
 *
 * It used to be 0.3 m - a pilaster, a column in the wall - and from the
 * middle of the aisle that is nothing: the two tool rows ran unbroken to
 * the far wall and the hall read as one 136 m tunnel, which no fab is. A
 * bay is a ROOM you step into, and what tells you so is the wall you step
 * past. At 1.6 m the divider stands a metre proud of the tool faces, the
 * bays occlude each other as the aisle recedes, and the far end of the
 * hall stops being a vanishing point and becomes the twentieth bay.
 *
 * It stops well outside the walkway stripe and the robot lane, so nothing
 * that moves has to find its way round it.
 */
export const PARTITION_NEAR_Z = BULKHEAD_Z - 1.15

/** Footprint of one partition, for the walk's colliders. */
export function partitionCollider(p: Partition): { minX: number; maxX: number; minZ: number; maxZ: number } {
  return {
    minX: p.x - 0.13,
    maxX: p.x + 0.13,
    minZ: Math.min(partitionZ(p, PARTITION_NEAR_Z), partitionZ(p, BULKHEAD_Z)),
    maxZ: Math.max(partitionZ(p, PARTITION_NEAR_Z), partitionZ(p, BULKHEAD_Z)),
  }
}

/**
 * Since the bulkhead, the partitions between bays are all in the chase,
 * where nobody can see them; what the aisle shows of a bay boundary is a
 * pilaster in the wall line. That is all that is built - the chase wall
 * behind it would be drawn and then overdrawn by the bulkhead, and the
 * window it used to carry looked into a service corridor.
 */
function buildPartitions(mats: FabMaterials): THREE.Group {
  const g = new THREE.Group()
  const H = FAB.ceilingHeight
  // Where the hoist beam crosses the divider's line, and the clear height
  // under it. A bay wall does not stop a monorail: the wall is built round
  // it, low under the beam and closed again above, which is why every fab
  // bay entrance has a header over a gap you can see the track run through.
  // Built solid, the divider swallowed the beam whole and the track that
  // this hall was rebuilt to show simply vanished nine times down its
  // length.
  // Sized to `OHT_SWEEP` - the beam AND the vehicle under it - not to the
  // beam alone. See the note on that constant for what the beam-only
  // version did.
  const OPENING_FAR = OHT_R + OHT_SWEEP.zHalf + 0.09
  const UNDER = OHT_SWEEP.yLo - 0.09
  const OVER = OHT_SWEEP.yHi + 0.06
  for (const p of partitions()) {
    const zNear = partitionZ(p, PARTITION_NEAR_Z)
    /** A slab of the divider between two z, from `y0` up to `y1`. */
    const slab = (zA: number, zB: number, y0: number, y1: number): void => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(0.26, y1 - y0, Math.abs(zB - zA)), mats.wall)
      m.position.set(p.x, (y0 + y1) / 2, partitionZ(p, (zA + zB) / 2))
      m.castShadow = true
      m.receiveShadow = true
      g.add(m)
    }
    // Solid from the bulkhead in to where the beam passes...
    slab(OPENING_FAR, BULKHEAD_Z + 0.1, 0, H)
    // ...then low under the beam, and a header above it.
    slab(PARTITION_NEAR_Z, OPENING_FAR, 0, UNDER)
    slab(PARTITION_NEAR_Z, OPENING_FAR, OVER, H)
    // The nosing on the exposed end. A 3.6 m white slab with a square white
    // edge has no corner: from down the aisle the dividers dissolved back
    // into the wall they stand out of. A grey return down the open end is
    // the corner guard a real one carries anyway, and it is what makes the
    // rhythm of the bays read from fifty metres.
    const nose = new THREE.Mesh(new THREE.BoxGeometry(0.3, UNDER, 0.06), mats.conduit)
    nose.position.set(p.x, UNDER / 2, zNear)
    g.add(nose)
    const header = new THREE.Mesh(new THREE.BoxGeometry(0.3, H - OVER, 0.06), mats.conduit)
    header.position.set(p.x, (OVER + H) / 2, zNear)
    g.add(header)
  }
  return g
}



// ------------------------------------------------------- aisle furniture
//
// WHAT A CLEANROOM IS FULL OF THAT IS NOT A TOOL.
//
// A fab is clean, not empty, and the difference is the whole argument of
// this file. The hall had its tools, its signs and its lights, and between
// them ran thirty metres of unbroken white wall - which is the strongest
// tell that a room was modelled rather than photographed. Real bay walls
// carry the things a technician needs within arm's reach of where they
// stand: the phone they call the shift lead on, the button they hit when
// something goes wrong, the procedure in a sleeve at the bay entrance, a
// box of gloves, a bin for the ones they take off. Overhead, the ceiling
// is crossed every few metres by the tray and pipe runs that feed the bays.
//
// None of it is decoration. A student who is told to raise an alarm has to
// have somewhere to raise it, and a student who never sees an EMO in a
// cleanroom will not look for one in a real cleanroom.
//
// INSTANCED, one draw call per family, for the reason the utility gantry
// is: this is a few hundred small objects, and a few hundred small objects
// drawn individually is exactly the hitch this project has paid for twice.

/** The printed procedure in its sleeve at a bay entrance. */
function sopTexture(): THREE.CanvasTexture {
  return canvasTexture(128, 176, (ctx) => {
    ctx.fillStyle = '#f7f8fa'
    ctx.fillRect(0, 0, 128, 176)
    ctx.strokeStyle = '#c8ced6'
    ctx.lineWidth = 2
    ctx.strokeRect(1, 1, 126, 174)
    // A header band and body copy: at aisle distance a sheet of paper is
    // a light rectangle with a dark bar at the top, and nothing else reads.
    ctx.fillStyle = '#2b6ca3'
    ctx.fillRect(8, 8, 112, 20)
    ctx.fillStyle = '#98a2ad'
    for (let i = 0; i < 11; i++) {
      const y = 42 + i * 11
      ctx.fillRect(10, y, i % 3 === 0 ? 60 : 108, 3)
    }
  })
}

/**
 * The bay-entrance station, the cross-aisle service runs, and the bins.
 *
 * Everything here hangs off the bay dividers, which is where a real one
 * puts them and, conveniently, the one surface in this hall that FACES
 * ALONG THE AISLE. Furniture on the bulkhead is seen edge-on by anyone
 * walking the hall and might as well not be built; furniture on the side
 * of a divider is square to the walk, and reads from thirty metres.
 */
function buildAisleFurniture(mats: FabMaterials): THREE.Group {
  const g = new THREE.Group()
  const parts = partitions()

  const place = (geometry: THREE.BufferGeometry, material: THREE.Material, m: THREE.Matrix4[]): void => {
    if (m.length === 0) return
    const mesh = new THREE.InstancedMesh(geometry, material, m.length)
    m.forEach((mat, i) => mesh.setMatrixAt(i, mat))
    mesh.instanceMatrix.needsUpdate = true
    mesh.frustumCulled = false
    g.add(mesh)
  }

  // --- the cross-aisle service runs ----------------------------------------
  //
  // A cable tray and two pipes spanning the hall at each bay boundary, above
  // the hoist track and below the filter deck. This is what a fab ceiling
  // does that an office ceiling does not: it is crossed. Without them the
  // ceiling was 136 m of parallel lines converging on a point, and the eye
  // read the hall as a tunnel with no end - the perspective had no rungs in
  // it to measure distance against. Each run is a rung.
  const CROSS_Y = 3.3
  const span = 2 * (BULKHEAD_Z + 0.1)
  const trays: THREE.Matrix4[] = []
  const rails: THREE.Matrix4[] = []
  const pipes: THREE.Matrix4[] = []
  const xs = [...new Set(parts.map((p) => p.x))]
  // Offset a metre off the bay boundary rather than on it: a 0.34 m tray on
  // the same centreline as a 0.26 m wall is a tray buried in a wall with its
  // edges poking out of both faces.
  for (const x0 of xs) {
    const x = x0 + 1.0
    trays.push(new THREE.Matrix4().makeTranslation(x, CROSS_Y, 0))
    for (const dz of [-0.17, 0.17]) rails.push(new THREE.Matrix4().makeTranslation(x + dz, CROSS_Y + 0.06, 0))
    for (const [dx, dy] of [[-0.4, -0.02], [0.42, 0.03]] as const) {
      pipes.push(new THREE.Matrix4().makeTranslation(x + dx, CROSS_Y + dy, 0))
    }
  }
  // The tray as a floor and two sides, so it is a channel rather than a bar.
  const trayGeo = new THREE.BoxGeometry(0.34, 0.025, span)
  place(trayGeo, mats.conduitDark, trays)
  place(new THREE.BoxGeometry(0.02, 0.1, span), mats.conduitDark, rails)
  const pipeGeo = new THREE.CylinderGeometry(0.055, 0.055, span, 10)
  pipeGeo.rotateX(Math.PI / 2)
  place(pipeGeo, mats.conduit, pipes)
  // Fab B's outer aisles are crossed the same way: from the second row's
  // divider line out to the wall, at the same bay boundaries, for the same
  // reason - an aisle with nothing across its ceiling has no rungs either.
  if (BALLROOM) {
    const near = 2 * SECOND_ROW_PIVOT_Z - (BULKHEAD_Z + 0.1)
    const wall = FAB.bayDepth + FAB.aisleHalfWidth
    const spanO = wall - near
    const oTrays: THREE.Matrix4[] = []
    const oRails: THREE.Matrix4[] = []
    const oPipes: THREE.Matrix4[] = []
    for (const side of [-1, 1]) {
      const cz = (side * (near + wall)) / 2
      for (const x0 of xs) {
        const x = x0 + 1.0
        oTrays.push(new THREE.Matrix4().makeTranslation(x, CROSS_Y, cz))
        for (const dz of [-0.17, 0.17]) oRails.push(new THREE.Matrix4().makeTranslation(x + dz, CROSS_Y + 0.06, cz))
        for (const [dx, dy] of [[-0.4, -0.02], [0.42, 0.03]] as const) {
          oPipes.push(new THREE.Matrix4().makeTranslation(x + dx, CROSS_Y + dy, cz))
        }
      }
    }
    place(new THREE.BoxGeometry(0.34, 0.025, spanO), mats.conduitDark, oTrays)
    place(new THREE.BoxGeometry(0.02, 0.1, spanO), mats.conduitDark, oRails)
    const oPipeGeo = new THREE.CylinderGeometry(0.055, 0.055, spanO, 10)
    oPipeGeo.rotateX(Math.PI / 2)
    place(oPipeGeo, mats.conduit, oPipes)
  }

  // --- what stands at the entrance to a bay --------------------------------
  //
  // Placed on the divider's side face, a little in from its open end, at the
  // heights they are actually mounted at: a phone you speak into standing
  // up, a button you hit with a palm without looking, a sleeve you read at
  // eye level, gloves above the bin they end up in.
  const andonBody: THREE.Matrix4[] = []
  const andonScreen: THREE.Matrix4[] = []
  const andonLamp: THREE.Matrix4[] = []
  const handset: THREE.Matrix4[] = []
  const emoPlate: THREE.Matrix4[] = []
  const emoButton: THREE.Matrix4[] = []
  const sleeve: THREE.Matrix4[] = []
  const sheet: THREE.Matrix4[] = []
  const binBody: THREE.Matrix4[] = []
  const binRim: THREE.Matrix4[] = []
  const binMouth: THREE.Matrix4[] = []
  const binBand: THREE.Matrix4[] = []

  /** A wall item on the divider face pointing `dir` along the aisle. */
  const on = (
    list: THREE.Matrix4[],
    p: Partition,
    dir: -1 | 1,
    dz: number,
    y: number,
    out: number,
  ): void => {
    list.push(new THREE.Matrix4().makeTranslation(p.x + dir * (0.13 + out), y, partitionZ(p, dz)))
  }

  // Every item sits between the divider's open end and the bulkhead, in one
  // group rather than scattered down the wall: a bay station is a place you
  // go to, not a set of fittings that happen to share a wall. Spread across
  // the full face they read as clutter, and two of them hung off the end of
  // the divider entirely and floated.
  for (const p of parts) {
    for (const dir of [-1, 1] as const) {
      on(andonBody, p, dir, BULKHEAD_Z - 0.36, 1.56, 0.045)
      on(andonScreen, p, dir, BULKHEAD_Z - 0.36, 1.62, 0.092)
      on(andonLamp, p, dir, BULKHEAD_Z - 0.36, 1.76, 0.045)
      on(handset, p, dir, BULKHEAD_Z - 0.55, 1.54, 0.075)
      on(emoPlate, p, dir, BULKHEAD_Z - 0.82, 1.28, 0.012)
      on(emoButton, p, dir, BULKHEAD_Z - 0.82, 1.28, 0.06)
      on(sleeve, p, dir, BULKHEAD_Z - 0.82, 1.72, 0.012)
      on(sheet, p, dir, BULKHEAD_Z - 0.82, 1.72, 0.026)
    }
    // One bin per divider, on the floor at its open end, in the bay rather
    // than in the walkway.
    const bz = partitionZ(p, PARTITION_NEAR_Z + 0.35)
    binBody.push(new THREE.Matrix4().makeTranslation(p.x + 0.45, 0.31, bz))
    binRim.push(new THREE.Matrix4().makeTranslation(p.x + 0.45, 0.62, bz))
    binMouth.push(new THREE.Matrix4().makeTranslation(p.x + 0.45, 0.55, bz))
    binBand.push(new THREE.Matrix4().makeTranslation(p.x + 0.45, 0.44, bz))
  }

  const panelDark = new THREE.MeshStandardMaterial({ color: 0x2a3038, roughness: 0.5 })
  // The waste stream's colour band round each bin - how a gowning-room bin
  // is told from a chemical one across a bay.
  const BIN_BAND = new THREE.MeshStandardMaterial({ color: 0x3a6fb5, roughness: 0.8, side: THREE.DoubleSide })
  const amber = new THREE.MeshStandardMaterial({ color: 0xffb020, emissive: 0xffb020, emissiveIntensity: 1.1 })
  const yellow = new THREE.MeshStandardMaterial({ color: 0xe8b83a, roughness: 0.7 })
  const sheetMat = new THREE.MeshStandardMaterial({ map: sopTexture(), roughness: 0.85 })

  place(new THREE.BoxGeometry(0.09, 0.34, 0.26), mats.toolPanel, andonBody)
  place(new THREE.BoxGeometry(0.006, 0.11, 0.17), panelDark, andonScreen)
  // The beacon on top of the station, not a lozenge stuck on the wall beside
  // it: a dome is what a call light is, and it is the part that has to read
  // from down the aisle when a bay is calling for help.
  place(new THREE.SphereGeometry(0.055, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), amber, andonLamp)
  place(new THREE.BoxGeometry(0.06, 0.2, 0.07), panelDark, handset)
  place(new THREE.BoxGeometry(0.02, 0.24, 0.24), yellow, emoPlate)
  place(new THREE.CylinderGeometry(0.055, 0.055, 0.05, 14).rotateZ(Math.PI / 2), EMO_RED, emoButton)
  place(new THREE.BoxGeometry(0.02, 0.34, 0.25), mats.toolPanel, sleeve)
  const sheetGeo = new THREE.PlaneGeometry(0.2, 0.28)
  sheetGeo.rotateY(-Math.PI / 2)
  place(sheetGeo, sheetMat, sheet)
  // A BIN YOU CAN SEE INTO. The first version was a light cylinder with a
  // solid dark disc on top, and from standing height the disc is almost all
  // of it - so every divider had an unidentifiable black lozenge at its
  // foot. What reads as a bin is the opening: a steel rim, the bag turned
  // over it, and a dark interior set below the rim so there is somewhere
  // for waste to go. The band round the body is the waste stream, which is
  // how a gowning-room bin is told from a chemical one.
  place(new THREE.CylinderGeometry(0.19, 0.165, 0.62, 16), mats.toolPanel, binBody)
  place(new THREE.TorusGeometry(0.185, 0.018, 6, 18).rotateX(Math.PI / 2), mats.steel, binRim)
  place(new THREE.CylinderGeometry(0.172, 0.172, 0.012, 16), panelDark, binMouth)
  place(new THREE.CylinderGeometry(0.193, 0.193, 0.07, 16, 1, true), BIN_BAND, binBand)

  // --- the drop to each plumbed tool ---------------------------------------
  //
  // The chase carries the run, but a tool's services still have to cross the
  // bulkhead to reach it, and where they do a real bay shows a bundle: a
  // sheathed loom clamped down the wall beside the tool face, with the
  // isolation valve on it. It is the only thing in the aisle that says a
  // tool is connected to the building rather than standing in it.
  const looms: THREE.Matrix4[] = []
  const valves: THREE.Matrix4[] = []
  const clamps: THREE.Matrix4[] = []
  for (const tool of TOOLS) {
    if (tool.position[2] === 0 || !DROP_KINDS.has(tool.kind)) continue
    const side = Math.sign(tool.position[2])
    // Clamped to the bulkhead beside the face; with no bulkhead, clamped
    // to the mainframe's own side wall, which is where it lands anyway.
    const x = tool.position[0] + toolSize(tool).w / 2 + (BALLROOM ? 0.07 : 0.34)
    const z = side * (BULKHEAD_Z - 0.12)
    looms.push(new THREE.Matrix4().makeTranslation(x, 1.9, z))
    valves.push(new THREE.Matrix4().makeTranslation(x, 1.35, z))
    for (const y of [1.0, 1.7, 2.4]) clamps.push(new THREE.Matrix4().makeTranslation(x, y, z))
  }
  place(new THREE.CylinderGeometry(0.05, 0.05, 2.6, 8), mats.conduitDark, looms)
  place(new THREE.BoxGeometry(0.1, 0.14, 0.1), mats.steel, valves)
  place(new THREE.BoxGeometry(0.13, 0.03, 0.13), mats.conduit, clamps)

  return g
}
// ---------------------------------------------------------- floor markings
//
// The keep-clear box taped on the floor in front of every load port, the
// way every fab marks the space a carrier hand-off needs. Yellow tape on a
// hatched field; one instanced plane per tool.

function keepClearTexture(): THREE.CanvasTexture {
  const tex = canvasTexture(256, 96, (ctx) => {
    ctx.clearRect(0, 0, 256, 96)
    ctx.strokeStyle = 'rgba(245, 197, 66, 0.9)'
    ctx.lineWidth = 8
    ctx.strokeRect(4, 4, 248, 88)
    ctx.strokeStyle = 'rgba(245, 197, 66, 0.35)'
    ctx.lineWidth = 3
    for (let x = -96; x < 256; x += 24) {
      ctx.beginPath()
      ctx.moveTo(x, 92)
      ctx.lineTo(x + 88, 4)
      ctx.stroke()
    }
  })
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// ---------------------------------------------------------------- floor wear
//
// WHAT A CLEAN FLOOR STILL RECORDS. A cleanroom floor is wiped every shift
// and never dirty, and that is exactly why the little it does show reads so
// strongly: every mark on it has a cause. The robots leave two faint wheel
// tracks down their lane, somebody stands in front of every screen, and
// where a taped box was moved the adhesive left a ghost. Nothing else - no dust, no
// stains, and nothing in the keep-clear boxes; those belong in the chase,
// which has its own decal (chase.ts).
//
// One room-sized transparent plane over the aisle, drawn from the layout
// so every mark lands where the thing that made it stands. Kept faint on
// purpose: at aisle distance this should be felt, not seen, and the floor
// underneath it stays the polished tile the reflections need.

function lcgFloor(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0x100000000
  }
}

/** The wear plane's half depth: the aisle plus the tool-front zone. */
const WEAR_HALF_Z = BULKHEAD_Z - FACE_PROUD

/**
 * One aisle's worth of wear: which strip of floor the decal covers, where
 * the walking lane and the robot lanes run in it, and which tool faces
 * open onto it. `side` 0 is the main aisle with a row each side; Fab B's
 * outer aisles have the second row's copies on the one side (`side`) and
 * the wall on the other, so their ports and screens are the tools' own,
 * mirrored the way `buildSecondRow` turned them.
 */
interface WearAisle {
  centreZ: number
  halfZ: number
  side: 0 | 1 | -1
  walkZ: number
  robotLanes: number[]
  walkLineHalf: number
}

// A function, not a constant: AMR_LANE_Z is declared further down the file.
function mainAisleWear(): WearAisle {
  return {
    centreZ: 0,
    halfZ: WEAR_HALF_Z,
    side: 0,
    walkZ: 0,
    robotLanes: [-AMR_LANE_Z, AMR_LANE_Z],
    walkLineHalf: FAB.aisleHalfWidth,
  }
}

function outerAisleWear(side: 1 | -1): WearAisle {
  // From the copies' face line out to the wall.
  const faceZ = 2 * SECOND_ROW_PIVOT_Z - WEAR_HALF_Z
  const wallZ = FAB.bayDepth + FAB.aisleHalfWidth
  return {
    centreZ: (side * (faceZ + wallZ)) / 2,
    halfZ: (wallZ - faceZ) / 2,
    side,
    walkZ: side * outerAisleZ(),
    robotLanes: [side * (2 * SECOND_ROW_PIVOT_Z - AMR_LANE_Z), side * (outerAisleZ() + OUTER_AISLE_HALF - 0.45)],
    walkLineHalf: OUTER_AISLE_HALF,
  }
}

function floorWearTexture(aisle: WearAisle, seed: number): THREE.CanvasTexture {
  const L = FAB.length
  const ZW = aisle.halfZ
  const cw = 4096
  const ch = 256
  const tex = canvasTexture(cw, ch, (ctx) => {
    const rnd = lcgFloor(seed)
    const px = (x: number) => ((x + L / 2) / L) * cw
    // The plane lies with its local +y toward world -z (the floor's own
    // rotation), so the canvas's top row is the -z edge.
    const pz = (z: number) => ((z - aisle.centreZ + ZW) / (2 * ZW)) * ch
    const mx = cw / L
    const mz = ch / (2 * ZW)
    /** A soft blot, in metres. */
    const blot = (x: number, z: number, rx: number, rz: number, alpha: number, tone: string) => {
      const cx = px(x)
      const cz = pz(z)
      const r = rx * mx
      const grad = ctx.createRadialGradient(cx, cz, 0, cx, cz, r)
      grad.addColorStop(0, `rgba(${tone},${alpha})`)
      grad.addColorStop(0.6, `rgba(${tone},${alpha * 0.5})`)
      grad.addColorStop(1, `rgba(${tone},0)`)
      ctx.save()
      ctx.translate(cx, cz)
      ctx.scale(1, (rz * mz) / r)
      ctx.translate(-cx, -cz)
      ctx.fillStyle = grad
      ctx.fillRect(cx - r, cz - r, r * 2, r * 2)
      ctx.restore()
    }
    ctx.clearRect(0, 0, cw, ch)

    // NO WALKING LANE. There was a band of dulled polish down the middle
    // of the aisle here, and looking straight down the aisle under the
    // lights it read as a dark stripe on the floor rather than a change of
    // sheen. A wiped cleanroom floor does not show one; removed.

    // The robots' wheel tracks down their lane, either side: two lines a
    // wheelbase apart, broken where a vehicle parks and turns.
    ctx.lineWidth = 1.4
    for (const laneZ of aisle.robotLanes) {
      for (const dz of [-0.24, 0.24]) {
        const z = laneZ + dz
        let x = -L / 2 + 4
        while (x < L / 2 - 4) {
          const run = 6 + rnd() * 14
          ctx.strokeStyle = `rgba(52,54,58,${0.07 + rnd() * 0.04})`
          ctx.beginPath()
          ctx.moveTo(px(x), pz(z))
          ctx.lineTo(px(Math.min(x + run, L / 2 - 4)), pz(z))
          ctx.stroke()
          x += run + 0.6 + rnd() * 1.5
        }
      }
    }

    for (const tool of TOOLS) {
      if (tool.position[2] === 0) continue
      const copy = aisle.side !== 0
      if (copy && (Math.sign(tool.position[2]) !== aisle.side || !hasSecondRowCopy(tool))) continue
      // The copy is the tool turned half a turn about the service aisle's
      // centreline at its own x: it faces the other way, its ports are
      // mirrored in x about it, and its face line is the tool's mirrored
      // in z about the pivot.
      const facing = copy ? -aisleDirection(tool) : aisleDirection(tool)
      const faceZ = copy ? 2 * aisle.side * SECOND_ROW_PIVOT_Z - toolFaceZ(tool) : toolFaceZ(tool)
      // The face's local x runs against world x on the +z side (bays.ts).
      const world = (lx: number): number => tool.position[0] + (facing > 0 ? -lx : lx)

      // NO MARKS IN THE KEEP-CLEAR BOX. There were caster arcs here - the
      // rubber a hand cart leaves swinging in to a port - drawn big enough
      // to read from standing height, and at that size they read as painted
      // circles inside the one square of floor a fab keeps clean. A real
      // scuff there is a few centimetres and wiped by the next shift; drawn
      // at all, it is wrong. The box stays clean.

      // The standing spot in front of the screen: the one square metre in
      // every bay that is walked on all shift.
      const hmiX = hmiFaceX(tool)
      blot(world(hmiX) + (rnd() - 0.5) * 0.2, faceZ + facing * 0.85, 0.45, 0.32, 0.17, '60,62,66')
    }

    // Tape ghosts: where a keep-clear box was re-laid, the adhesive's
    // outline stayed. One edge each, inside the walkway line, a dozen down
    // the hall.
    for (let k = 0; k < 12; k++) {
      const x = -L / 2 + 6 + rnd() * (L - 12)
      // In the main aisle either walkway line; in an outer aisle only the
      // line on the tools' side - nobody tapes a box against a wall.
      const side = aisle.side !== 0 ? -aisle.side : rnd() < 0.5 ? -1 : 1
      const z = aisle.walkZ + side * (aisle.walkLineHalf - 0.32 - rnd() * 0.25)
      const len = 0.7 + rnd() * 0.8
      ctx.fillStyle = `rgba(64,60,52,${0.14 + rnd() * 0.06})`
      ctx.fillRect(px(x), pz(z) - 1, len * mx, 2.2)
      if (rnd() < 0.5) ctx.fillRect(px(x) - 1, pz(z), 2.2, 0.35 * mz * (rnd() < 0.5 ? 1 : -1))
    }
  })
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

function buildFloorWear(): THREE.Group {
  const g = new THREE.Group()
  const aisles: Array<[WearAisle, number]> = [[mainAisleWear(), 4211]]
  // Fab B's outer aisles wear the same way, each with its own decal.
  if (BALLROOM) aisles.push([outerAisleWear(1), 5171], [outerAisleWear(-1), 6247])
  for (const [aisle, seed] of aisles) {
    const mat = new THREE.MeshStandardMaterial({
      map: floorWearTexture(aisle, seed),
      transparent: true,
      depthWrite: false,
      roughness: 0.97,
      polygonOffset: true,
      polygonOffsetFactor: -2,
    })
    const wear = new THREE.Mesh(new THREE.PlaneGeometry(FAB.length, 2 * aisle.halfZ), mat)
    wear.rotation.x = -Math.PI / 2
    // Over the painted lines (0.002-0.006) - tape wears too - and under
    // nothing; the offset keeps it off the tile at a grazing angle.
    wear.position.set(0, 0.008, aisle.centreZ)
    wear.renderOrder = 1
    g.add(wear)
  }
  return g
}

function buildFloorMarkings(): THREE.Group {
  const g = new THREE.Group()
  const frames: THREE.Matrix4[] = []
  for (const tool of TOOLS) {
    if (tool.position[2] === 0 || tool.loadPorts === 0) continue
    const facing = aisleDirection(tool)
    // From the face out: the box starts where the load ports' shelves end.
    const z = toolFaceZ(tool) + facing * (0.4 + 0.6)
    // IN FRONT OF THE PORTS, not of the whole machine. What the hatching
    // keeps clear is the ground a carrier is handed over, so on a six-metre
    // implanter it is a box at the front-end end, not a six-metre stripe.
    const lo = portX(tool, 0) - 0.55
    const hi = portX(tool, tool.loadPorts - 1) + 0.55
    const localMid = (lo + hi) / 2
    const frameAt = (x: number, zz: number): THREE.Matrix4 =>
      new THREE.Matrix4()
        .makeTranslation(x, 0.006, zz)
        .multiply(new THREE.Matrix4().makeRotationX(-Math.PI / 2))
        .multiply(new THREE.Matrix4().makeScale(Math.min(hi - lo, toolSize(tool).w, 4.6), 1.2, 1))
    frames.push(frameAt(tool.position[0] + (facing > 0 ? -localMid : localMid), z))
    // Fab B's copy of this tool: its ports face the outer aisle, mirrored
    // in x about the tool and in z about the service aisle's centreline.
    if (BALLROOM && hasSecondRowCopy(tool)) {
      frames.push(frameAt(tool.position[0] - (facing > 0 ? -localMid : localMid), 2 * Math.sign(tool.position[2]) * SECOND_ROW_PIVOT_Z - z))
    }
  }
  const mesh = new THREE.InstancedMesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({ map: keepClearTexture(), transparent: true, depthWrite: false }),
    frames.length,
  )
  frames.forEach((f, i) => mesh.setMatrixAt(i, f))
  mesh.frustumCulled = false
  g.add(mesh)
  return g
}

// ---------------------------------------------------------------- stockers
//
// The carrier stocker: the automated rack every bay keeps its lots in
// between steps, with a crane that fetches and files them. A wall of
// carriers on shelves with a machine moving in front of it is the second
// most recognisable thing in a fab after the hoists, and a student who has
// clicked a vehicle "bound for" a tool can now see where the lot waited.
// One at each end of the substrate bays, against the back wall.

export interface Stocker {
  group: THREE.Group
  /** The crane's mast (moves along x) and platform (moves along y). */
  mast: THREE.Group
  platform: THREE.Group
  /** Crane targets, in the rack's local frame, and its state. */
  targetX: number
  targetY: number
  timer: number
  x: number
  y: number
  /** Slot positions the crane can go to. */
  slots: Array<[number, number]>
}

export interface Stockers {
  group: THREE.Group
  stockers: Stocker[]
  advance(dt: number): void
}

/**
 * Set into the bulkhead like everything else, its back in the chase and
 * its crane on the aisle side. Kept under the hoist rail's carriers
 * (their undersides pass at 2.5 m) - a real stocker is bulkheaded exactly
 * this way, and a taller one would be in the vehicles' path.
 */
// Five metres before the first bay, whichever hall - so it packs with the
// row; `x` is refreshed on a hall switch (`refreshHallDerived`).
export const STOCKER = { x: Math.min(...TOOLS.filter((t) => t.position[2] !== 0).map((t) => t.position[0])) - 5, z: BULKHEAD_Z - 0.15, w: 4.4, h: 2.4, d: 1.0 }
const STOCKER_COLS = 5
const STOCKER_ROWS = 4

/** Footprints, for the walk's colliders. */
export function stockerColliders(): Array<{ minX: number; maxX: number; minZ: number; maxZ: number }> {
  return [1, -1].map((side) => ({
    minX: STOCKER.x - STOCKER.w / 2 - 0.3,
    maxX: STOCKER.x + STOCKER.w / 2 + 0.3,
    minZ: Math.min(side * (STOCKER.z - STOCKER.d / 2 - 1.1), side * (STOCKER.z + STOCKER.d / 2)),
    maxZ: Math.max(side * (STOCKER.z - STOCKER.d / 2 - 1.1), side * (STOCKER.z + STOCKER.d / 2)),
  }))
}

export function buildStockers(mats: FabMaterials, attach: Attachments): Stockers {
  const g = new THREE.Group()
  const stockers: Stocker[] = []
  const frameMat = mats.conduitDark
  for (const side of [1, -1] as const) {
    const r = new THREE.Group()
    r.position.set(STOCKER.x, 0, side * STOCKER.z)
    // The open front (local -z) faces the aisle; the back panel (local +z)
    // faces the hall's wall.
    r.rotation.y = side > 0 ? 0 : Math.PI
    const { w, h, d } = STOCKER
    // Frame: uprights, top, base, and the shelves.
    const parts: THREE.Mesh[] = []
    for (const dx of [-w / 2, w / 2]) {
      for (const dz of [-d / 2, d / 2]) {
        const post = new THREE.Mesh(box(0.08, h, 0.08), frameMat)
        post.position.set(dx, h / 2, dz)
        parts.push(post)
      }
    }
    const top = new THREE.Mesh(box(w + 0.1, 0.1, d + 0.1), mats.toolPanel)
    top.position.set(0, h + 0.05, 0)
    parts.push(top)
    const back = new THREE.Mesh(box(w, h, 0.04), mats.toolBody)
    back.position.set(0, h / 2, d / 2)
    parts.push(back)
    const slots: Array<[number, number]> = []
    for (let row = 0; row < STOCKER_ROWS; row++) {
      const y = 0.25 + row * 0.55
      const shelf = new THREE.Mesh(box(w - 0.1, 0.04, d - 0.1), mats.toolPanel)
      shelf.position.set(0, y, 0)
      parts.push(shelf)
      for (let col = 0; col < STOCKER_COLS; col++) {
        const x = -w / 2 + (w / STOCKER_COLS) * (col + 0.5)
        slots.push([x, y])
        // Most slots hold a carrier; a few are empty, as a working rack is.
        if ((row * 7 + col * 3 + (side > 0 ? 1 : 0)) % 5 === 0) continue
        r.updateMatrix()
        attach.foups.push(
          r.matrix.clone().multiply(new THREE.Matrix4().makeTranslation(x, y + 0.18, 0.05)),
        )
      }
    }
    for (const p of parts) r.add(p)
    // The crane: a rail along the front at floor and top, a mast that runs
    // along it, and a platform on the mast that lifts to the shelf. It
    // carries a carrier of its own.
    for (const y of [0.06, h - 0.06]) {
      const rail = new THREE.Mesh(box(w + 0.3, 0.06, 0.1), mats.steel)
      rail.position.set(0, y, -d / 2 - 0.35)
      r.add(rail)
    }
    // ENCLOSED. A stocker is a sealed cabinet with its own filtered air
    // and a robot inside; the first version was an open rack with the
    // crane running on the aisle side, which is a shelf, not a stocker,
    // and a contamination path no fab would run. Glass across the front
    // so the crane still shows, solid ends and a lid, and the two I/O
    // ports on the front where the crane's own errand goes.
    const zEnc = -d / 2 - 0.75
    const pane = new THREE.Mesh(box(w + 0.3, h - 0.5, 0.012), mats.glass)
    pane.position.set(0, 0.5 + (h - 0.5) / 2, zEnc)
    r.add(pane)
    const kick = new THREE.Mesh(box(w + 0.3, 0.5, 0.05), mats.toolBody)
    kick.position.set(0, 0.25, zEnc)
    r.add(kick)
    for (const dx of [-1, 1]) {
      const end = new THREE.Mesh(box(0.05, h + 0.1, d + 0.75), mats.toolBody)
      end.position.set(dx * (w / 2 + 0.15), (h + 0.1) / 2, (zEnc + d / 2) / 2)
      end.castShadow = true
      r.add(end)
    }
    const lid = new THREE.Mesh(box(w + 0.35, 0.1, d + 0.8), mats.toolPanel)
    lid.position.set(0, h + 0.05, (zEnc + d / 2) / 2)
    r.add(lid)
    for (const [i, px] of [-w / 2 + 0.4, -w / 2 + 0.95].entries()) {
      const plate = new THREE.Mesh(box(0.5, 0.08, 0.45), mats.toolPanel)
      plate.position.set(px, 0.92, zEnc - 0.22)
      r.add(plate)
      const seam = new THREE.Mesh(box(0.46, 0.5, 0.012), mats.conduitDark)
      seam.position.set(px, 1.2, zEnc - 0.03)
      r.add(seam)
      const label = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.06), portLabelMaterial(i === 0 ? 'IN' : 'OUT'))
      label.rotation.y = Math.PI
      label.position.set(px, 1.52, zEnc - 0.04)
      r.add(label)
      if (i === 1) {
        r.updateMatrix()
        attach.foups.push(r.matrix.clone().multiply(new THREE.Matrix4().makeRotationY(Math.PI).setPosition(px, 1.12, zEnc - 0.24)))
      }
    }
    mergeStatics(r, mats)
    const mast = new THREE.Group()
    const mastBar = new THREE.Mesh(box(0.1, h - 0.2, 0.1), mats.steel)
    mastBar.position.y = h / 2
    mast.add(mastBar)
    const platform = new THREE.Group()
    const plate = new THREE.Mesh(box(0.5, 0.05, 0.5), mats.conduitDark)
    plate.position.set(0, 0, 0.2)
    platform.add(plate)
    const foup = buildFoup(mats)
    for (const part of [...foup.children]) {
      part.position.y += 0.19
      part.position.z += 0.2
      platform.add(part)
    }
    mergeStatics(platform, mats)
    mast.add(platform)
    mast.position.set(0, 0, -d / 2 - 0.35)
    r.add(mast)
    g.add(r)
    stockers.push({
      group: r,
      mast,
      platform,
      targetX: slots[3][0],
      targetY: slots[3][1],
      timer: 0,
      x: 0,
      y: 0.3,
      slots,
    })
  }

  const advance = (dt: number): void => {
    for (const s of stockers) {
      s.timer += dt
      const dx = s.targetX - s.x
      const dy = s.targetY - s.y
      s.x += Math.sign(dx) * Math.min(Math.abs(dx), 0.7 * dt)
      s.y += Math.sign(dy) * Math.min(Math.abs(dy), 0.5 * dt)
      s.mast.position.x = s.x
      s.platform.position.y = s.y + 0.05
      if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01 && s.timer > 2.5) {
        // A new errand: another slot, or the port at the bottom corner.
        s.timer = 0
        if (Math.random() < 0.3) {
          s.targetX = -STOCKER.w / 2 + 0.4
          s.targetY = 0.25
        } else {
          const [x, y] = s.slots[Math.floor(Math.random() * s.slots.length)]
          s.targetX = x
          s.targetY = y
        }
      }
    }
  }
  return { group: g, stockers, advance }
}

/**
 * The asset-tag plate on the front of a tool.
 *
 * Drawn large and high-contrast because it has to be legible from across the
 * aisle - which is the whole point of it, and the one navigation cue here that
 * is not an abstraction. The accent stripe down the left edge repeats the
 * tool's colour so the plate and the light tower agree.
 */

/**
 * The paperwork on a tool.
 *
 * Photograph any production bay and the tool fronts are not smooth: they
 * are covered in small printed things. A rating plate by the disconnect, a
 * barcode the asset system reads, a calibration sticker with a date on it,
 * a warning triangle beside anything that can hurt you, an ESD mark where
 * you may touch, an earth-bond tag on the frame. Every one of them is 30 to
 * 120 mm across and none of them is decoration - a tool accretes paperwork
 * for as long as it is in service, and that accretion is most of what
 * separates a machine that is WORKING from a machine that was rendered.
 *
 * Twelve of them on one atlas, alpha-cut so a tile can be any shape inside
 * its square, and every sticker in the building merged into a single draw
 * (see `buildAttachments`). The art is deliberately readable only as shape
 * and colour: at the distance anyone stands from a tool face, a real
 * calibration label is a pale rectangle with a coloured band, and drawing
 * legible text on it would be a lie about how far away the reader is.
 *
 * AND EVERY ONE OF THEM CASTS A SHADOW, which is what was missing and what
 * made them read as printed on the tool rather than stuck to it. They stand
 * 14 mm off the skin - the accent band is 10 mm proud and they have to clear
 * it - so a real one at that stand-off has a visible line of shadow under
 * its lower edge, and there was none: the ambient-occlusion pass runs at a
 * 0.7 m radius and cannot see a 14 mm step, so nothing in the renderer was
 * ever going to draw it. It has to be in the art.
 *
 * Cast from the LABEL'S OUTLINE and not from the printing on it, which is
 * why each tile is now drawn on its own first and then stamped into the
 * sheet: shadow the strokes as they are laid down and every bar of the
 * barcode gets its own little shadow, and the label reads as embossed. One
 * `drawImage` of a finished tile casts one shadow, from its alpha
 * silhouette, which is the shape that is actually standing off the wall.
 */
const STICKER_COLS = 4
const STICKER_ROWS = 3
/**
 * Clear border inside each tile, in pixels of 128, for the shadow to fall in
 * without spilling into the neighbouring tile's UVs. The art shrinks by that
 * much, which a die-cut label has anyway.
 */
const STICKER_PAD = 7

function stickerAtlas(): THREE.CanvasTexture {
  const tex = paintStickerAtlas()
  // AUTHORED IN sRGB, like every other painted mark on this floor - the
  // hazard tape learned this the hard way (`stripeTexture`) and so did the
  // placards. Canvas colours are sRGB by definition; decoded as linear they
  // come out pale and desaturated, and a label that renders BRIGHTER than
  // the lit panel it is stuck to is a label that floats. Measured on the
  // face: the skin lands around 218, the paper white was reaching 238.
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

function paintStickerAtlas(): THREE.CanvasTexture {
  const T = 128
  // One tile at a time, off the sheet.
  const scratch = document.createElement('canvas')
  scratch.width = T
  scratch.height = T
  const sctx = scratch.getContext('2d')!
  return canvasTexture(STICKER_COLS * T, STICKER_ROWS * T, (ctx) => {
    ctx.clearRect(0, 0, STICKER_COLS * T, STICKER_ROWS * T)
    /** Draw into tile `i`, in its own 0..1 space. */
    const tile = (i: number, draw: (c: CanvasRenderingContext2D) => void): void => {
      sctx.setTransform(1, 0, 0, 1, 0, 0)
      sctx.clearRect(0, 0, T, T)
      sctx.save()
      sctx.scale(T, T)
      sctx.lineWidth = 0.012
      draw(sctx)
      sctx.restore()
      const x = (i % STICKER_COLS) * T
      const y = Math.floor(i / STICKER_COLS) * T
      ctx.save()
      // Clipped to its own cell, so a shadow that reaches the edge is cut
      // rather than smeared across the tile next door.
      ctx.beginPath()
      ctx.rect(x, y, T, T)
      ctx.clip()
      // Down and a little to the right: the room's light comes from the
      // filter ceiling, so what a label on a vertical face casts is mostly
      // under its bottom edge. Offsets and blur are in device pixels and
      // ignore the transform, which is why they are written as pixels of
      // 128 rather than as fractions of the tile.
      //
      // AND NOT DARK. A label 14 mm off a wall, under a ceiling that is one
      // continuous area light, casts a shadow that is shallow and soft - the
      // filter ceiling fills it in from every direction at once. Drawn dark
      // it stops being a shadow and becomes an outline, and an outlined label
      // reads as a sticker in a diagram. This is about three quarters of the
      // panel it falls on, which is what a 14 mm step under a diffuse source
      // actually costs. The alpha cut (0.5) then trims the soft fringe, so
      // what survives is a solid band two or three pixels of 128 wide - on a
      // 150 mm label, about 3 mm of shadow.
      ctx.shadowColor = 'rgba(126, 136, 150, 0.9)'
      ctx.shadowOffsetX = 1.5
      ctx.shadowOffsetY = 3
      ctx.shadowBlur = 2.5
      ctx.drawImage(scratch, x + STICKER_PAD, y + STICKER_PAD, T - 2 * STICKER_PAD, T - 2 * STICKER_PAD)
      ctx.restore()
    }
    /** A printed label: a plate, a coloured header, and lines of body text. */
    const plate = (c: CanvasRenderingContext2D, band: string, rows: number, y0 = 0.16, h = 0.68): void => {
      c.fillStyle = '#eef0f2'
      c.fillRect(0.06, y0, 0.88, h)
      c.strokeStyle = '#9aa2ab'
      c.strokeRect(0.06, y0, 0.88, h)
      c.fillStyle = band
      c.fillRect(0.06, y0, 0.88, 0.13)
      c.fillStyle = '#6c747d'
      for (let r = 0; r < rows; r++) {
        const w = r % 3 === 2 ? 0.42 : 0.74
        c.fillRect(0.12, y0 + 0.2 + r * 0.11, w, 0.05)
      }
    }
    // 0 warning triangle
    tile(0, (c) => {
      c.fillStyle = '#f2c018'
      c.beginPath(); c.moveTo(0.5, 0.1); c.lineTo(0.94, 0.86); c.lineTo(0.06, 0.86); c.closePath(); c.fill()
      c.strokeStyle = '#15181c'; c.stroke()
      c.fillStyle = '#15181c'
      c.fillRect(0.455, 0.36, 0.09, 0.3); c.fillRect(0.455, 0.71, 0.09, 0.09)
    })
    // 1 ESD - yellow ground triangle
    tile(1, (c) => {
      c.fillStyle = '#f2c018'
      c.fillRect(0.08, 0.14, 0.84, 0.72)
      c.strokeStyle = '#15181c'; c.strokeRect(0.08, 0.14, 0.84, 0.72)
      c.fillStyle = '#15181c'
      c.beginPath(); c.moveTo(0.5, 0.24); c.lineTo(0.78, 0.66); c.lineTo(0.22, 0.66); c.closePath(); c.fill()
      c.fillRect(0.3, 0.72, 0.4, 0.05)
    })
    // 2 barcode
    tile(2, (c) => {
      c.fillStyle = '#f7f8f9'; c.fillRect(0.04, 0.26, 0.92, 0.48)
      c.strokeStyle = '#aab1b8'; c.strokeRect(0.04, 0.26, 0.92, 0.48)
      c.fillStyle = '#14171b'
      let x = 0.1
      for (let i = 0; i < 22 && x < 0.9; i++) { const w = 0.008 + (i % 4) * 0.006; c.fillRect(x, 0.31, w, 0.28); x += w + 0.014 }
      c.fillRect(0.1, 0.63, 0.5, 0.045)
    })
    // 3 calibration due - green band
    tile(3, (c) => plate(c, '#2f9e57', 3))
    // 4 rating plate - grey band
    tile(4, (c) => plate(c, '#5b6670', 4))
    // 5 earth bond
    tile(5, (c) => {
      c.fillStyle = '#2f9e57'; c.beginPath(); c.arc(0.5, 0.5, 0.4, 0, Math.PI * 2); c.fill()
      c.fillStyle = '#f5f7f8'
      for (let i = 0; i < 3; i++) c.fillRect(0.5 - (0.26 - i * 0.07), 0.5 + i * 0.1, (0.26 - i * 0.07) * 2, 0.05)
      c.fillRect(0.47, 0.26, 0.06, 0.24)
    })
    // 6 inspection dot
    tile(6, (c) => {
      c.fillStyle = '#2f7ed8'; c.beginPath(); c.arc(0.5, 0.5, 0.33, 0, Math.PI * 2); c.fill()
      c.fillStyle = '#eaf1fb'; c.beginPath(); c.arc(0.5, 0.5, 0.15, 0, Math.PI * 2); c.fill()
    })
    // 7 flow arrow
    tile(7, (c) => {
      c.fillStyle = '#eef0f2'; c.fillRect(0.06, 0.32, 0.88, 0.36)
      c.strokeStyle = '#9aa2ab'; c.strokeRect(0.06, 0.32, 0.88, 0.36)
      c.fillStyle = '#2a3038'
      c.beginPath(); c.moveTo(0.76, 0.5); c.lineTo(0.54, 0.36); c.lineTo(0.54, 0.44); c.lineTo(0.18, 0.44)
      c.lineTo(0.18, 0.56); c.lineTo(0.54, 0.56); c.lineTo(0.54, 0.64); c.closePath(); c.fill()
    })
    // 8 lockout point - red band
    tile(8, (c) => plate(c, '#cf2f34', 2, 0.22, 0.56))
    // 9 laser aperture
    tile(9, (c) => {
      c.fillStyle = '#cf2f34'; c.fillRect(0.06, 0.24, 0.88, 0.52)
      c.fillStyle = '#f7f8f9'
      c.fillRect(0.12, 0.31, 0.52, 0.06); c.fillRect(0.12, 0.42, 0.66, 0.06); c.fillRect(0.12, 0.53, 0.4, 0.06)
      c.beginPath(); c.arc(0.82, 0.5, 0.07, 0, Math.PI * 2); c.fill()
    })
    // 10 QR-ish asset square
    tile(10, (c) => {
      c.fillStyle = '#f7f8f9'; c.fillRect(0.14, 0.14, 0.72, 0.72)
      c.fillStyle = '#14171b'
      for (let i = 0; i < 7; i++) for (let j = 0; j < 7; j++) {
        if (((i * 7 + j * 3) % 5) < 2) c.fillRect(0.18 + i * 0.093, 0.18 + j * 0.093, 0.075, 0.075)
      }
      for (const [a, b] of [[0.18, 0.18], [0.63, 0.18], [0.18, 0.63]] as const) {
        c.fillStyle = '#14171b'; c.fillRect(a, b, 0.19, 0.19)
        c.fillStyle = '#f7f8f9'; c.fillRect(a + 0.05, b + 0.05, 0.09, 0.09)
      }
    })
    // 11 hot surface
    tile(11, (c) => {
      c.fillStyle = '#f2c018'
      c.beginPath(); c.moveTo(0.5, 0.12); c.lineTo(0.92, 0.84); c.lineTo(0.08, 0.84); c.closePath(); c.fill()
      c.strokeStyle = '#15181c'; c.stroke()
      c.strokeStyle = '#15181c'; c.lineWidth = 0.035
      for (const dx of [-0.14, 0, 0.14]) {
        c.beginPath(); c.moveTo(0.5 + dx, 0.72); c.quadraticCurveTo(0.56 + dx, 0.58, 0.5 + dx, 0.46); c.stroke()
      }
    })
  })
}

/** UVs for one tile of the sticker atlas, on a unit quad. */
function stickerQuad(tileIndex: number, size: number): THREE.BufferGeometry {
  const geo = new THREE.PlaneGeometry(size, size)
  const uv = geo.attributes.uv
  const col = tileIndex % STICKER_COLS
  const row = Math.floor(tileIndex / STICKER_COLS)
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, (col + uv.getX(i)) / STICKER_COLS, (STICKER_ROWS - 1 - row + uv.getY(i)) / STICKER_ROWS)
  }
  return geo
}
function assetTagTexture(tag: string, accent: number): THREE.CanvasTexture {
  const accentCss = '#' + accent.toString(16).padStart(6, '0')
  return canvasTexture(512, 144, (ctx) => {
    ctx.fillStyle = '#f7f9fb'
    ctx.fillRect(0, 0, 512, 144)
    ctx.strokeStyle = '#9aa3ad'
    ctx.lineWidth = 6
    ctx.strokeRect(3, 3, 506, 138)
    ctx.fillStyle = accentCss
    ctx.fillRect(6, 6, 34, 132)
    ctx.fillStyle = '#14181d'
    ctx.font = 'bold 74px system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(tag, 275, 78)
  })
}

/** A cylinder, upright unless `axis` says otherwise. */
export const cyl = (
  rTop: number,
  rBottom: number,
  h: number,
  segments = 20,
): THREE.CylinderGeometry => new THREE.CylinderGeometry(rTop, rBottom, h, segments)

type Footprint = {
  w: number
  h: number
  d: number
  /** Local z of the front face (negative: the front is local -z). */
  front: number
  /** How far the face stands out of the bulkhead; the whole depth for a free-standing tool. */
  proud: number
  /** Local z of the bulkhead's face - where anything standing beside the tool backs onto. */
  bulk: number
  /**
   * The mainframe skin, [x0, x1]: the part of the face NOT taken by the
   * glazed front end. Everything a kind puts ON its face belongs between
   * these two numbers - the front end is glass and frame end to end, and
   * anything drawn across it is drawn behind it.
   */
  skin: [number, number]
}
type Silhouette = (g: THREE.Group, mats: FabMaterials, s: Footprint, accent: number) => void

/**
 * Hazard placards - the one thing on a real tool face that says what is
 * inside it. Text and a colour field, at the size of the real ones, one
 * canvas per kind for the whole floor.
 */
type PlacardKind = 'flammable' | 'toxic' | 'corrosive' | 'radiation' | 'laser' | 'hot' | 'rf' | 'esd' | 'hv' | 'inert'
const PLACARDS: Record<PlacardKind, { fill: string; ink: string; text: string; mark: string }> = {
  flammable: { fill: '#d8262c', ink: '#ffffff', text: 'FLAMMABLE', mark: '▲' },
  toxic: { fill: '#f4f4f4', ink: '#111111', text: 'TOXIC GAS', mark: '☠' },
  corrosive: { fill: '#f4f4f4', ink: '#111111', text: 'CORROSIVE', mark: '▼' },
  radiation: { fill: '#f5c542', ink: '#111111', text: 'RADIATION', mark: '☢' },
  laser: { fill: '#f5c542', ink: '#111111', text: 'LASER', mark: '✦' },
  hot: { fill: '#f08a24', ink: '#111111', text: 'HOT SURFACE', mark: '≋' },
  rf: { fill: '#f5c542', ink: '#111111', text: 'RF ENERGY', mark: '⚡' },
  esd: { fill: '#f5c542', ink: '#111111', text: 'ESD AREA', mark: '⚡' },
  hv: { fill: '#f5c542', ink: '#111111', text: 'HIGH VOLTAGE', mark: '⚡' },
  inert: { fill: '#f4f4f4', ink: '#111111', text: 'ASPHYXIANT', mark: '◯' },
}
const placardCache = new Map<PlacardKind, THREE.MeshBasicMaterial>()
export function placardMaterial(kind: PlacardKind): THREE.MeshBasicMaterial {
  let m = placardCache.get(kind)
  if (m) return m
  const p = PLACARDS[kind]
  const tex = canvasTexture(128, 128, (ctx) => {
    ctx.fillStyle = p.fill
    ctx.fillRect(0, 0, 128, 128)
    ctx.strokeStyle = p.ink
    ctx.lineWidth = 5
    ctx.strokeRect(5, 5, 118, 118)
    ctx.fillStyle = p.ink
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = 'bold 54px system-ui, sans-serif'
    ctx.fillText(p.mark, 64, 50)
    ctx.font = 'bold 17px system-ui, sans-serif'
    ctx.fillText(p.text, 64, 103)
  })
  tex.colorSpace = THREE.SRGBColorSpace
  m = new THREE.MeshBasicMaterial({ map: tex })
  placardCache.set(kind, m)
  return m
}

/**
 * The load-port number stencilled over each port: LP1, LP2 ... The one
 * label a technician reads at a port, since a lot is always dispatched to
 * a port by number. One canvas per number for the whole floor.
 */
const portLabelCache = new Map<string, THREE.MeshBasicMaterial>()
function portLabelMaterial(text: string): THREE.MeshBasicMaterial {
  let m = portLabelCache.get(text)
  if (m) return m
  const tex = canvasTexture(128, 48, (ctx) => {
    ctx.fillStyle = '#e9edf1'
    ctx.fillRect(0, 0, 128, 48)
    ctx.fillStyle = '#14181d'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = 'bold 30px system-ui, sans-serif'
    ctx.fillText(text, 64, 25)
  })
  tex.colorSpace = THREE.SRGBColorSpace
  m = new THREE.MeshBasicMaterial({ map: tex })
  portLabelCache.set(text, m)
  return m
}

/**
 * ONE unlit material for every glow and indicator on the floor, the colour
 * carried per vertex, so a face's five lit things merge into one draw
 * instead of five. Past white in linear light on purpose: these are
 * sources, and the high tier's bloom threshold is what tells a source
 * from a surface.
 */
export const UNLIT = new THREE.MeshBasicMaterial({ vertexColors: true })
type Tint = readonly [number, number, number]
export function tint(geometry: THREE.BufferGeometry, [r, g, b]: Tint): THREE.BufferGeometry {
  const n = geometry.attributes.position.count
  const colours = new Float32Array(n * 3)
  for (let i = 0; i < n; i++) {
    colours[i * 3] = r
    colours[i * 3 + 1] = g
    colours[i * 3 + 2] = b
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3))
  return geometry
}
/** The glow behind a viewport into something hot. */
const GLOW_HOT: Tint = [2.2, 0.9, 0.35]
/**
 * Gold-plated copper: QDT-24's sample holders and its feedthroughs.
 *
 * NOT FULLY METALLIC. A metalness-1 surface is lit only by what it
 * reflects, and this hall gives it little to reflect, so plated copper
 * rendered a dull olive-brown. Part metal, a light base, and a faint glow
 * of its own keep it reading as the bright gold it is.
 */
const GOLD_PLATE = new THREE.MeshStandardMaterial({
  color: 0xf6cd5c,
  metalness: 0.55,
  roughness: 0.3,
  emissive: 0x6e4d0c,
  emissiveIntensity: 0.45,
})
/** A dilution refrigerator's vacuum can: bright machined aluminium, not the darker steel of pipework. */
const CAN_ALU = new THREE.MeshStandardMaterial({ color: 0xd4d9de, metalness: 0.35, roughness: 0.32 })
/** EUV-22's source vessel: the enclosure's paint, without its panel map. */
const SOURCE_PAINT = new THREE.MeshStandardMaterial({ color: 0xe6e9ec, roughness: 0.5, metalness: 0.15 })
/** The white dial of a pressure gauge, and its needle. */
const GAUGE_FACE = new THREE.MeshStandardMaterial({ color: 0xf2f2ec, roughness: 0.5 })
const GAUGE_NEEDLE = new THREE.MeshStandardMaterial({ color: 0x1a1d21, roughness: 0.6 })
/** Cooling-water hoses: supply blue, return red. */
const HOSE_BLUE = new THREE.MeshStandardMaterial({ color: 0x2f6fb8, roughness: 0.6 })
const HOSE_RED = new THREE.MeshStandardMaterial({ color: 0xb8322f, roughness: 0.6 })
/**
 * SEM-23's console screens, painted once. The image is what a CD-SEM shows
 * of a line/space pattern - dark spaces, grey lines, the bright rims the
 * secondary electrons light at every edge - with the two measurement cursors
 * on one line; the recipe screen is its settings and a results table.
 */
const consoleScreens = new Map<string, THREE.MeshBasicMaterial>()
function consoleScreen(kind: 'image' | 'recipe'): THREE.MeshBasicMaterial {
  const cached = consoleScreens.get(kind)
  if (cached) return cached
  const tex = canvasTexture(256, 148, (ctx) => {
    if (kind === 'image') {
      ctx.fillStyle = '#1a1c1e'
      ctx.fillRect(0, 0, 256, 148)
      // Seeded noise: the same picture every session.
      let seed = 7
      const rnd = (): number => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296)
      for (let k = 0; k < 7; k++) {
        const x0 = 8 + k * 36
        ctx.fillStyle = '#5b5f63'
        ctx.fillRect(x0, 12, 16, 118)
        ctx.fillStyle = '#e8eaec'
        ctx.fillRect(x0 - 2, 12, 3, 118)
        ctx.fillRect(x0 + 15, 12, 3, 118)
      }
      for (let n = 0; n < 900; n++) {
        const v = Math.floor(rnd() * 120)
        ctx.fillStyle = `rgba(${v},${v},${v},0.35)`
        ctx.fillRect(rnd() * 256, 12 + rnd() * 118, 1, 1)
      }
      // The cursors on the middle line, and the reading.
      ctx.strokeStyle = '#3ddc84'
      ctx.lineWidth = 1
      for (const x of [115, 134]) {
        ctx.beginPath()
        ctx.moveTo(x, 40)
        ctx.lineTo(x, 104)
        ctx.stroke()
      }
      ctx.fillStyle = '#0b0c0d'
      ctx.fillRect(0, 130, 256, 18)
      ctx.fillStyle = '#3ddc84'
      ctx.font = 'bold 12px monospace'
      ctx.fillText('CD 32.4 nm', 6, 143)
      ctx.fillStyle = '#c9cdd1'
      ctx.fillText('500 eV  200kx', 150, 143)
      ctx.fillStyle = '#0b0c0d'
      ctx.fillRect(0, 0, 256, 11)
    } else {
      ctx.fillStyle = '#10243a'
      ctx.fillRect(0, 0, 256, 148)
      ctx.fillStyle = '#2e6da4'
      ctx.fillRect(0, 0, 256, 16)
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 11px sans-serif'
      ctx.fillText('RECIPE  ADI-M1-LS', 6, 12)
      ctx.font = '10px monospace'
      const rows = ['Site  CD(nm)  LWR', ' 1    32.4   2.1', ' 2    32.1   2.3', ' 3    32.9   2.0', ' 4    31.8   2.4', ' 5    32.6   2.2']
      rows.forEach((row, i) => {
        ctx.fillStyle = i === 0 ? '#9fc3e6' : '#dce6ef'
        ctx.fillText(row, 8, 32 + i * 16)
      })
      ctx.fillStyle = '#1f8f4a'
      ctx.fillRect(170, 116, 78, 22)
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 11px sans-serif'
      ctx.fillText('IN SPEC', 184, 131)
    }
  })
  tex.colorSpace = THREE.SRGBColorSpace
  const material = new THREE.MeshBasicMaterial({ map: tex })
  consoleScreens.set(kind, material)
  return material
}

/** A flexible line along a few points: braided helium hose, a coax bundle. */
function hose(points: Array<[number, number, number]>, r: number): THREE.BufferGeometry {
  const curve = new THREE.CatmullRomCurve3(points.map(([x, y, z]) => new THREE.Vector3(x, y, z)))
  return new THREE.TubeGeometry(curve, 24, r, 8, false)
}
/** Indicator strips on electronics racks and module doors. */
const LED_GREEN: Tint = [0.3, 1.8, 0.7]
const LED_BLUE: Tint = [0.3, 0.9, 2.0]
const LED_AMBER: Tint = [2.0, 1.2, 0.2]
/** Floor drain grating in front of the wet tools. */
const GRATE = new THREE.MeshStandardMaterial({ color: 0x3a4048, roughness: 0.8, metalness: 0.4 })

/**
 * The parts a face is assembled from. Every helper places in the tool's
 * local frame, where the front is -z and `f` is the face plane; "in front
 * of" means smaller z.
 */
/** Patches of the face the silhouette has claimed (face.claim); reset per tool. */
const faceClaims: Array<[number, number, number, number]> = []

const face = {
  /** A door or access panel flush in a face, with its handle. */
  door(g: THREE.Group, mats: FabMaterials, w: number, h: number, x: number, y: number, f: number): void {
    const panel = new THREE.Mesh(box(w, h, 0.03), mats.toolPanel)
    panel.position.set(x, y, f - 0.015)
    g.add(panel)
    const handle = new THREE.Mesh(box(0.03, 0.2, 0.04), mats.steel)
    handle.position.set(x + w / 2 - 0.09, y, f - 0.05)
    g.add(handle)
  },
  /** A window in a face: frame, dark reveal, glass. */
  window(g: THREE.Group, mats: FabMaterials, w: number, h: number, x: number, y: number, f: number, back: THREE.Material = mats.screen): void {
    const frame = new THREE.Mesh(box(w + 0.1, h + 0.1, 0.05), mats.toolPanel)
    frame.position.set(x, y, f - 0.025)
    g.add(frame)
    const reveal = new THREE.Mesh(box(w, h, 0.02), back)
    reveal.position.set(x, y, f - 0.045)
    g.add(reveal)
    const pane = new THREE.Mesh(box(w, h, 0.01), mats.glass)
    pane.position.set(x, y, f - 0.062)
    g.add(pane)
  },
  /**
   * A glazed housing standing proud of the face, open to it, for the
   * mechanism a tool shows through its window: the saw, the scope, the
   * press. Parts go inside it, between `f` and `f - depth`.
   */
  hood(g: THREE.Group, mats: FabMaterials, w: number, h: number, depth: number, x: number, y: number, f: number): void {
    const t = 0.04
    const zc = f - depth / 2
    const top = new THREE.Mesh(box(w + 2 * t, t, depth), mats.toolPanel)
    top.position.set(x, y + h / 2 + t / 2, zc)
    g.add(top)
    const bottom = new THREE.Mesh(box(w + 2 * t, t, depth), mats.toolPanel)
    bottom.position.set(x, y - h / 2 - t / 2, zc)
    g.add(bottom)
    for (const dx of [-1, 1]) {
      const side = new THREE.Mesh(box(t, h, depth), mats.toolPanel)
      side.position.set(x + dx * (w / 2 + t / 2), y, zc)
      g.add(side)
    }
    const pane = new THREE.Mesh(box(w, h, 0.01), mats.glass)
    pane.position.set(x, y, f - depth + 0.005)
    g.add(pane)
  },
  /** A hazard placard on a surface at `f`, facing the aisle. */
  placard(g: THREE.Group, kind: PlacardKind, size: number, x: number, y: number, f: number): void {
    const sign = new THREE.Mesh(new THREE.PlaneGeometry(size, size), placardMaterial(kind))
    sign.rotation.y = Math.PI
    sign.position.set(x, y, f - 0.006)
    g.add(sign)
    face.claim(x - size / 2, x + size / 2, y - size / 2, y + size / 2)
  },
  /**
   * Keep the printed labels off a patch of the face: a placard, a rack of
   * instruments. `buildTool` scatters its stickers after the silhouette has
   * run, and a notice stuck across the middle of a FLAMMABLE placard is a
   * thing no safety walk would leave there.
   */
  claim(x0: number, x1: number, y0: number, y1: number): void {
    faceClaims.push([x0, x1, y0, y1])
  },
  /**
   * A cabinet standing beside the face, its back on the bulkhead: the gas
   * box, the RF rack, the chemical cabinet that every real tool has next
   * to it and that is the first thing a technician is told not to open.
   */
  cabinet(g: THREE.Group, mats: FabMaterials, w: number, h: number, depth: number, x: number, s: Footprint, mat = mats.toolPanel): number {
    const zFront = s.bulk - depth
    const body = new THREE.Mesh(box(w, h, depth), mat)
    body.position.set(x, h / 2, s.bulk - depth / 2)
    body.castShadow = true
    g.add(body)
    const handle = new THREE.Mesh(box(0.03, 0.18, 0.04), mats.steel)
    handle.position.set(x + w / 2 - 0.1, 1.05, zFront - 0.02)
    g.add(handle)
    return zFront
  },
  /** An indicator strip: a row of LEDs on a rack or a module door. */
  leds(g: THREE.Group, colour: Tint, w: number, x: number, y: number, f: number): void {
    const strip = new THREE.Mesh(tint(box(w, 0.025, 0.01), colour), UNLIT)
    strip.position.set(x, y, f - 0.005)
    g.add(strip)
  },
  /**
   * A floor drain grating in front of a wet tool. Kept at the tool's feet
   * and no wider than the wet part of it: a full-width strip a metre out
   * read as a black mat laid across the walkway, and a trench drain in a
   * real wet bay runs under the bench, not down the middle of the aisle.
   */
  drain(g: THREE.Group, w: number, x: number, z: number): void {
    const grate = new THREE.Mesh(new THREE.PlaneGeometry(w, 0.28), GRATE)
    grate.rotation.x = -Math.PI / 2
    // Over the keep-clear marking (0.006), which otherwise paints it out.
    grate.position.set(x, 0.009, z)
    g.add(grate)
  },
}

/**
 * What each tool shows of itself from the aisle.
 *
 * REALISTIC ON PURPOSE, which reverses the choice this file made before. The
 * earlier faces were textbook cutaways - the beamline exposed, the baths open,
 * platens turning on the roof - chosen so a student could tell twenty tools
 * apart by shape. They were also what a real floor never shows, and a
 * training environment that teaches a technician to expect an implanter with
 * its beamline in the air has taught the wrong thing. A current fab is a
 * bulkhead wall with faces in it, and a technician tells them apart the way
 * these faces now let a student: by the placard, the light tower, the vendor
 * livery, and by WHAT STANDS BESIDE EACH ONE - an RF rack, an ampoule
 * cabinet, a source-gas box, a chemical cabinet, a tester rack, a test head
 * on its manipulator - and by what each face carries: a viewport with a hot
 * glow, a radiation interlock door, a saw behind glass.
 *
 * Each entry adds to the shared face rather than replacing it, since real
 * tools do share a chassis. No vendor's product is modelled: category shapes,
 * not copies of any real tool from Applied, Lam, TEL, ASML, Ebara or anyone
 * else.
 */
const SILHOUETTES: Partial<Record<ToolKind, Silhouette>> = {
  // Cluster etch: the RF generators and match networks in a rack beside
  // the face, the coax up to the match on the chamber lid.
  // The coax runs inside the bulkhead to the match on the chamber lid; an
  // earlier version drew it up the open air to a box on the roof, which
  // no fab wires that way.
  etcher: (g, mats, s) => {
    const x = s.w / 2 + 0.65
    const zf = face.cabinet(g, mats, 0.9, 1.7, 0.6, x, s)
    face.placard(g, 'rf', 0.26, x, 1.4, zf)
    face.leds(g, LED_GREEN, 0.5, x, 0.55, zf)
    face.leds(g, LED_BLUE, 0.5, x, 0.5, zf)
    // The generators' front panels: a meter window each.
    for (const y of [1.0, 0.8]) face.window(g, mats, 0.4, 0.1, x, y, zf)
    // WHAT AN ETCHER RUNS ON, which the face never said. RF was the only
    // hazard placarded here, and a plasma etch bay's gas box holds Cl2,
    // HBr and BCl3 - the most toxic and the most corrosive cabinet on the
    // front-end floor, and the reason an etch bay has gas monitoring in it.
    face.placard(g, 'toxic', 0.24, s.skin[0] + 0.4, 1.52, s.front)
    face.placard(g, 'corrosive', 0.24, s.skin[0] + 0.68, 1.52, s.front)
  },

  // Deposition: the heated precursor cabinet, its ampoules on top and the
  // lagged delivery line up and over into the tool.
  deposition: (g, mats, s) => {
    const x = -s.w / 2 - 0.55
    const zf = face.cabinet(g, mats, 0.8, 1.4, 0.6, x, s)
    face.placard(g, 'hot', 0.26, x - 0.16, 1.1, zf)
    // Metal-organic precursors are pyrophoric: the cabinet says so.
    face.placard(g, 'flammable', 0.26, x + 0.16, 1.1, zf)
    // The ampoules are INSIDE the heated cabinet - an earlier version
    // stood them on its roof, which is a spill nobody would allow. What
    // shows is the sight window onto them and the lagged delivery line
    // from the cabinet top into the tool.
    face.window(g, mats, 0.3, 0.3, x, 0.6, zf)
    const rise = new THREE.Mesh(cyl(0.06, 0.06, s.h - 1.55, 10), mats.toolPanel)
    rise.position.set(x - 0.25, 1.4 + (s.h - 1.55) / 2, s.bulk - 0.2)
    g.add(rise)
    const run = new THREE.Mesh(cyl(0.06, 0.06, 0.8, 10), mats.toolPanel)
    run.rotation.z = Math.PI / 2
    run.position.set(x + 0.15, s.h - 0.15, s.bulk - 0.2)
    g.add(run)
  },

  // Epi: a lamp-heated reactor. No viewport - the earlier face had a port
  // with the lamps glowing through it, and a reactor shows its aisle
  // nothing of the sort; the lamp module is a metre inside behind the
  // process kit. What tells it apart is the lamp power cabinet beside it,
  // with the meter windows and the HOT placard, and the gas placards on
  // the face for what it runs on.
  epi: (g, mats, s) => {
    const x = s.w / 2 + 0.65
    const zf = face.cabinet(g, mats, 0.9, 1.9, 0.6, x, s)
    face.placard(g, 'hot', 0.26, x, 1.45, zf)
    for (const y of [1.05, 0.85]) face.window(g, mats, 0.4, 0.1, x, y, zf)
    face.leds(g, LED_AMBER, 0.5, x, 0.6, zf)
    face.leds(g, LED_GREEN, 0.5, x, 0.55, zf)
    // Epi runs on silane and dichlorosilane with HCl for the etch-back: the
    // gas box is in the chase, and its placards are on the face - on the
    // skin, in the row a real bay hangs them in, not scattered up the
    // middle of what is now the front end's glass.
    const px = s.skin[0] + 0.45
    face.placard(g, 'flammable', 0.24, px, 1.55, s.front)
    face.placard(g, 'toxic', 0.24, px + 0.28, 1.55, s.front)
    face.placard(g, 'corrosive', 0.24, px + 0.56, 1.55, s.front)
  },

  // Vertical furnace: a tall cabinet with the tube inside it, the boat
  // loader's service door, and the exhaust stack up through the ceiling.
  // No window: a furnace shows nothing of its heat from the aisle - the
  // tube is a metre inside the cabinet, behind the boat elevator - and
  // the sight slit the first version had was an invention. What says
  // "hot" here is the placard, the stack, and the thermocouple loop on
  // the door.
  furnace: (g, mats, s) => {
    const x = -s.w / 2 + 0.75
    face.door(g, mats, 1.0, 2.2, x, 1.2, s.front)
    face.placard(g, 'hot', 0.24, x + 0.28, 1.95, s.front - 0.03)
    face.leds(g, LED_AMBER, 0.3, x - 0.2, 1.95, s.front - 0.03)
    // What the furnace runs on, placarded the way a thermal bay marks its
    // cabinets: the process gas, and the nitrogen the tube is purged and
    // idled under. NITROGEN IS NOT FLAMMABLE - it is an asphyxiant, and a
    // fab placards it as one. Carrying a flammable diamond for the purge
    // was the one placard on the floor that named the wrong hazard, which
    // in a room where the placard IS the safety training is worse than no
    // placard at all.
    face.placard(g, 'flammable', 0.2, x - 0.22, 1.65, s.front - 0.03)
    face.placard(g, 'inert', 0.2, x + 0.02, 1.65, s.front - 0.03)
    const stack = new THREE.Mesh(cyl(0.16, 0.16, FAB.ceilingHeight - s.h, 16), mats.conduit)
    stack.position.set(s.w / 2 - 0.5, s.h + (FAB.ceilingHeight - s.h) / 2, s.front + 0.28)
    g.add(stack)
    const flange = new THREE.Mesh(cyl(0.22, 0.22, 0.05, 16), mats.steel)
    flange.position.set(s.w / 2 - 0.5, s.h + 0.03, s.front + 0.28)
    g.add(flange)
  },

  // Scanner and track: the track is the face with the load ports; the
  // scanner is the taller, plainer box beside it, joined in-line, with the
  // interlocked door and the laser placard on its front.
  litho: (g, mats, s) => {
    const sw = 2.8
    const sh = 2.9
    const x = -s.w / 2 - sw / 2 - 0.35
    const scanner = new THREE.Mesh(box(sw, sh, s.proud + 0.1), mats.toolBody)
    scanner.position.set(x, sh / 2, s.bulk - (s.proud + 0.1) / 2)
    scanner.castShadow = true
    g.add(scanner)
    const livery = new THREE.Mesh(
      box(sw + 0.02, 0.12, s.proud + 0.12),
      new THREE.MeshStandardMaterial({ color: 0xffc857, emissive: 0xffc857, emissiveIntensity: 0.35, roughness: 0.5 }),
    )
    livery.position.set(x, sh - 0.35, s.bulk - (s.proud + 0.1) / 2)
    g.add(livery)
    const zf = s.bulk - s.proud - 0.1
    face.door(g, mats, 0.9, 2.0, x + 0.6, 1.1, zf)
    face.placard(g, 'laser', 0.26, x - 0.6, 1.7, zf)
    face.leds(g, LED_AMBER, 0.4, x - 0.6, 1.4, zf)
    // LITH-03 is a scanner AND ITS TRACK, and the track is the half that
    // handles litres of solvent - the resist's casting solvent and the
    // developer. Every real coat/develop track carries a flammable
    // placard for it; this face carried only the scanner's laser.
    //
    // LOW on the skin, not at the eye height a placard normally takes.
    // Four load ports leave this face barely a metre of skin, and the HMI
    // bezel stands 0.62 m proud across the whole of it from 1.21 to 1.79:
    // anything hung there at 1.5 m is behind a monitor.
    face.placard(g, 'flammable', 0.22, s.skin[0] + 0.35, 0.72, s.front)
    // The reticle port: where a reticle pod goes into the scanner, on a
    // small shelf of its own on the scanner's face - the one port on
    // the floor that takes a pod and not a carrier.
    const rx = x - 0.6
    const shelf = new THREE.Mesh(box(0.36, 0.04, 0.3), mats.toolPanel)
    shelf.position.set(rx, 0.98, zf - 0.15)
    g.add(shelf)
    const portDoor = new THREE.Mesh(box(0.3, 0.26, 0.012), mats.conduitDark)
    portDoor.position.set(rx, 1.14, zf - 0.006)
    g.add(portDoor)
    const pod = new THREE.Mesh(box(0.2, 0.06, 0.2), mats.conduitDark)
    pod.position.set(rx, 1.03, zf - 0.16)
    g.add(pod)
    const rspLabel = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.06), portLabelMaterial('RSP'))
    rspLabel.rotation.y = Math.PI
    rspLabel.position.set(rx, 1.3, zf - 0.006)
    g.add(rspLabel)
    // The in-line interface between track and scanner.
    const bridge = new THREE.Mesh(box(0.4, 1.2, s.proud - 0.1), mats.toolPanel)
    bridge.position.set(-s.w / 2 - 0.18, 1.3, s.bulk - (s.proud - 0.1) / 2)
    g.add(bridge)
  },

  // SAQP cell: four process modules in a fixed order, deposit, etch,
  // deposit, etch, read off their indicator colours on the module doors.
  // Across the skin at chest height, where the doors a technician opens
  // actually are - up at 1.6 m they were behind the front end's glass and
  // their indicator strips ran through the accent band.
  patterning: (g, mats, s) => {
    const pitch = 0.48
    const x0 = s.skin[0] + 0.35
    for (let i = 0; i < 4; i++) {
      const x = x0 + i * pitch
      if (x + 0.21 > s.skin[1] - 0.05) break
      face.door(g, mats, 0.42, 0.5, x, 1.0, s.front)
      face.leds(g, i % 2 === 0 ? LED_GREEN : LED_BLUE, 0.3, x, 1.32, s.front - 0.03)
      const label = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.06), portLabelMaterial(`PM${i + 1}`))
      label.rotation.y = Math.PI
      label.position.set(x, 1.42, s.front - 0.006)
      g.add(label)
    }
  },

  // Process-gas cabinets: five doors with sight windows, each placarded
  // for what is in it, and the gas monitoring panel with its channels.
  facilities: (g, mats, s) => {
    // The doors stop under the accent band and the tag: at 1.9 m they
    // reached over both, and FAC-06 was the one face on the floor with no
    // legible tag.
    for (let i = 0; i < 5; i++) {
      const x = -s.w / 2 + 0.45 + i * 0.72
      face.door(g, mats, 0.64, 1.5, x, 0.8, s.front)
      face.window(g, mats, 0.28, 0.36, x, 1.25, s.front - 0.03)
      face.placard(g, i % 2 === 0 ? 'flammable' : 'toxic', 0.22, x, 0.7, s.front - 0.03)
      // The cylinder inside, seen through the sight window.
      const bottle = new THREE.Mesh(cyl(0.11, 0.11, 0.5, 12), mats.steel)
      bottle.position.set(x, 1.25, s.front + 0.1)
      g.add(bottle)
    }
    const x = s.w / 2 - 0.5
    const panel = new THREE.Mesh(box(0.5, 0.5, 0.06), mats.conduitDark)
    panel.position.set(x, 0.9, s.front - 0.03)
    g.add(panel)
    for (let i = 0; i < 5; i++) face.leds(g, i === 3 ? LED_AMBER : LED_GREEN, 0.3, x, 1.08 - i * 0.08, s.front - 0.06)
  },

  // Metrology: mostly screen and paperwork - the data display and its
  // keyboard tray on the skin, the laser placard the optics earn, and the
  // one thing that tells a metrology bay from an inspection bay at a
  // glance: the MONITOR-WAFER CABINET standing beside it. Every metrology
  // tool on a real floor has one, because the tool is qualified against
  // known wafers before anybody trusts a number it prints.
  metrology: (g, mats, s) => {
    const x = s.skin[0] + 0.75
    const bezel = new THREE.Mesh(box(0.72, 0.46, 0.05), mats.toolPanel)
    bezel.position.set(x, 1.42, s.front - 0.03)
    g.add(bezel)
    const screen = new THREE.Mesh(box(0.64, 0.38, 0.01), mats.screen)
    screen.position.set(x, 1.42, s.front - 0.06)
    g.add(screen)
    const tray = new THREE.Mesh(box(0.7, 0.03, 0.28), mats.toolPanel)
    tray.position.set(x, 0.96, s.front - 0.15)
    g.add(tray)
    face.placard(g, 'laser', 0.22, x + 0.6, 1.42, s.front)
    // The cabinet: a shelved rack against the bulkhead with a glazed front,
    // the wafer boxes on its shelves. Built open rather than as a box with
    // a window in it - a reveal behind the glass hides whatever is meant to
    // be seen through it, which is what happened the first time.
    const cx = -s.w / 2 - 0.55
    const D = 0.55
    const back = s.bulk - 0.03
    const zf = s.bulk - D
    const zc = s.bulk - D / 2
    // Carcass: back, sides, top, and the closed base under the glazing.
    const carcass = new THREE.Mesh(box(0.82, 1.75, 0.06), mats.toolPanel)
    carcass.position.set(cx, 0.875, back)
    carcass.castShadow = true
    g.add(carcass)
    for (const dx of [-0.38, 0.38]) {
      const sidePanel = new THREE.Mesh(box(0.06, 1.75, D), mats.toolPanel)
      sidePanel.position.set(cx + dx, 0.875, zc)
      g.add(sidePanel)
    }
    for (const [y, h] of [[1.72, 0.06], [0.26, 0.52]] as const) {
      const panel = new THREE.Mesh(box(0.76, h, D), mats.toolPanel)
      panel.position.set(cx, y, zc)
      g.add(panel)
    }
    const cavity = new THREE.Mesh(box(0.7, 1.15, 0.01), CAVITY_DARK)
    cavity.position.set(cx, 1.12, back - 0.04)
    g.add(cavity)
    for (const y of [0.72, 1.12, 1.52]) {
      const shelf = new THREE.Mesh(box(0.7, 0.02, 0.44), mats.steel)
      shelf.position.set(cx, y, zc)
      g.add(shelf)
      for (const dx of [-0.17, 0.17]) {
        const pod = new THREE.Mesh(box(0.26, 0.13, 0.28), mats.foup)
        pod.position.set(cx + dx, y + 0.075, zc)
        g.add(pod)
      }
    }
    const pane = new THREE.Mesh(box(0.7, 1.15, 0.01), mats.glass)
    pane.position.set(cx, 1.12, zf + 0.02)
    g.add(pane)
    const cabHandle = new THREE.Mesh(box(0.03, 0.2, 0.04), mats.steel)
    cabHandle.position.set(cx + 0.3, 1.12, zf - 0.02)
    g.add(cabHandle)
  },

  // Implanter: the longest face in the bay, and the only one with a
  // radiation interlock. End-station door on the left, beacon over it,
  // and the source-gas cabinet with its own placard beside.
  implanter: (g, mats, s) => {
    const dx = -s.w / 2 + 0.8
    face.door(g, mats, 1.0, 2.0, dx, 1.05, s.front)
    face.placard(g, 'radiation', 0.28, dx - 0.18, 1.75, s.front - 0.03)
    // The terminal sits at tens of kilovolts: the one tool in the bay
    // placarded for high voltage as well as X-rays.
    face.placard(g, 'hv', 0.28, dx + 0.18, 1.75, s.front - 0.03)
    const beacon = new THREE.Mesh(cyl(0.05, 0.05, 0.1, 10), EMO_RED)
    beacon.position.set(dx, s.h + 0.1, s.front + 0.2)
    g.add(beacon)
    const x = s.w / 2 + 0.6
    const zf = face.cabinet(g, mats, 0.8, 2.0, 0.6, x, s)
    face.window(g, mats, 0.3, 0.4, x, 1.55, zf)
    face.placard(g, 'toxic', 0.24, x, 1.0, zf)
  },

  // Single-wafer clean: a plain face over its ports, the chemical cabinet
  // beside it placarded, and the drain grating in the floor in front.
  wetbench: (g, mats, s) => {
    const x = -s.w / 2 - 0.6
    const zf = face.cabinet(g, mats, 0.9, 1.9, 0.6, x, s)
    face.window(g, mats, 0.34, 0.4, x, 1.5, zf)
    // Dilute HF is what a clean runs on, and HF is a poison as well as
    // an acid: both placards, the way a wet bay's cabinets carry both.
    face.placard(g, 'corrosive', 0.24, x - 0.16, 0.95, zf)
    face.placard(g, 'toxic', 0.24, x + 0.16, 0.95, zf)
    face.drain(g, Math.min(s.w * 0.55, 2.4), 0, s.front - 0.5)
  },

  // CMP: wet, so the slurry cabinet, the drain, and the cone a technician
  // leaves on the floor in front of it.
  cmp: (g, mats, s) => {
    const x = s.w / 2 + 0.6
    const zf = face.cabinet(g, mats, 0.9, 1.6, 0.6, x, s)
    face.placard(g, 'corrosive', 0.24, x, 1.1, zf)
    face.drain(g, Math.min(s.w * 0.55, 2.4), 0, s.front - 0.5)
    const cone = new THREE.Mesh(cyl(0.02, 0.15, 0.5, 12), mats.levellingPad)
    cone.position.set(s.w / 2 + 0.9, 0.25, s.front - 1.3)
    g.add(cone)
  },

  // Plating: the chemical cabinet, its drip tray, the corrosive placard.
  plating: (g, mats, s) => {
    const x = s.w / 2 + 0.6
    const zf = face.cabinet(g, mats, 0.9, 1.9, 0.6, x, s)
    face.window(g, mats, 0.34, 0.4, x, 1.5, zf)
    face.placard(g, 'corrosive', 0.24, x, 0.95, zf)
    // The drip tray under the chemical cabinet: a bunded stainless pan.
    // In the levelling pads' yellow it read as a mat somebody had put down.
    const tray = new THREE.Mesh(box(1.0, 0.03, 0.6), mats.steel)
    tray.position.set(x, 0.015, zf - 0.32)
    g.add(tray)
    for (const [w, d, dx, dz] of [[1.0, 0.04, 0, -0.28], [1.0, 0.04, 0, 0.28], [0.04, 0.6, -0.48, 0], [0.04, 0.6, 0.48, 0]] as const) {
      const kerb = new THREE.Mesh(box(w, 0.06, d), mats.steel)
      kerb.position.set(x + dx, 0.03, zf - 0.32 + dz)
      g.add(kerb)
    }
    face.drain(g, Math.min(s.w * 0.55, 2.4), 0, s.front - 0.5)
  },

  // Czochralski puller: the one vessel in the hall that shows itself - the
  // pull chamber rising from the face to the ceiling, and the viewport
  // onto the melt at eye height.
  crystal: (g, mats, s) => {
    const vessel = new THREE.Mesh(cyl(0.5, 0.55, FAB.ceilingHeight - s.h, 24), mats.steel)
    vessel.position.set(0, s.h + (FAB.ceilingHeight - s.h) / 2, s.front + 0.3)
    vessel.castShadow = true
    g.add(vessel)
    const collar = new THREE.Mesh(cyl(0.62, 0.62, 0.1, 24), mats.toolPanel)
    collar.position.set(0, s.h + 0.05, s.front + 0.3)
    g.add(collar)
    // The furnace chamber's door: a round flanged vessel door on the
    // face, its clamp bolts round the rim, and the viewport onto the
    // melt in its centre - a pressure vessel, not a cabinet with a hole.
    const dx = -0.55
    const dy = 1.3
    const door = new THREE.Mesh(cyl(0.45, 0.45, 0.05, 32), mats.steel)
    door.rotation.x = Math.PI / 2
    door.position.set(dx, dy, s.front - 0.025)
    g.add(door)
    const flange = new THREE.Mesh(new THREE.TorusGeometry(0.46, 0.03, 8, 32), mats.steel)
    flange.position.set(dx, dy, s.front - 0.05)
    g.add(flange)
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2
      const bolt = new THREE.Mesh(cyl(0.025, 0.025, 0.03, 8), mats.conduitDark)
      bolt.rotation.x = Math.PI / 2
      bolt.position.set(dx + Math.cos(a) * 0.38, dy + Math.sin(a) * 0.38, s.front - 0.065)
      g.add(bolt)
    }
    const hinge = new THREE.Mesh(box(0.06, 0.5, 0.08), mats.steel)
    hinge.position.set(dx - 0.52, dy, s.front - 0.04)
    g.add(hinge)
    const port = new THREE.Mesh(tint(new THREE.CircleGeometry(0.13, 20), GLOW_HOT), UNLIT)
    port.rotation.y = Math.PI
    port.position.set(dx, dy, s.front - 0.056)
    g.add(port)
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.15, 0.025, 8, 24), mats.steel)
    ring.position.set(dx, dy, s.front - 0.06)
    g.add(ring)
    face.placard(g, 'hot', 0.22, 0.3, 1.88, s.front)
    // The argon a puller runs under is an asphyxiant, and the placard
    // says so on every real one.
    face.placard(g, 'inert', 0.22, dx, 0.62, s.front)
  },

  // Wafer prep: the lapping plate turning behind a window in the face.
  waferprep: (g, mats, s) => {
    // The hood tops out under the asset tag (bodyH - 0.35): a taller one hid
    // the tag's lower half behind its top rail.
    face.hood(g, mats, 1.1, 0.9, 0.45, 0, 0.94, s.front)
    const plate = new THREE.Mesh(cyl(0.4, 0.4, 0.06, 28), mats.steel)
    plate.position.set(0, 0.74, s.front - 0.22)
    g.add(plate)
    const grooves = new THREE.Mesh(
      cyl(0.38, 0.38, 0.02, 28),
      new THREE.MeshStandardMaterial({ map: padGrooveTexture(), roughness: 0.5, metalness: 0.4 }),
    )
    grooves.position.set(0, 0.78, s.front - 0.22)
    grooves.userData.spin = 1.6
    g.add(grooves)
    // The carrier head over the plate on its sweep arm, and the plate's
    // spindle under it - a lapping plate on its own is a disc in a box.
    const spindle = new THREE.Mesh(cyl(0.06, 0.06, 0.24, 12), mats.steel)
    spindle.position.set(0, 0.61, s.front - 0.22)
    g.add(spindle)
    const arm = new THREE.Mesh(box(0.08, 0.06, 0.5), mats.toolPanel)
    arm.position.set(0.18, 1.22, s.front - 0.2)
    g.add(arm)
    const armPost = new THREE.Mesh(cyl(0.04, 0.04, 0.5, 10), mats.steel)
    armPost.position.set(0.18, 1.0, s.front - 0.04)
    g.add(armPost)
    const head = new THREE.Mesh(cyl(0.16, 0.16, 0.12, 24), mats.toolPanel)
    head.position.set(0.18, 0.86, s.front - 0.3)
    g.add(head)
    const headShaft = new THREE.Mesh(cyl(0.03, 0.03, 0.3, 8), mats.steel)
    headShaft.position.set(0.18, 1.05, s.front - 0.3)
    g.add(headShaft)
  },

  // Inspection: from the aisle, a cluster-tool face like any other - the
  // granite is inside, under the optics, not standing in the bay - told
  // apart by its REVIEW STATION. Two displays side by side on a console
  // standing off the face, because reviewing a defect map is done with the
  // map on one screen and the image on the other, and an operator sits at
  // it for a shift. (One screen and a tray was the metrology face exactly,
  // and the two tools were indistinguishable on the floor.)
  inspection: (g, mats, s) => {
    const x = s.skin[0] + 0.9
    const zf = s.front - 0.34
    // The console: a desk on a pedestal, standing proud of the face.
    const desk = new THREE.Mesh(box(1.5, 0.05, 0.5), mats.toolPanel)
    desk.position.set(x, 0.95, zf - 0.06)
    desk.castShadow = true
    g.add(desk)
    const pedestal = new THREE.Mesh(box(1.1, 0.88, 0.34), mats.conduitDark)
    pedestal.position.set(x, 0.46, zf - 0.02)
    g.add(pedestal)
    for (const [i, dx] of [-0.37, 0.37].entries()) {
      const bezel = new THREE.Mesh(box(0.66, 0.44, 0.05), mats.toolPanel)
      bezel.position.set(x + dx, 1.4, s.front - 0.06)
      bezel.rotation.y = -dx * 0.18
      g.add(bezel)
      const screen = new THREE.Mesh(box(0.58, 0.36, 0.01), mats.screen)
      screen.position.copy(bezel.position)
      screen.rotation.copy(bezel.rotation)
      screen.translateZ(-0.032)
      g.add(screen)
      face.leds(g, i === 0 ? LED_GREEN : LED_BLUE, 0.1, x + dx, 1.16, s.front)
    }
    const keyboard = new THREE.Mesh(box(0.46, 0.02, 0.18), mats.conduitDark)
    keyboard.position.set(x, 0.99, zf - 0.14)
    g.add(keyboard)
    face.placard(g, 'laser', 0.22, x + 0.86, 1.55, s.front)
  },

  // Probe: the test head docked on the prober's roof with its manipulator
  // stand beside, and the tester rack past that - the only face on the
  // floor with a machine standing on top of it.
  //
  // The head sits ON the prober, over the chuck, overhanging the front the
  // way a docked head does, and the manipulator is a stout stand at the
  // tool's shoulder holding it there. Drawn as a thin post to the floor
  // with a two-metre arm reaching across the whole face, which is what
  // this was, it read as a door frame standing in the bay.
  probe: (g, mats, s) => {
    // OVER THE SKIN, NOT OVER THE FRONT END. The prober's body is 1.6 m so
    // that the head on top of it stays under the carriers, and at that
    // height the head is level with the front end - put on the front end's
    // end of the roof it went straight behind the glazing and the machine
    // that makes a prober a prober was invisible from the aisle. It sits
    // over the mainframe skin instead, silhouetted against the bulkhead,
    // with its manipulator at that shoulder and the tester rack beyond the
    // front end at the other.
    const hx = -s.w / 2 + 0.85
    const head = new THREE.Mesh(box(0.95, 0.44, 0.75), mats.conduitDark)
    head.position.set(hx, s.h + 0.24, s.front + 0.1)
    head.rotation.z = 0.06
    head.castShadow = true
    g.add(head)
    // The docking ring under it, onto the prober's head plate.
    const dock = new THREE.Mesh(cyl(0.3, 0.3, 0.06, 20), mats.steel)
    dock.position.set(hx, s.h + 0.01, s.front + 0.12)
    g.add(dock)
    // The manipulator: a boxed stand at the tool's shoulder, and the short
    // yoke from it to the head.
    const cx = -s.w / 2 - 0.42
    const column = new THREE.Mesh(box(0.34, s.h + 0.2, 0.4), mats.steel)
    column.position.set(cx, (s.h + 0.2) / 2, s.bulk - 0.35)
    column.castShadow = true
    g.add(column)
    const yoke = new THREE.Mesh(box(hx - cx, 0.14, 0.18), mats.steel)
    yoke.position.set((cx + hx) / 2, s.h + 0.24, s.front + 0.25)
    g.add(yoke)
    // The cable bundle from the head down the stand - the thing that makes
    // a docked head read as connected to something.
    const loom = new THREE.Mesh(cyl(0.06, 0.06, s.h - 0.5, 8), mats.conduitDark)
    loom.position.set(cx + 0.1, (s.h - 0.5) / 2 + 0.2, s.front + 0.2)
    g.add(loom)
    const x = s.w / 2 + 1.35
    const zf = face.cabinet(g, mats, 0.8, 2.0, 0.6, x, s, mats.toolBody)
    face.leds(g, LED_GREEN, 0.5, x, 1.7, zf)
    face.leds(g, LED_BLUE, 0.5, x, 1.55, zf)
    face.placard(g, 'esd', 0.22, x, 1.1, zf)
  },

  // Final test: the face IS the handler - its magazine loaders are the
  // ports - with the test head docked on its roof from the manipulator
  // beside, and the two tester racks on the other side with their
  // indicator strips. (An earlier version put "the handler" on the roof
  // as a box, which is a machine on top of itself.)
  test: (g, mats, s) => {
    const hx = -s.w / 2 + 0.85
    const head = new THREE.Mesh(box(0.9, 0.42, 0.7), mats.conduitDark)
    head.position.set(hx, s.h + 0.20, s.front + 0.12)
    head.castShadow = true
    g.add(head)
    const dock = new THREE.Mesh(cyl(0.28, 0.28, 0.06, 20), mats.steel)
    dock.position.set(hx, s.h + 0.01, s.front + 0.14)
    g.add(dock)
    const cx = -s.w / 2 - 0.42
    const column = new THREE.Mesh(box(0.34, s.h + 0.2, 0.4), mats.steel)
    column.position.set(cx, (s.h + 0.2) / 2, s.bulk - 0.35)
    column.castShadow = true
    g.add(column)
    const yoke = new THREE.Mesh(box(hx - cx, 0.14, 0.18), mats.steel)
    yoke.position.set((cx + hx) / 2, s.h + 0.20, s.front + 0.26)
    g.add(yoke)
    const loom = new THREE.Mesh(cyl(0.06, 0.06, s.h - 0.5, 8), mats.conduitDark)
    loom.position.set(cx + 0.1, (s.h - 0.5) / 2 + 0.2, s.front + 0.2)
    g.add(loom)
    for (const [i, x] of [s.w / 2 + 0.55, s.w / 2 + 1.45].entries()) {
      const zf = face.cabinet(g, mats, 0.8, 2.0, 0.6, x, s, mats.toolBody)
      face.leds(g, i === 0 ? LED_GREEN : LED_BLUE, 0.5, x, 1.7, zf)
      face.leds(g, LED_GREEN, 0.5, x, 1.55, zf)
      face.leds(g, i === 0 ? LED_BLUE : LED_AMBER, 0.5, x, 1.4, zf)
    }
    face.placard(g, 'esd', 0.22, s.w / 2 - 1.0, 0.7, s.front)
  },

  // Dicing saw: small, with the spindle and blade behind glass.
  dicing: (g, mats, s) => {
    face.hood(g, mats, 0.9, 0.9, 0.5, 0, 0.94, s.front)
    const spindle = new THREE.Mesh(cyl(0.07, 0.07, 0.5, 14), mats.steel)
    spindle.rotation.z = Math.PI / 2
    spindle.position.set(0, 1.09, s.front - 0.25)
    g.add(spindle)
    const blade = new THREE.Mesh(cyl(0.2, 0.2, 0.015, 28), mats.toolPanel)
    blade.rotation.z = Math.PI / 2
    blade.position.set(0.12, 1.09, s.front - 0.25)
    g.add(blade)
    const chuck = new THREE.Mesh(cyl(0.3, 0.3, 0.03, 24), mats.steel)
    chuck.position.set(0, 0.57, s.front - 0.25)
    g.add(chuck)
  },

  // Wire bonder: the bond head under its microscope behind the safety
  // glass, the lead-frame track running under it between the two
  // magazines, and the wire spool on the face beside.
  //
  // Between the loaders, not off to one side: the work area of a bonder is
  // what the magazines feed, and the scope has to be over it. The old
  // version put a 0.6 m tapered cylinder in the hood - it read as a traffic
  // cone in a glass case - and hung the wire spool half off the face.
  bonding: (g, mats, s) => {
    face.hood(g, mats, 0.9, 0.86, 0.5, 0, 1.05, s.front)
    // The scope column, with the binocular head on top and the objective
    // nose under it. Short and slim: a bond scope is a hand's span.
    const scope = new THREE.Mesh(cyl(0.055, 0.055, 0.3, 12), mats.steel)
    scope.position.set(0, 1.24, s.front - 0.28)
    g.add(scope)
    const nose = new THREE.Mesh(cyl(0.032, 0.055, 0.09, 12), mats.conduitDark)
    nose.position.set(0, 1.05, s.front - 0.28)
    g.add(nose)
    const eyepiece = new THREE.Mesh(box(0.22, 0.1, 0.16), mats.toolPanel)
    eyepiece.position.set(0, 1.43, s.front - 0.32)
    eyepiece.rotation.x = 0.25
    g.add(eyepiece)
    // The lead-frame track through the work area, and the heater block
    // under it that the frame is clamped onto.
    const track = new THREE.Mesh(box(0.86, 0.02, 0.09), mats.steel)
    track.position.set(0, 0.86, s.front - 0.26)
    g.add(track)
    const block = new THREE.Mesh(box(0.2, 0.06, 0.16), mats.conduitDark)
    block.position.set(0, 0.82, s.front - 0.26)
    g.add(block)
    // The wire spool on its spindle, on the face where an operator changes
    // it - flat to the aisle, the way it hangs on the machine.
    const spool = new THREE.Mesh(cyl(0.075, 0.075, 0.05, 18), mats.toolPanel)
    spool.rotation.x = Math.PI / 2
    spool.position.set(s.w / 2 - 0.42, 1.4, s.front - 0.05)
    g.add(spool)
    const hub = new THREE.Mesh(cyl(0.02, 0.02, 0.09, 10), mats.steel)
    hub.rotation.x = Math.PI / 2
    hub.position.set(s.w / 2 - 0.42, 1.4, s.front - 0.06)
    g.add(hub)
    face.placard(g, 'esd', 0.2, -s.w / 2 + 0.4, 1.4, s.front)
  },

  // Mould press: the platen and its posts behind the safety-glass door.
  packaging: (g, mats, s) => {
    face.hood(g, mats, 1.2, 1.3, 0.5, 0, 1.12, s.front)
    const platen = new THREE.Mesh(box(0.9, 0.16, 0.36), mats.steel)
    platen.position.set(0, 1.57, s.front - 0.25)
    g.add(platen)
    const base = new THREE.Mesh(box(0.9, 0.14, 0.36), mats.steel)
    base.position.set(0, 0.57, s.front - 0.25)
    g.add(base)
    for (const x of [-0.35, 0.35]) {
      const post = new THREE.Mesh(cyl(0.05, 0.05, 1.0, 10), mats.steel)
      post.position.set(x, 1.07, s.front - 0.25)
      g.add(post)
    }
    face.placard(g, 'hot', 0.22, 0.9, 1.9, s.front)
  },

  // Bulk gas and chemical supply, at the head of the hall: the valve
  // manifold cabinets on the side the aisle arrives at (the face's local
  // +x, which is world -x), with the cylinder cabinets behind on the
  // hall's end side. The first version put every cabinet on the end side
  // and left the aisle a blank 5 m panel to walk up to.
  gas: (g, mats, s) => {
    const placardFor = (i: number): PlacardKind => (i % 3 === 0 ? 'flammable' : i % 3 === 1 ? 'toxic' : 'corrosive')
    // Cylinder cabinets along the end side.
    //
    // THE CYLINDER IS INSIDE THE CABINET. It was drawn centred on the
    // cabinet's own door, so half a metre of gas bottle stood out through
    // a closed door into the room - and a cylinder of anything on this row
    // loose in a cleanroom is the single thing a gas cabinet exists to
    // prevent. What a real one shows is a sight window with the bottle,
    // its regulator and the pigtail behind it, and the door handle beside.
    for (let i = 0; i < 6; i++) {
      const x = -s.w / 2 + 0.4 + i * 0.78
      const cab = new THREE.Mesh(box(0.62, 2.5, 0.7), mats.toolPanel)
      cab.position.set(x, 1.25, s.d / 2 + 0.55)
      cab.castShadow = true
      g.add(cab)
      const zf = s.d / 2 + 0.9
      const bottle = new THREE.Mesh(cyl(0.15, 0.15, 1.35, 14), mats.steel)
      bottle.position.set(x, 0.9, zf - 0.34)
      g.add(bottle)
      const neck = new THREE.Mesh(cyl(0.05, 0.05, 0.16, 10), mats.conduitDark)
      neck.position.set(x, 1.66, zf - 0.34)
      g.add(neck)
      // The sight window onto it, with the regulator behind the glass.
      const reveal = new THREE.Mesh(box(0.3, 0.62, 0.02), CAVITY_DARK)
      reveal.position.set(x, 1.35, zf - 0.05)
      g.add(reveal)
      const frame = new THREE.Mesh(box(0.38, 0.7, 0.03), mats.toolPanel)
      frame.position.set(x, 1.35, zf - 0.02)
      g.add(frame)
      const pane = new THREE.Mesh(box(0.3, 0.62, 0.01), mats.glass)
      pane.position.set(x, 1.35, zf + 0.006)
      g.add(pane)
      const handle = new THREE.Mesh(box(0.03, 0.2, 0.04), mats.steel)
      handle.position.set(x + 0.24, 0.95, zf + 0.02)
      g.add(handle)
      const sign = new THREE.Mesh(new THREE.PlaneGeometry(0.22, 0.22), placardMaterial(placardFor(i)))
      sign.position.set(x, 2.1, zf + 0.012)
      g.add(sign)
    }
    // Valve manifold boxes facing the aisle: a row of three cabinets
    // with sight windows onto their regulators, placarded, and the gas
    // detection panel above them with its channel lamps.
    const xf = s.w / 2 + 0.05
    for (let i = 0; i < 3; i++) {
      const z = -0.75 + i * 0.75
      const cab = new THREE.Mesh(box(0.55, 2.2, 0.66), mats.toolPanel)
      cab.position.set(xf + 0.275, 1.1, z)
      cab.castShadow = true
      g.add(cab)
      const xw = xf + 0.55
      const frame = new THREE.Mesh(box(0.05, 0.5, 0.4), mats.toolPanel)
      frame.position.set(xw + 0.02, 1.5, z)
      g.add(frame)
      const reveal = new THREE.Mesh(box(0.02, 0.4, 0.3), mats.screen)
      reveal.position.set(xw + 0.045, 1.5, z)
      g.add(reveal)
      for (const dz of [-0.08, 0.08]) {
        const gauge = new THREE.Mesh(cyl(0.05, 0.05, 0.03, 12), mats.steel)
        gauge.rotation.z = Math.PI / 2
        gauge.position.set(xw + 0.06, 1.5, z + dz)
        g.add(gauge)
      }
      const pane = new THREE.Mesh(box(0.01, 0.4, 0.3), mats.glass)
      pane.position.set(xw + 0.085, 1.5, z)
      g.add(pane)
      const sign = new THREE.Mesh(new THREE.PlaneGeometry(0.22, 0.22), placardMaterial(placardFor(i)))
      sign.rotation.y = Math.PI / 2
      sign.position.set(xw + 0.006, 1.0, z)
      g.add(sign)
      const handle = new THREE.Mesh(box(0.04, 0.18, 0.03), mats.steel)
      handle.position.set(xw + 0.02, 1.05, z + 0.2)
      g.add(handle)
    }
    // The gas monitoring panel: AT EYE HEIGHT, beside the manifolds, which
    // is where a technician reads it and where the acknowledge button has
    // to be reachable. At 2.5 m it was 0.3 m clear of the cabinet tops -
    // a box hanging in the air over the row.
    const panel = new THREE.Mesh(box(0.08, 0.5, 0.42), mats.conduitDark)
    panel.position.set(xf + 0.04, 1.55, 1.5)
    g.add(panel)
    const panelBack = new THREE.Mesh(box(0.06, 0.62, 0.54), mats.toolPanel)
    panelBack.position.set(xf + 0.01, 1.55, 1.5)
    g.add(panelBack)
    for (let i = 0; i < 5; i++) {
      const lamp = new THREE.Mesh(tint(box(0.01, 0.025, 0.28), i === 2 ? LED_AMBER : LED_GREEN), UNLIT)
      lamp.position.set(xf + 0.085, 1.71 - i * 0.07, 1.5)
      g.add(lamp)
    }
    const ack = new THREE.Mesh(cyl(0.035, 0.035, 0.03, 14), EMO_RED)
    ack.rotation.z = Math.PI / 2
    ack.position.set(xf + 0.09, 1.36, 1.5)
    g.add(ack)
  },

  // EUV scanner, and the track it is fed through.
  //
  // THE FACE IS THE TRACK. An EUV scanner takes no carriers: lots come to
  // the coat/develop track docked in line with it, and the track's front end
  // is where the hoists land them, so that is what stands in the wall with
  // the load ports. The scanner is the enclosure beside it - the largest
  // thing on the floor, taller than anything else in the bay - and its SOURCE
  // is at the back of it, out of sight of the aisle: a sealed vacuum vessel
  // running in hydrogen, with nothing to look into (13.5 nm light is
  // invisible, and the vessel has no window), fed through the floor by the
  // beam tube from the CO2 drive laser in the sub-fab.
  //
  // HEIGHT BY DEPTH. The transport sweeps from 2.09 m up, from 0.6 m in front
  // of the face line to 0.155 m behind it, so the enclosure is 2 m tall where
  // it meets the aisle and rises to full height only 0.2 m behind the face.
  euv: (g, mats, s, accent) => {
    const livery = new THREE.MeshStandardMaterial({ color: accent, emissive: accent, emissiveIntensity: 0.35, roughness: 0.5 })
    const LOW = 2.0
    const TALL = 3.2
    const set = s.front + 0.2
    const sw = 4.2
    const xIn = -s.w / 2 - 0.35
    const xOut = xIn - sw
    const cx = (xIn + xOut) / 2
    const zf = s.front - 0.1

    // --- the enclosure, in the bay ---
    const lower = new THREE.Mesh(box(sw, LOW - 0.1, s.bulk - zf), mats.toolBody)
    lower.position.set(cx, LOW / 2 + 0.05, (zf + s.bulk) / 2)
    lower.castShadow = true
    g.add(lower)
    const kick = new THREE.Mesh(box(sw - 0.06, 0.1, s.bulk - zf - 0.03), mats.conduitDark)
    kick.position.set(cx, 0.05, (zf + s.bulk) / 2 + 0.015)
    g.add(kick)
    const upper = new THREE.Mesh(box(sw, TALL - LOW, s.bulk - set), mats.toolBody)
    upper.position.set(cx, (LOW + TALL) / 2, (set + s.bulk) / 2)
    upper.castShadow = true
    g.add(upper)
    // The step between them, capped in panel grey so the setback reads.
    const ledge = new THREE.Mesh(box(sw, 0.02, set - zf), mats.toolPanel)
    ledge.position.set(cx, LOW + 0.01, (zf + set) / 2)
    g.add(ledge)
    const lowBand = new THREE.Mesh(box(sw + 0.02, 0.1, s.bulk - zf + 0.02), livery)
    lowBand.position.set(cx, LOW - 0.3, (zf + s.bulk) / 2)
    g.add(lowBand)
    const highBand = new THREE.Mesh(box(sw + 0.02, 0.1, s.bulk - set + 0.02), livery)
    highBand.position.set(cx, TALL - 0.25, (set + s.bulk) / 2)
    g.add(highBand)
    // Panel joints: the enclosure is a wall of removable skins, not a block.
    for (let i = 1; i < 4; i++) {
      const jx = xOut + (i * sw) / 4
      const joint = new THREE.Mesh(box(0.012, TALL - LOW - 0.12, 0.01), mats.conduitDark)
      joint.position.set(jx, (LOW + TALL) / 2 - 0.05, set - 0.005)
      g.add(joint)
    }
    // Two service doors at the outboard end, the status panel and the
    // hazard placards mid-way, and the reticle port at the track's end.
    face.door(g, mats, 0.85, 1.4, xOut + 0.55, 0.85, zf)
    face.door(g, mats, 0.85, 1.4, xOut + 1.5, 0.85, zf)
    const bezel = new THREE.Mesh(box(0.42, 0.28, 0.02), mats.toolPanel)
    bezel.position.set(cx + 0.9, 1.35, zf - 0.01)
    g.add(bezel)
    const status = new THREE.Mesh(box(0.34, 0.2, 0.01), mats.screen)
    status.position.set(cx + 0.9, 1.35, zf - 0.022)
    g.add(status)
    face.leds(g, LED_BLUE, 0.4, cx + 0.9, 1.12, zf)
    face.placard(g, 'laser', 0.26, cx + 0.3, 1.35, zf)
    face.placard(g, 'hv', 0.22, cx + 0.3, 0.98, zf)
    const rx = xIn - 0.5
    const shelf = new THREE.Mesh(box(0.36, 0.04, 0.3), mats.toolPanel)
    shelf.position.set(rx, 0.98, zf - 0.15)
    g.add(shelf)
    const portDoor = new THREE.Mesh(box(0.3, 0.26, 0.012), mats.conduitDark)
    portDoor.position.set(rx, 1.14, zf - 0.006)
    g.add(portDoor)
    const pod = new THREE.Mesh(box(0.2, 0.06, 0.2), mats.conduitDark)
    pod.position.set(rx, 1.03, zf - 0.16)
    g.add(pod)
    const rsp = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.06), portLabelMaterial('RSP'))
    rsp.rotation.y = Math.PI
    rsp.position.set(rx, 1.3, zf - 0.006)
    g.add(rsp)
    // The in-line interface the wafers cross from track to scanner.
    const bridge = new THREE.Mesh(box(0.35, 1.3, s.bulk - s.front + 0.05), mats.toolPanel)
    bridge.position.set((xIn - s.w / 2) / 2, 0.95, (s.front - 0.05 + s.bulk) / 2)
    g.add(bridge)
    // The track handles litres of casting solvent and developer.
    face.placard(g, 'flammable', 0.22, s.skin[0] + 0.3, 0.72, s.front)

    // --- the scanner's back half, behind the wall line ---
    //
    // Kept outboard of the strip beside the mainframe, where every tool
    // has its gas box, disconnect and gas tubing (buildTool, chase.ts), and
    // under the chase's CDA header in a bay-and-chase hall. In a ballroom
    // nothing hangs over it and it keeps its full height.
    const xBack = -s.w / 2 - 1.0
    const backD = 1.4
    const backH = BALLROOM ? TALL : 2.45
    const back = new THREE.Mesh(box(xBack - xOut, backH, backD), mats.toolBody)
    back.position.set((xBack + xOut) / 2, backH / 2, s.bulk + backD / 2)
    back.castShadow = true
    g.add(back)

    // --- the source, at the back ---
    //
    // No deeper than the track's own back (`s.d` behind the face): in a
    // ballroom the next row's backs face this one across a service aisle,
    // and a vessel standing proud of the line narrowed it to a squeeze.
    const sx = xOut + 1.25
    const dz = (d: number): number => s.bulk + d
    const R = 0.72
    const yc = 1.55
    const D0 = 1.75
    const D1 = 2.5
    const dMid = (D0 + D1) / 2
    // Painted like the rest of the enclosure, but WITHOUT the panel map: a
    // cylinder's end caps take the skin texture's seams and latches as a
    // printed square on a disc.
    const paint = SOURCE_PAINT
    const drum = new THREE.Mesh(cyl(R, R, D1 - D0, 32), paint)
    drum.rotation.x = Math.PI / 2
    drum.position.set(sx, yc, dz(dMid))
    drum.castShadow = true
    g.add(drum)
    // The end flanges as RINGS, open-ended. As solid discs a hair wider and
    // deeper than the drum they capped its ends in polished steel, which
    // this hall lights almost black: the vessel read as a dark target.
    for (const d of [D0 + 0.02, D1 - 0.02]) {
      const rim = new THREE.Mesh(new THREE.CylinderGeometry(R + 0.03, R + 0.03, 0.05, 32, 1, true), mats.steel)
      rim.rotation.x = Math.PI / 2
      rim.position.set(sx, yc, dz(d))
      g.add(rim)
    }
    const band = new THREE.Mesh(new THREE.CylinderGeometry(R + 0.01, R + 0.01, 0.1, 32, 1, true), livery)
    band.rotation.x = Math.PI / 2
    band.position.set(sx, yc, dz(dMid))
    g.add(band)
    // The collector's cone narrowing to the intermediate focus, where the
    // light passes into the scanner.
    const cone = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.55, D0 - backD, 24), mats.steel)
    cone.rotation.x = -Math.PI / 2
    cone.position.set(sx, yc, dz((backD + D0) / 2))
    g.add(cone)
    // Legs, and the beam tube up through the floor from the drive laser.
    for (const lx of [-0.5, 0.5]) {
      for (const ld of [D0 + 0.1, D1 - 0.1]) {
        const leg = new THREE.Mesh(box(0.08, 0.95, 0.08), mats.conduitDark)
        leg.position.set(sx + lx, 0.475, dz(ld))
        g.add(leg)
      }
    }
    const tube = new THREE.Mesh(cyl(0.1, 0.1, 0.86, 16), mats.steel)
    tube.position.set(sx, 0.43, dz(dMid))
    g.add(tube)
    for (const [y, r, h] of [[0.015, 0.22, 0.03], [0.3, 0.14, 0.04], [0.84, 0.14, 0.04]] as const) {
      const collar = new THREE.Mesh(cyl(r, r, h, 20), mats.conduitDark)
      collar.position.set(sx, y, dz(dMid))
      g.add(collar)
    }
    // A blank flanged service port on the end cap, bolted shut.
    const flange = new THREE.Mesh(cyl(0.3, 0.3, 0.04, 28), mats.toolPanel)
    flange.rotation.x = Math.PI / 2
    flange.position.set(sx, yc, dz(D1 + 0.02))
    g.add(flange)
    const blank = new THREE.Mesh(cyl(0.25, 0.25, 0.03, 28), paint)
    blank.rotation.x = Math.PI / 2
    blank.position.set(sx, yc, dz(D1 + 0.055))
    g.add(blank)
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2
      const bolt = new THREE.Mesh(cyl(0.018, 0.018, 0.03, 8), mats.conduitDark)
      bolt.rotation.x = Math.PI / 2
      bolt.position.set(sx + Math.cos(a) * 0.275, yc + Math.sin(a) * 0.275, dz(D1 + 0.06))
      g.add(bolt)
    }
    // Turbo pumps either side, on short necks, with their forelines down.
    for (const side of [-1, 1]) {
      const neck = new THREE.Mesh(cyl(0.08, 0.08, 0.16, 16), mats.steel)
      neck.rotation.z = Math.PI / 2
      neck.position.set(sx + side * (R + 0.06), 1.2, dz(dMid))
      g.add(neck)
      const pump = new THREE.Mesh(cyl(0.14, 0.14, 0.3, 20), mats.toolPanel)
      pump.rotation.z = Math.PI / 2
      pump.position.set(sx + side * (R + 0.29), 1.2, dz(dMid))
      g.add(pump)
      const pumpFlange = new THREE.Mesh(cyl(0.16, 0.16, 0.03, 20), mats.steel)
      pumpFlange.rotation.z = Math.PI / 2
      pumpFlange.position.set(sx + side * (R + 0.15), 1.2, dz(dMid))
      g.add(pumpFlange)
      const foreline = new THREE.Mesh(cyl(0.035, 0.035, 1.05, 10), mats.steel)
      foreline.position.set(sx + side * (R + 0.4), 0.6, dz(dMid))
      g.add(foreline)
    }
    // The droplet generator on the vessel's shoulder, the tin catcher under
    // it, and the hydrogen supply up out of the floor.
    const droplet = new THREE.Mesh(cyl(0.06, 0.06, 0.34, 14), mats.steel)
    droplet.rotation.z = 0.55
    droplet.position.set(sx - 0.28, yc + R - 0.02, dz(dMid - 0.05))
    g.add(droplet)
    const catcher = new THREE.Mesh(box(0.22, 0.2, 0.22), mats.toolPanel)
    catcher.position.set(sx + 0.3, yc - R + 0.02, dz(dMid - 0.05))
    g.add(catcher)
    const h2 = new THREE.Mesh(cyl(0.018, 0.018, 1.2, 8), mats.steel)
    h2.position.set(sx - 0.42, 0.6, dz(D1 - 0.08))
    g.add(h2)
    // Its placards on the end cap, facing the chase walkway.
    for (const [kind, px] of [['flammable', -0.38], ['laser', 0.38]] as const) {
      const sign = new THREE.Mesh(new THREE.PlaneGeometry(0.22, 0.22), placardMaterial(kind))
      sign.position.set(sx + px, yc + 0.36, dz(D1 + 0.006))
      g.add(sign)
    }
  },

  // CD-SEM: a closed cabinet on vibration isolation beside its front end -
  // the column is inside it, and all a real one shows of it is the cover on
  // the roof - with the high-voltage rack on one side and, on the other, the
  // desk the measurement is actually run from: two monitors, one for the
  // image and one for the recipe.
  ebeam: (g, mats, s) => {
    // The chamber cabinet's front on the skin: its door, its vacuum readout,
    // and the load lock's panel beside the front end.
    const cx = s.skin[0] + 0.65
    face.door(g, mats, 0.95, 1.0, cx, 0.95, s.front)
    const readoutBezel = new THREE.Mesh(box(0.26, 0.12, 0.02), mats.toolPanel)
    readoutBezel.position.set(cx - 0.2, 1.62, s.front - 0.01)
    g.add(readoutBezel)
    const readout = new THREE.Mesh(box(0.2, 0.07, 0.01), mats.screen)
    readout.position.set(cx - 0.2, 1.62, s.front - 0.022)
    g.add(readout)
    const vac = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.06), portLabelMaterial('VAC'))
    vac.rotation.y = Math.PI
    vac.position.set(cx + 0.14, 1.62, s.front - 0.006)
    g.add(vac)
    // On the chamber door itself, clear of the EMO beside it.
    face.placard(g, 'hv', 0.2, cx + 0.2, 1.22, s.front - 0.03)
    const llx = s.skin[1] - 0.95
    const lock = new THREE.Mesh(box(0.34, 0.3, 0.06), mats.toolPanel)
    lock.position.set(llx, 0.6, s.front - 0.03)
    g.add(lock)
    face.leds(g, LED_GREEN, 0.06, llx - 0.06, 0.66, s.front - 0.06)
    face.leds(g, LED_AMBER, 0.06, llx + 0.06, 0.66, s.front - 0.06)
    const ll = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.06), portLabelMaterial('LL'))
    ll.rotation.y = Math.PI
    ll.position.set(llx, 0.54, s.front - 0.062)
    g.add(ll)
    // Vibration isolation: the cabinet floats on air mounts over its frame,
    // and the dark gap between them runs the width of the chamber.
    const gap = new THREE.Mesh(box(s.skin[1] - s.skin[0] - 0.1, 0.035, 0.02), CAVITY_DARK)
    gap.position.set((s.skin[0] + s.skin[1]) / 2, 0.36, s.front - 0.008)
    g.add(gap)
    // The mounts stand on the base frame, tucked under the cabinet's edge
    // with only their faces showing - not as posts out on the floor, which
    // is how they read a hand in front of it.
    const frameRail = new THREE.Mesh(box(s.skin[1] - s.skin[0] - 0.1, 0.05, 0.05), mats.steel)
    frameRail.position.set((s.skin[0] + s.skin[1]) / 2, 0.125, s.front - 0.02)
    g.add(frameRail)
    for (const mx of [s.skin[0] + 0.3, s.skin[1] - 0.3]) {
      const mount = new THREE.Mesh(cyl(0.065, 0.075, 0.18, 18), mats.conduitDark)
      mount.position.set(mx, 0.24, s.front + 0.02)
      g.add(mount)
      const cap = new THREE.Mesh(cyl(0.05, 0.05, 0.02, 18), mats.steel)
      cap.position.set(mx, 0.34, s.front + 0.02)
      g.add(cap)
    }
    // The column cover on the roof, behind the transport's line, and the
    // gun's high-voltage cable back from it.
    const cz = s.front + 0.37
    const cover = new THREE.Mesh(box(0.8, 0.36, 0.34), mats.toolBody)
    cover.position.set(cx, s.h + 0.18, cz)
    cover.castShadow = true
    g.add(cover)
    const gunCap = new THREE.Mesh(cyl(0.15, 0.15, 0.22, 20), mats.toolPanel)
    gunCap.position.set(cx, s.h + 0.47, cz)
    g.add(gunCap)
    const hvCable = new THREE.Mesh(
      hose([[cx + 0.1, s.h + 0.52, cz], [cx + 0.3, s.h + 0.55, cz + 0.12], [cx + 0.5, s.h + 0.12, s.bulk + 0.2]], 0.025),
      mats.conduitDark,
    )
    g.add(hvCable)

    // The high-voltage rack.
    const rx = -s.w / 2 - 0.55
    const zf = face.cabinet(g, mats, 0.8, 2.0, 0.6, rx, s, mats.toolBody)
    face.leds(g, LED_BLUE, 0.5, rx, 1.7, zf)
    face.leds(g, LED_GREEN, 0.5, rx, 1.55, zf)
    face.leds(g, LED_AMBER, 0.5, rx, 1.4, zf)
    face.placard(g, 'hv', 0.22, rx, 1.05, zf)
    const label = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.06), portLabelMaterial('HV'))
    label.rotation.y = Math.PI
    label.position.set(rx, 1.86, zf - 0.006)
    g.add(label)

    // The operator's console: a sit-down desk against the wall.
    const dx = s.w / 2 + 0.95
    const dw = 1.4
    const dd = 0.66
    const dzc = s.bulk - dd / 2
    const top = new THREE.Mesh(box(dw, 0.04, dd), mats.toolPanel)
    top.position.set(dx, 0.74, dzc)
    top.castShadow = true
    g.add(top)
    for (const ex of [-1, 1]) {
      const end = new THREE.Mesh(box(0.04, 0.72, dd - 0.04), mats.conduitDark)
      end.position.set(dx + ex * (dw / 2 - 0.03), 0.36, dzc)
      g.add(end)
    }
    const modesty = new THREE.Mesh(box(dw - 0.08, 0.4, 0.03), mats.conduitDark)
    modesty.position.set(dx, 0.5, s.bulk - 0.08)
    g.add(modesty)
    for (const [i, ox] of [-0.33, 0.33].entries()) {
      const stand = new THREE.Mesh(box(0.06, 0.2, 0.06), mats.conduitDark)
      stand.position.set(dx + ox, 0.86, s.bulk - 0.2)
      g.add(stand)
      const foot = new THREE.Mesh(box(0.22, 0.015, 0.16), mats.conduitDark)
      foot.position.set(dx + ox, 0.765, s.bulk - 0.2)
      g.add(foot)
      const monitor = new THREE.Mesh(box(0.6, 0.37, 0.04), mats.toolPanel)
      monitor.position.set(dx + ox, 1.12, s.bulk - 0.24)
      monitor.rotation.y = ox * 0.35
      g.add(monitor)
      // What a CD-SEM operator watches: the image with its measurement
      // cursors on one screen, the recipe and the results on the other.
      const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.54, 0.31), consoleScreen(i === 0 ? 'image' : 'recipe'))
      screen.position.copy(monitor.position)
      screen.rotation.copy(monitor.rotation)
      screen.rotateY(Math.PI)
      screen.translateZ(0.022)
      g.add(screen)
      face.leds(g, i === 0 ? LED_GREEN : LED_BLUE, 0.04, dx + ox + 0.24, 0.95, s.bulk - 0.26)
    }
    const keyboard = new THREE.Mesh(box(0.44, 0.02, 0.15), mats.conduitDark)
    keyboard.position.set(dx - 0.08, 0.77, s.bulk - dd + 0.2)
    g.add(keyboard)
    const trackball = new THREE.Mesh(new THREE.SphereGeometry(0.035, 12, 8), mats.conduitDark)
    trackball.position.set(dx + 0.3, 0.79, s.bulk - dd + 0.2)
    g.add(trackball)
  },

  // Quantum device test: a dilution refrigerator as one looks when it is
  // cold and running - a closed cylindrical can hanging from the top plate of
  // its frame, the gold stages inside it out of sight - beside the cabinet of
  // control electronics that is the station's face. Chips come to it by
  // hand: diced, bonded into a gold-plated sample holder at the bench in
  // front of the face, and loaded into the fridge, a cool-down a load.
  // Round the fridge, what every one of them needs: the gas-handling system
  // that circulates the helium mixture, the pulse-tube compressor that does
  // the first 297 kelvin, the flexible lines from both to the fridge's top,
  // and the rack of microwave electronics that drives and reads the qubits.
  cryostat: (g, mats, s) => {
    // --- the control cabinet's face, and the loading bench before it ---
    const bx = s.skin[0] + 0.7
    for (const [i, y] of [1.62, 1.44, 1.26].entries()) {
      const unit = new THREE.Mesh(box(1.0, 0.14, 0.03), mats.conduitDark)
      unit.position.set(bx, y, s.front - 0.015)
      g.add(unit)
      const display = new THREE.Mesh(box(0.18, 0.07, 0.01), mats.screen)
      display.position.set(bx - 0.3, y, s.front - 0.035)
      g.add(display)
      face.leds(g, i === 1 ? LED_AMBER : LED_GREEN, 0.3, bx + 0.22, y, s.front - 0.03)
    }
    face.claim(bx - 0.5, bx + 0.5, 1.19, 1.69)
    face.placard(g, 'esd', 0.2, s.skin[1] - 1.15, 0.7, s.front)
    const benchW = 1.2
    const benchD = 0.5
    const bz = s.front - benchD / 2
    const benchTop = new THREE.Mesh(box(benchW, 0.04, benchD), mats.toolPanel)
    benchTop.position.set(bx, 0.9, bz)
    benchTop.castShadow = true
    g.add(benchTop)
    const mat = new THREE.Mesh(box(benchW - 0.1, 0.005, benchD - 0.08), mats.conduitDark)
    mat.position.set(bx, 0.923, bz)
    g.add(mat)
    for (const lx of [-1, 1]) {
      for (const lz of [-1, 1]) {
        const leg = new THREE.Mesh(box(0.04, 0.88, 0.04), mats.steel)
        leg.position.set(bx + lx * (benchW / 2 - 0.05), 0.44, bz + lz * (benchD / 2 - 0.05))
        g.add(leg)
      }
    }
    // Sample holders waiting in their rack, and one on the mat with a chip
    // being bonded into it.
    const rack = new THREE.Mesh(box(0.34, 0.05, 0.12), mats.steel)
    rack.position.set(bx + 0.3, 0.95, bz + 0.1)
    g.add(rack)
    for (let k = 0; k < 3; k++) {
      const puck = new THREE.Mesh(cyl(0.045, 0.045, 0.06, 20), GOLD_PLATE)
      puck.position.set(bx + 0.19 + k * 0.11, 1.0, bz + 0.1)
      g.add(puck)
    }
    const holder = new THREE.Mesh(box(0.12, 0.02, 0.12), GOLD_PLATE)
    holder.position.set(bx + 0.02, 0.935, bz - 0.08)
    g.add(holder)
    const chip = new THREE.Mesh(box(0.04, 0.006, 0.04), mats.conduitDark)
    chip.position.set(bx + 0.02, 0.948, bz - 0.08)
    g.add(chip)
    // The stereo microscope the bonding is done under.
    const scopeBase = new THREE.Mesh(box(0.2, 0.03, 0.22), mats.toolPanel)
    scopeBase.position.set(bx - 0.36, 0.935, bz)
    g.add(scopeBase)
    const post = new THREE.Mesh(cyl(0.018, 0.018, 0.4, 10), mats.steel)
    post.position.set(bx - 0.36, 1.13, bz + 0.08)
    g.add(post)
    const head = new THREE.Mesh(box(0.12, 0.1, 0.16), mats.toolPanel)
    head.position.set(bx - 0.36, 1.22, bz - 0.01)
    g.add(head)
    for (const ex of [-0.03, 0.03]) {
      const eyepiece = new THREE.Mesh(cyl(0.015, 0.015, 0.1, 10), mats.conduitDark)
      eyepiece.rotation.x = 0.7
      eyepiece.position.set(bx - 0.36 + ex, 1.29, bz - 0.08)
      g.add(eyepiece)
    }

    // --- the fridge, in its frame ---
    const FW = 1.2
    const FD = 1.0
    const FH = 2.0
    const fx = -s.w / 2 - 0.15 - FW / 2
    const fz = s.bulk - FD / 2
    for (const px of [-1, 1]) {
      for (const pz of [-1, 1]) {
        const upright = new THREE.Mesh(box(0.06, FH, 0.06), mats.conduitDark)
        upright.position.set(fx + px * (FW / 2 - 0.03), FH / 2, fz + pz * (FD / 2 - 0.03))
        upright.castShadow = true
        g.add(upright)
      }
    }
    for (const y of [0.1, FH - 0.03]) {
      for (const pz of [-1, 1]) {
        const rail = new THREE.Mesh(box(FW, 0.06, 0.06), mats.conduitDark)
        rail.position.set(fx, y, fz + pz * (FD / 2 - 0.03))
        g.add(rail)
      }
      for (const px of [-1, 1]) {
        const rail = new THREE.Mesh(box(0.06, 0.06, FD), mats.conduitDark)
        rail.position.set(fx + px * (FW / 2 - 0.03), y, fz)
        g.add(rail)
      }
    }
    // The top plate on two cross-beams, and the vacuum can hanging from it.
    for (const pz of [-0.34, 0.34]) {
      const beam = new THREE.Mesh(box(FW, 0.05, 0.06), mats.conduitDark)
      beam.position.set(fx, 1.9, fz + pz)
      g.add(beam)
    }
    const topPlate = new THREE.Mesh(cyl(0.38, 0.38, 0.04, 36), mats.steel)
    topPlate.position.set(fx, 1.855, fz)
    g.add(topPlate)
    const canFlange = new THREE.Mesh(cyl(0.35, 0.35, 0.04, 36), CAN_ALU)
    canFlange.position.set(fx, 1.815, fz)
    g.add(canFlange)
    const can = new THREE.Mesh(cyl(0.31, 0.31, 1.33, 36), CAN_ALU)
    can.position.set(fx, 1.795 - 0.665, fz)
    can.castShadow = true
    g.add(can)
    const joint = new THREE.Mesh(cyl(0.325, 0.325, 0.03, 36), CAN_ALU)
    joint.position.set(fx, 1.2, fz)
    g.add(joint)
    const dish = new THREE.Mesh(new THREE.CylinderGeometry(0.31, 0.22, 0.08, 36), CAN_ALU)
    dish.position.set(fx, 0.425, fz)
    g.add(dish)
    // On the top plate: the microwave feedthroughs at the front, low, and
    // the pulse-tube head and the still's pumping port at the back, where
    // they can stand taller than the transport's line.
    for (let k = 0; k < 4; k++) {
      const sma = new THREE.Mesh(cyl(0.022, 0.022, 0.07, 10), GOLD_PLATE)
      sma.position.set(fx - 0.15 + k * 0.1, 1.91, fz - 0.2)
      g.add(sma)
    }
    const ptx = fx + 0.14
    const ptz = s.bulk - 0.2
    const ptHead = new THREE.Mesh(cyl(0.08, 0.08, 0.36, 18), mats.steel)
    ptHead.position.set(ptx, 2.05, ptz)
    g.add(ptHead)
    const motor = new THREE.Mesh(box(0.16, 0.14, 0.16), mats.toolPanel)
    motor.position.set(ptx, 2.3, ptz)
    g.add(motor)
    const stillX = fx - 0.16
    const stillPort = new THREE.Mesh(cyl(0.055, 0.055, 0.3, 16), mats.steel)
    stillPort.position.set(stillX, 2.02, ptz)
    g.add(stillPort)
    const stillFlange = new THREE.Mesh(cyl(0.075, 0.075, 0.03, 16), mats.steel)
    stillFlange.position.set(stillX, 2.17, ptz)
    g.add(stillFlange)

    // --- the gas-handling system ---
    const gx = fx - FW / 2 - 0.1 - 0.45
    const gz = face.cabinet(g, mats, 0.9, 1.9, 0.6, gx, s, mats.toolPanel)
    for (let i = 0; i < 3; i++) {
      const x = gx - 0.25 + i * 0.25
      const rim = new THREE.Mesh(cyl(0.075, 0.075, 0.03, 24), mats.steel)
      rim.rotation.x = Math.PI / 2
      rim.position.set(x, 1.5, gz - 0.015)
      g.add(rim)
      const dial = new THREE.Mesh(new THREE.CircleGeometry(0.062, 24), GAUGE_FACE)
      dial.rotation.y = Math.PI
      dial.position.set(x, 1.5, gz - 0.032)
      g.add(dial)
      const needle = new THREE.Mesh(box(0.005, 0.05, 0.004), GAUGE_NEEDLE)
      needle.position.set(x, 1.5, gz - 0.036)
      needle.rotation.z = -0.9 + i * 0.75
      needle.translateY(0.02)
      g.add(needle)
    }
    // The mimic panel: the circuit drawn on the door, its valves lit.
    const mimic = new THREE.Mesh(box(0.7, 0.34, 0.01), mats.screen)
    mimic.position.set(gx, 1.08, gz - 0.006)
    g.add(mimic)
    face.leds(g, LED_GREEN, 0.5, gx, 1.14, gz - 0.012)
    face.leds(g, LED_BLUE, 0.3, gx - 0.1, 1.0, gz - 0.012)
    face.placard(g, 'inert', 0.22, gx, 0.65, gz)
    const ghs = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.06), portLabelMaterial('GHS'))
    ghs.rotation.y = Math.PI
    ghs.position.set(gx, 1.74, gz - 0.006)
    g.add(ghs)

    // --- the pulse-tube compressor, water-cooled ---
    const cw = 0.5
    const ch = 0.78
    const cd = 0.62
    const kx = gx - 0.45 - 0.08 - cw / 2
    const comp = new THREE.Mesh(box(cw, ch, cd), mats.toolBody)
    comp.position.set(kx, ch / 2, s.bulk - cd / 2)
    comp.castShadow = true
    g.add(comp)
    const grille = new THREE.Mesh(box(cw - 0.08, 0.01, cd - 0.1), mats.conduitDark)
    grille.position.set(kx, ch + 0.005, s.bulk - cd / 2)
    g.add(grille)
    face.leds(g, LED_GREEN, 0.12, kx, 0.64, s.bulk - cd)
    const pt = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.06), portLabelMaterial('PT'))
    pt.rotation.y = Math.PI
    pt.position.set(kx, 0.52, s.bulk - cd - 0.006)
    g.add(pt)
    // Its cooling water, supply and return, down its side and into the wall.
    for (const [m, d] of [[HOSE_BLUE, -0.12], [HOSE_RED, -0.26]] as const) {
      const e = kx - cw / 2
      g.add(new THREE.Mesh(hose([[e, 0.3, s.bulk + d], [e - 0.1, 0.2, s.bulk + d], [e - 0.13, 0.06, s.bulk + d * 0.5], [e - 0.13, 0.05, s.bulk]], 0.016), m))
    }

    // --- the flexible lines, all behind the transport's line ---
    // Helium supply and return, compressor to the pulse-tube head.
    for (const d of [-0.06, -0.14]) {
      g.add(
        new THREE.Mesh(
          hose([[kx + 0.1, ch, s.bulk + d], [kx + 0.3, 2.2, s.bulk + d], [(kx + ptx) / 2, 2.42, ptz + d + 0.2], [ptx - 0.12, 2.36, ptz], [ptx - 0.08, 2.3, ptz]], 0.018),
          mats.steel,
        ),
      )
    }
    // The still's pumping line and the condensing line, GHS to fridge top.
    g.add(new THREE.Mesh(hose([[gx + 0.2, 1.9, s.bulk - 0.25], [gx + 0.3, 2.15, s.bulk - 0.25], [stillX - 0.3, 2.3, ptz], [stillX, 2.19, ptz]], 0.04), mats.steel))
    g.add(new THREE.Mesh(hose([[gx - 0.1, 1.9, s.bulk - 0.2], [gx + 0.1, 2.25, s.bulk - 0.15], [stillX + 0.05, 2.12, ptz + 0.1], [fx, 1.9, ptz + 0.08]], 0.012), mats.steel))

    // --- the microwave rack, and its coax over the cabinet to the fridge ---
    const mx = s.w / 2 + 0.55
    const mz = face.cabinet(g, mats, 0.8, 2.0, 0.6, mx, s, mats.toolBody)
    for (let i = 0; i < 6; i++) face.leds(g, i % 3 === 2 ? LED_AMBER : LED_BLUE, 0.5, mx, 1.75 - i * 0.12, mz)
    face.placard(g, 'esd', 0.22, mx, 0.8, mz)
    g.add(
      new THREE.Mesh(
        hose([[mx, 2.0, s.bulk - 0.12], [mx - 0.3, 2.14, s.bulk - 0.12], [0, 2.14, s.bulk - 0.12], [fx + 0.5, 2.14, s.bulk - 0.12], [fx + 0.1, 2.0, s.bulk - 0.22], [fx + 0.02, 1.9, s.bulk - 0.26]], 0.03),
        mats.conduitDark,
      ),
    )
  },

  // Gowning: the station past the airlock where the protocol is checked -
  // the cross-over bench, the rail of gowns waiting on it, the bootie and
  // glove racks, the mirror you check your hood in, and the tag on a
  // sign hung over it all. An open island, not a cabinet: the first
  // version stood a 5 m white slab across the aisle, which is what a
  // student entering from the airlock saw instead of the hall. The
  // lockers are at the airlock (buildGowning), where they belong.
  gowning: (g, mats, s) => {
    // Cross-over bench down the island's middle.
    const bench = new THREE.Mesh(box(s.w * 0.9, 0.06, 0.5), mats.toolPanel)
    bench.position.set(0, 0.45, 0)
    bench.castShadow = true
    g.add(bench)
    for (const dx of [-s.w * 0.4, 0, s.w * 0.4]) {
      const leg = new THREE.Mesh(box(0.06, 0.42, 0.44), mats.steel)
      leg.position.set(dx, 0.21, 0)
      g.add(leg)
    }
    // The gown rail over the bench's far side, gowns on it.
    const railZ = 0.5
    for (const dx of [-s.w * 0.42, s.w * 0.42]) {
      const post = new THREE.Mesh(cyl(0.025, 0.025, 2.0, 8), mats.steel)
      post.position.set(dx, 1.0, railZ)
      g.add(post)
    }
    const rail = new THREE.Mesh(cyl(0.02, 0.02, s.w * 0.84, 8), mats.steel)
    rail.rotation.z = Math.PI / 2
    rail.position.set(0, 1.95, railZ)
    g.add(rail)
    // A gown on a hanger is FLAT: cloth hanging off a wire, a hand's
    // breadth thick, with sleeves down the sides. Turned as cylinders of
    // 0.4 m diameter they were six white barrels on a rail - the shape a
    // bollard has, not the shape a coverall has - and they were the first
    // thing a student saw on entering the hall.
    for (let i = 0; i < 6; i++) {
      const x = -s.w * 0.3 + (i * (s.w * 0.6)) / 5
      const part = (w: number, h: number, d: number, dx: number, y: number): void => {
        const m = new THREE.Mesh(box(w, h, d), mats.toolBody)
        m.position.set(x + dx, y, railZ)
        g.add(m)
      }
      part(0.2, 0.16, 0.12, 0, 1.87) // hood
      part(0.42, 0.1, 0.1, 0, 1.76) // shoulders
      part(0.4, 0.72, 0.11, 0, 1.35) // body
      part(0.46, 0.28, 0.12, 0, 0.87) // hem, wider than the body
      part(0.09, 0.62, 0.09, -0.235, 1.42) // sleeves
      part(0.09, 0.62, 0.09, 0.235, 1.42)
      const hanger = new THREE.Mesh(cyl(0.008, 0.008, 0.12, 6), mats.steel)
      hanger.position.set(x, 1.95, railZ)
      g.add(hanger)
    }
    // Bootie rack at one end, glove and mask dispensers at the other.
    for (let r = 0; r < 3; r++) {
      const shelf = new THREE.Mesh(box(0.6, 0.03, 0.4), mats.steel)
      shelf.position.set(s.w / 2 + 0.05, 0.3 + r * 0.35, 0)
      g.add(shelf)
    }
    for (const dx of [-0.2, 0.2]) {
      const upright = new THREE.Mesh(cyl(0.015, 0.015, 1.05, 6), mats.steel)
      upright.position.set(s.w / 2 + 0.05 + dx, 0.55, 0.18)
      g.add(upright)
    }
    const dispPost = new THREE.Mesh(cyl(0.025, 0.025, 1.7, 8), mats.steel)
    dispPost.position.set(-s.w / 2 - 0.05, 0.85, 0)
    g.add(dispPost)
    for (const [dy, w, h] of [[1.5, 0.16, 0.24], [1.2, 0.28, 0.16], [0.95, 0.16, 0.24]] as const) {
      const disp = new THREE.Mesh(box(w, h, 0.1), mats.toolPanel)
      disp.position.set(-s.w / 2 - 0.05, dy, 0.08)
      g.add(disp)
    }
    // The full-length mirror, on its stand beside the dispensers.
    const mirror = new THREE.Mesh(box(0.5, 1.5, 0.03), mats.steel)
    mirror.position.set(-s.w / 2 - 0.05, 1.05, -0.45)
    mirror.rotation.y = Math.PI / 2
    g.add(mirror)
    // The tag's sign, hung from the ceiling over the bench's front edge,
    // where the shared face puts the placard.
    const signY = s.h - 0.14
    const board = new THREE.Mesh(box(1.7, 0.5, 0.06), mats.toolPanel)
    board.position.set(0, signY, s.front + 0.02)
    g.add(board)
    for (const dx of [-0.6, 0.6]) {
      const rod = new THREE.Mesh(cyl(0.012, 0.012, FAB.ceilingHeight - signY - 0.25, 6), mats.conduit)
      rod.position.set(dx, (FAB.ceilingHeight + signY + 0.25) / 2, s.front + 0.02)
      g.add(rod)
    }
  },
}


/**
 * What fills the open zone over the walkway.
 *
 * In a production-bay photograph the centre of the ceiling is the busiest
 * thing in the frame, and it is busy with a specific and repeating set of
 * objects: the structural steel the transport hangs from - PAINTED SAFETY
 * YELLOW, which is the one strong colour anywhere near a cleanroom ceiling
 * - the cable trays and conduit running with it, and the strip fittings
 * tucked between. None of it is tidy and all of it is parallel.
 *
 * This is that, and it is deliberately the cheap half of the effect: the
 * deck plane above is what makes it read, because a dark ground is what
 * gives every one of these a silhouette. Against the old white lid they
 * would have disappeared.
 *
 * Instanced by family; the whole zone is a handful of draws.
 */
function buildServiceDeck(half: number): THREE.Group {
  const g = new THREE.Group()
  const H = FAB.ceilingHeight
  const L = FAB.length

  const place = (geometry: THREE.BufferGeometry, material: THREE.Material, m: THREE.Matrix4[]): void => {
    if (m.length === 0) return
    const mesh = new THREE.InstancedMesh(geometry, material, m.length)
    m.forEach((mat, i) => mesh.setMatrixAt(i, mat))
    mesh.instanceMatrix.needsUpdate = true
    mesh.frustumCulled = false
    g.add(mesh)
  }
  const at = (x: number, y: number, z: number) => new THREE.Matrix4().makeTranslation(x, y, z)

  const YELLOW = new THREE.MeshStandardMaterial({ color: 0xd8a520, roughness: 0.6, metalness: 0.25 })
  const TRAY = new THREE.MeshStandardMaterial({ color: 0x4b525a, roughness: 0.7, metalness: 0.45 })
  const CONDUIT = new THREE.MeshStandardMaterial({ color: 0x9aa1a9, roughness: 0.5, metalness: 0.5 })

  // The two longitudinal rails the whole zone is organised around, running
  // the length of the building just inside the deck's edges.
  for (const dz of [-half + 0.34, half - 0.34]) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(L - 2, 0.14, 0.09), YELLOW)
    rail.position.set(0, H + 0.06, dz)
    g.add(rail)
    const web = new THREE.Mesh(new THREE.BoxGeometry(L - 2, 0.03, 0.22), YELLOW)
    web.position.set(0, H + 0.14, dz)
    g.add(web)
  }

  // Conduit and a cable tray down the middle, at two heights so the zone has
  // depth rather than being one layer of sticks.
  const trayStringer: THREE.Matrix4[] = []
  const rungs: THREE.Matrix4[] = []
  const conduits: THREE.Matrix4[] = []
  for (const dz of [-0.2, 0.2]) trayStringer.push(at(0, H + 0.1, dz))
  for (let x = -L / 2 + 1; x < L / 2 - 1; x += 0.32) rungs.push(at(x, H + 0.075, 0))
  for (const [dz, dy] of [[-0.62, 0.03], [-0.5, 0.13], [0.5, 0.13], [0.62, 0.03]] as const) {
    conduits.push(at(0, H + dy, dz))
  }
  const stringer = new THREE.BoxGeometry(L - 2, 0.07, 0.025)
  place(stringer, TRAY, trayStringer)
  place(new THREE.BoxGeometry(0.03, 0.015, 0.36), TRAY, rungs)
  const pipe = new THREE.CylinderGeometry(0.035, 0.035, L - 2, 8)
  pipe.rotateZ(Math.PI / 2)
  place(pipe, CONDUIT, conduits)

  // Cross-braces between the rails, and the drop rods that carry the lot up
  // into the structure. The beat of these down the hall is what gives the
  // overhead its perspective.
  const braces: THREE.Matrix4[] = []
  const rods: THREE.Matrix4[] = []
  for (let x = -L / 2 + 2; x < L / 2 - 2; x += 2.4) {
    braces.push(at(x, H + 0.17, 0))
    for (const dz of [-half + 0.34, half - 0.34]) rods.push(at(x, H + 0.3, dz))
  }
  place(new THREE.BoxGeometry(0.08, 0.05, half * 2 - 0.6), YELLOW, braces)
  place(new THREE.CylinderGeometry(0.016, 0.016, 0.24, 6), CONDUIT, rods)

  return g
}
/** Build the cleanroom shell: floor, FFU ceiling, perimeter walls. */
export function buildRoom(mats: FabMaterials): THREE.Group {
  const g = new THREE.Group()
  const width = FAB.bayDepth * 2 + FAB.aisleHalfWidth * 2
  const L = FAB.length
  const H = FAB.ceilingHeight

  // The floor, with the sub-fab stairwell cut out of it. A shape rather than
  // a plane since the sub-fab arrived: the hole is real geometry, so the room
  // below is visible through it and nowhere else.
  const stair = SUBFAB.stair
  const outline = new THREE.Shape()
  outline.moveTo(-L / 2, -width / 2)
  outline.lineTo(L / 2, -width / 2)
  outline.lineTo(L / 2, width / 2)
  outline.lineTo(-L / 2, width / 2)
  const well = new THREE.Path()
  // Shape y maps to world -z under the floor's rotation.
  well.moveTo(stair.minX, -stair.maxZ)
  well.lineTo(stair.maxX, -stair.maxZ)
  well.lineTo(stair.maxX, -stair.minZ)
  well.lineTo(stair.minX, -stair.minZ)
  outline.holes.push(well)
  const floorGeo = new THREE.ShapeGeometry(outline)
  // ShapeGeometry UVs are in shape units; remap to the 0..1 the old plane
  // had, so the raised-floor texture tiles exactly as it always did.
  {
    const pos = floorGeo.attributes.position
    const uv = floorGeo.attributes.uv
    for (let i = 0; i < pos.count; i++) {
      uv.setXY(i, pos.getX(i) / L + 0.5, pos.getY(i) / width + 0.5)
    }
  }
  const floor = new THREE.Mesh(floorGeo, mats.floor)
  floor.rotation.x = -Math.PI / 2
  floor.receiveShadow = true
  // Drawn first, like the bulkhead (bays.ts): the floor covers the whole
  // sub-fab whenever that is drawn, and sorted by its mid-hall centre it
  // was painted after it - every skid down there shaded, then hidden.
  floor.renderOrder = -10
  g.add(floor)

  // Hazard striping around the stairwell mouth, the way the aisle edges are
  // striped - the floor opening is the one place on this level you can fall.
  const wellStripes: Array<[number, number, number, number]> = [
    [(stair.minX + stair.maxX) / 2, stair.minZ - 0.2, stair.maxX - stair.minX + 0.8, 0.25],
    [(stair.minX + stair.maxX) / 2, stair.maxZ + 0.2, stair.maxX - stair.minX + 0.8, 0.25],
    [stair.minX - 0.2, (stair.minZ + stair.maxZ) / 2, 0.25, stair.maxZ - stair.minZ],
    [stair.maxX + 0.35, (stair.minZ + stair.maxZ) / 2, 0.25, stair.maxZ - stair.minZ],
  ]
  for (const [x, z, w, d] of wellStripes) {
    const stripe = new THREE.Mesh(new THREE.PlaneGeometry(w, d), mats.stripe)
    stripe.rotation.x = -Math.PI / 2
    stripe.position.set(x, 0.004, z)
    g.add(stripe)
  }

  // THE CEILING IS NOT ONE FLAT LID.
  //
  // Filter face over the BAYS, where the air has to come down clean onto the
  // wafers, and an OPEN SERVICE DECK over the walkway, where it does not.
  // That is how a bay-and-chase fab is actually built, and it is the single
  // biggest difference between a photograph of a production bay and a
  // rendering of one: in the photograph the middle of the ceiling is a
  // thicket - track, trays, conduit, painted steel, strip lights - with lit
  // panels either side of it. A continuous white plane from wall to wall
  // reads as an office.
  //
  // The deck sits 0.22 m above the filter line so the eye can tell it is a
  // different plane, and it is dark, which is what gives the overhead any
  // depth at all: everything hung under it now has something to be seen
  // against.
  const AISLE_DECK = 1.62
  for (const [z0, z1] of [
    [-width / 2, -AISLE_DECK],
    [AISLE_DECK, width / 2],
  ]) {
    const bay = new THREE.Mesh(new THREE.PlaneGeometry(L, z1 - z0), mats.ffu)
    bay.rotation.x = Math.PI / 2
    bay.position.set(0, H, (z0 + z1) / 2)
    g.add(bay)
  }
  const deck = new THREE.Mesh(
    new THREE.PlaneGeometry(L, AISLE_DECK * 2),
    new THREE.MeshStandardMaterial({ color: 0x5a626b, roughness: 0.95 }),
  )
  deck.rotation.x = Math.PI / 2
  deck.position.set(0, H + 0.22, 0)
  g.add(deck)
  g.add(buildServiceDeck(AISLE_DECK))

  // The luminaires: six rows of teardrop fixtures running the full hall - a
  // pair over the aisle, two over each bay - which is how a filter ceiling
  // is lit: the lights hang in the grid lines BETWEEN filter modules, in
  // continuous rows, because every module face is filter and the light has
  // nowhere else to go. Each row is a chain of 1.2 m fittings with a hand's
  // width between them, one InstancedMesh for the lot; the brightness costs
  // nothing because the material is unlit. The fixtures themselves cast no
  // light - the culled downlights in `buildLighting` do that job, and the
  // environment capture in FabScene puts these rows into the floor's sheen.
  // The rows sit exactly where the lamps do (CEILING_MODULE_Z), so the pool
  // under a downlight is under a fixture.
  //
  // Four rows between the two bulkheads - the only ceiling anyone sees
  // since the chase was walled off: the aisle pair where the aisle
  // downlights are, and one over each bay floor. Six rows fitted a 11.2 m
  // hall; in a 6.5 m one they came out on top of each other and the two
  // outermost ran into the hoist track's hangers.
  const aisleRow = CEILING_MODULE_Z / 2
  const rows = [-BAY_ROW_Z, -aisleRow, aisleRow, BAY_ROW_Z]
  // A ballroom's filter ceiling runs wall to wall and is lit wall to wall:
  // two more rows over each service side, where the chase's strip fittings
  // hang in the other layout (chase.ts puts them at 1.7 and 5.4 m out).
  // Then the second row's bays and its outer aisle, laid the way the first
  // row's are: a row over each bay floor, a pair over the aisle.
  if (BALLROOM) {
    const outer = outerAisleZ()
    for (const z of [BULKHEAD_Z + 1.7, 2 * SECOND_ROW_PIVOT_Z - BAY_ROW_Z, outer - aisleRow, outer + aisleRow]) rows.push(-z, z)
  }
  const cols = Math.floor(L / CEILING_GRID_X)
  const fixtures = new THREE.InstancedMesh(
    new THREE.BoxGeometry(CEILING_GRID_X - 0.08, 0.06, 0.16),
    mats.lightPanel,
    rows.length * cols,
  )
  // NO PER-BAY COLOUR HERE ANY MORE.
  //
  // The litho bay used to carry amber instance colours on this mesh. It is
  // gone, and deliberately: the lighting menu offers AMBER as one of three
  // presets - "the safelight, no UV to expose resist" - so a student who
  // has picked WHITE has asked for a white fab and is entitled to get one.
  // A single bay quietly overriding that choice was the simulation
  // disagreeing with its own settings menu, and it read as a bug because it
  // was one. The safelight lesson lives in the preset, where the whole hall
  // turns and nobody can miss the point.
  const white = new THREE.Color(1, 1, 1)
  const mtx = new THREE.Matrix4()
  let i = 0
  for (const z of rows) {
    for (let c = 0; c < cols; c++) {
      const x = -L / 2 + (c + 0.5) * CEILING_GRID_X
      mtx.makeTranslation(x, H - 0.03, z)
      fixtures.setMatrixAt(i, mtx)
      // ALL WHITE. The litho bay used to carry an amber instance colour
      // here, and it is gone: the lighting menu offers Amber as a preset of
      // its own - "the safelight, no UV to expose resist" - so a student who
      // has chosen WHITE has asked for a white fab and should get one. A bay
      // that overrode that choice was the sim disagreeing with its own
      // settings menu. The safelight lesson lives in the preset, where the
      // whole hall turns and the point is unmissable.
      fixtures.setColorAt(i, white)
      i++
    }
  }
  g.add(fixtures)

  g.add(buildBaySigns(mats))
  // The ballroom's outer aisles: the same bays, read from the other side,
  // with the copies' tags (`buildSecondRow`).
  if (BALLROOM) {
    for (const side of [-1, 1]) {
      g.add(buildBaySigns(mats, side * outerAisleZ(), (t) => Math.sign(t.position[2]) === side, (t) => secondTag(t.assetTag)))
    }
  }

  const wallGeo = [
    { w: L, d: 0.2, x: 0, z: -width / 2 },
    { w: L, d: 0.2, x: 0, z: width / 2 },
    { w: 0.2, d: width, x: -L / 2, z: 0 },
    { w: 0.2, d: width, x: L / 2, z: 0 },
  ]
  for (const w of wallGeo) {
    const m = new THREE.Mesh(box(w.w, H, w.d), mats.wall)
    m.position.set(w.x, H / 2, w.z)
    m.receiveShadow = true
    g.add(m)
  }

  // Aisle edge marking - the walkway boundary a technician keeps clear.
  //
  // A PLAIN LINE, not hazard chevrons. Yellow-and-black diagonals mean "you
  // can be hurt here" and a fab spends them carefully: the stairwell mouth,
  // a swing door's arc, a pinch point. Run for 130 m down both sides of a
  // walkway they say nothing, and they say it in the loudest colour on the
  // floor - the aisle read as a forklift lane in a warehouse rather than as
  // the cleanest room in the building. Two 100 mm lines, a walkway blue
  // outside a thin white edge, which is how the boundary is actually laid.
  const walkLine = new THREE.MeshStandardMaterial({ color: 0x4b7bb5, roughness: 0.6 })
  const walkEdge = new THREE.MeshStandardMaterial({ color: 0xf2f4f6, roughness: 0.6 })
  // Each aisle's two edges: (centreline, half width). The main aisle in
  // both halls; the ballroom's two outer aisles as well.
  const aisles: Array<[number, number]> = [[0, FAB.aisleHalfWidth]]
  if (BALLROOM) aisles.push([-outerAisleZ(), OUTER_AISLE_HALF], [outerAisleZ(), OUTER_AISLE_HALF])
  for (const [centre, half] of aisles) {
    for (const side of [-1, 1]) {
      const line = new THREE.Mesh(new THREE.PlaneGeometry(L - 2, 0.1), walkLine)
      line.rotation.x = -Math.PI / 2
      line.position.set(0, 0.002, centre + side * half)
      g.add(line)
      const edge = new THREE.Mesh(new THREE.PlaneGeometry(L - 2, 0.04), walkEdge)
      edge.rotation.x = -Math.PI / 2
      edge.position.set(0, 0.002, centre + side * (half - 0.08))
      g.add(edge)
    }
  }

  g.add(buildUtilities(mats))
  g.add(buildPartitions(mats))
  g.add(buildAisleFurniture(mats))
  g.add(buildFloorMarkings())
  g.add(buildFloorWear())

  return g
}

/**
 * Which tools get a utility drop.
 *
 * The ones that consume something from the building - process gas, vacuum,
 * cooling water, exhaust. A metrology bench, a prober and a gas cabinet do
 * not, which is why the drops in a real bay are a scatter rather than a comb,
 * and why they are worth drawing at all: a drop marks a tool that is plumbed
 * into the sub-fab, so the scatter itself is information.
 */
const DROP_KINDS = new Set<ToolKind>([
  // An EUV scanner is the most heavily plumbed tool on the floor - hydrogen,
  // vacuum, cooling water for the source and the drive laser - and an SEM
  // column and a pulse-tube compressor both take water and exhaust.
  'euv',
  'ebeam',
  'cryostat',
  'crystal',
  'waferprep',
  'epi',
  'wetbench',
  'implanter',
  'etcher',
  'deposition',
  'litho',
  'patterning',
  'furnace',
  'cmp',
  'plating',
])

/**
 * The overhead utility gantry, the drops that feed tools from it, and the
 * levelling pads every tool stands on.
 *
 * Modelled from production-bay reference photography rather than from a
 * P&ID, and the reference is emphatic: what a real cleanroom SHOWS of its
 * plumbing is one horizontal run per side at high level, a scatter of
 * vertical drops, a few pendant fixtures, and nothing else. The forest of
 * pipe lives under the floor - which is where this fab already put it. Every
 * branch drawn up here would be both less accurate and more expensive.
 *
 * Deliberately monochrome, too. The colour code (vacuum steel, PCW blue, gas
 * amber, UPW green) is a sub-fab convention and belongs down there; painting
 * the cleanroom in five colours to make the house model legible would trade a
 * true room for a diagram. Legibility is better earned on demand.
 *
 * INSTANCED THROUGHOUT: one draw call per family, however many tools the
 * aisle grows to. Not premature - the walking hitch this project spent two
 * sessions chasing was a draw-call and shader-count problem, and a few
 * hundred little cylinders is exactly how it would come back.
 */
function buildUtilities(mats: FabMaterials): THREE.Group {
  const g = new THREE.Group()
  const L = FAB.length
  const H = FAB.ceilingHeight
  /** In the chase, behind the bulkhead - where a real fab's run is. */
  const RUN_Z = 8.8
  const RUN_Y = 2.95

  const place = (
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    matrices: THREE.Matrix4[],
  ): void => {
    if (matrices.length === 0) return
    const mesh = new THREE.InstancedMesh(geometry, material, matrices.length)
    matrices.forEach((m, i) => mesh.setMatrixAt(i, m))
    mesh.instanceMatrix.needsUpdate = true
    // The gantry never moves, and its bounding sphere spans the whole hall -
    // culling it per instance costs more than drawing it.
    mesh.frustumCulled = false
    g.add(mesh)
  }

  // --- wall-line luminaires ------------------------------------------------
  //
  // The bright horizontal band down each side wall, at the height where wall
  // meets ceiling. In the reference photograph these are as much of the
  // room's character as the ceiling grid: they draw the two long edges of the
  // hall and give the perspective its rails. Unlit geometry, like the ceiling
  // fixtures - the light in this room comes from the culled downlights, and
  // adding real sources here is exactly the mistake that cost two sessions of
  // hitch-chasing.
  //
  // On the bulkhead line since the bulkhead arrived - the perimeter wall
  // they used to hang on is in the chase now.
  const stripGeo = new THREE.BoxGeometry(L - 2, 0.17, 0.09)
  const strips: THREE.Matrix4[] = []
  for (const side of [-1, 1]) {
    strips.push(new THREE.Matrix4().makeTranslation(0, H - 0.42, side * (BULKHEAD_Z - 0.15)))
  }
  place(stripGeo, mats.stripLight, strips)

  // --- the two runs, wall to wall ------------------------------------------
  const runGeo = new THREE.CylinderGeometry(0.105, 0.105, L - 4, 10)
  const runs: THREE.Matrix4[] = []
  const lieAlongX = new THREE.Matrix4().makeRotationZ(Math.PI / 2)
  for (const side of [-1, 1]) {
    runs.push(new THREE.Matrix4().makeTranslation(0, RUN_Y, side * RUN_Z).multiply(lieAlongX))
  }
  place(runGeo, mats.conduit, runs)

  // --- hangers holding the runs off the ceiling ----------------------------
  const hangerH = H - RUN_Y
  const hangerGeo = new THREE.CylinderGeometry(0.022, 0.022, hangerH, 6)
  const hangers: THREE.Matrix4[] = []
  for (let x = -L / 2 + 3; x <= L / 2 - 3; x += 6) {
    for (const side of [-1, 1]) {
      hangers.push(new THREE.Matrix4().makeTranslation(x, RUN_Y + hangerH / 2, side * RUN_Z))
    }
  }
  place(hangerGeo, mats.conduit, hangers)

  // --- drops to the tools that are plumbed into the building ---------------
  const dropGeo = new THREE.CylinderGeometry(0.05, 0.05, 1, 8)
  const drops: THREE.Matrix4[] = []
  const feet: THREE.Matrix4[] = []
  const footGeo = new THREE.CylinderGeometry(0.1, 0.12, 0.09, 10)
  for (const tool of TOOLS) {
    const [x, , z] = tool.position
    const size = toolSize(tool)

    if (DROP_KINDS.has(tool.kind)) {
      // Behind the tool, on the wall side: where a hookup actually lands, and
      // out of the sight line between the aisle and the tool's own HMI.
      const behind = z + Math.sign(z || 1) * (size.d / 2 + 0.3)
      const top = H - 0.05
      const bottom = size.h + 0.15
      const height = Math.max(top - bottom, 0.2)
      drops.push(
        new THREE.Matrix4()
          .makeTranslation(x - size.w * 0.28, bottom + height / 2, behind)
          .scale(new THREE.Vector3(1, height, 1)),
      )
    }

    // Levelling pads. Every tool in the reference photograph stands on four
    // bright yellow feet, and nothing else in the room is that colour - which
    // is why they read as strongly as they do at a glance.
    //
    // JUST OUTSIDE the cabinet footprint, not under it. A pad tucked under the
    // body is a pad nobody can see - which is what the first version of this
    // did - and a real levelling foot sits on a bracket that projects past the
    // panel line anyway, so the honest placement is also the visible one.
    if (tool.kind === 'gowning') continue
    // A bulkheaded face shows only its front pair; the back pair is in
    // the chase. The free-standing tools on the axis keep all four.
    const front = toolFaceZ(tool) + aisleDirection(tool) * 0.04
    const back = z === 0 ? -(size.d / 2 + 0.04) : null
    for (const dx of [-1, 1]) {
      feet.push(new THREE.Matrix4().makeTranslation(x + dx * (size.w / 2 + 0.04), 0.045, front))
      if (back !== null) feet.push(new THREE.Matrix4().makeTranslation(x + dx * (size.w / 2 + 0.04), 0.045, back))
    }
  }
  place(dropGeo, mats.conduitDark, drops)
  place(footGeo, mats.levellingPad, feet)

  // --- pendant fixtures over the walkway -----------------------------------
  //
  // Ionisers and sensors, hung on rods at intervals down the aisle. Small,
  // and the only thing in the hall that hangs BELOW the ceiling plane, which
  // is what gives the overhead its sense of depth.
  const rodGeo = new THREE.CylinderGeometry(0.014, 0.014, 0.75, 6)
  const podGeo = new THREE.CylinderGeometry(0.11, 0.13, 0.22, 10)
  const rods: THREE.Matrix4[] = []
  const pods: THREE.Matrix4[] = []
  let flip = 1
  for (let x = -L / 2 + 9; x <= L / 2 - 9; x += 17) {
    const z = flip * (FAB.aisleHalfWidth - 0.55)
    rods.push(new THREE.Matrix4().makeTranslation(x, H - 0.375, z))
    pods.push(new THREE.Matrix4().makeTranslation(x, H - 0.86, z))
    flip = -flip
  }
  place(rodGeo, mats.conduit, rods)
  place(podGeo, mats.conduitDark, pods)

  return g
}

/**
 * A FOUP: the sealed 25-wafer carrier that moves lots between tools.
 *
 * The parts a technician recognises it by: the front door plate that the
 * load port unlatches, the flange on top the overhead hoist grips, and the
 * two side handles. Shared between the load ports and the hoist vehicles,
 * so a carrier passing overhead is the same object as the one docked.
 */
export function buildFoup(mats: FabMaterials): THREE.Group {
  const g = new THREE.Group()
  const body = new THREE.Mesh(box(0.36, 0.32, 0.36), mats.foup)
  body.castShadow = true
  g.add(body)
  // The door, on the tool side (local -z, the same face the load port
  // docks against), darker and slightly proud of the body.
  const door = new THREE.Mesh(box(0.3, 0.26, 0.012), mats.conduitDark)
  door.position.set(0, -0.01, -0.185)
  g.add(door)
  // The robotic flange the OHT hoist grips, and its standoff.
  const flange = new THREE.Mesh(cyl(0.07, 0.07, 0.02, 14), mats.steel)
  flange.position.y = 0.19
  g.add(flange)
  const stem = new THREE.Mesh(cyl(0.03, 0.03, 0.02, 10), mats.steel)
  stem.position.y = 0.17
  g.add(stem)
  // Side handles.
  for (const dx of [-1, 1]) {
    const handle = new THREE.Mesh(box(0.02, 0.05, 0.16), mats.toolPanel)
    handle.position.set(dx * 0.19, 0.02, 0)
    g.add(handle)
  }
  return g
}

/**
 * Bay signs: the plaque hanging over the aisle at each pair of bays, the
 * bay number large and the asset tags of the two tools under it. In a real
 * fab this is how a technician who does not know the floor finds a tool
 * from thirty metres - by reading signs, not by recognising machines - and
 * a hall with no signage is a hall nobody could be sent across. Numbered
 * in aisle order; not localised, like the asset tags, because it is
 * stencilled on a physical plate.
 *
 * Hung down the aisle centreline at `z`. The main aisle lists both rows'
 * tools; a ballroom's outer aisle lists the copies that face it, on the
 * one side it has tools, under the same bay number - a bay is a bay
 * whichever aisle you read it from.
 */
function buildBaySigns(
  mats: FabMaterials,
  z = 0,
  facing: (t: Tool) => boolean = () => true,
  tagOf: (t: Tool) => string = (t) => t.assetTag,
): THREE.Group {
  const g = new THREE.Group()
  const H = FAB.ceilingHeight
  const byX = new Map<number, Tool[]>()
  for (const tool of TOOLS) {
    // The two on the aisle's own axis (gowning, gas) are not in a bay.
    if (tool.position[2] === 0 || !facing(tool)) continue
    const list = byX.get(tool.position[0]) ?? []
    list.push(tool)
    byX.set(tool.position[0], list)
  }
  const xs = [...byX.keys()].sort((a, b) => a - b)
  xs.forEach((x) => {
    const tools = byX.get(x)!.sort((a, b) => a.position[2] - b.position[2])
    // The bay's number comes from layout.ts, the same call the andon boards
    // and the technicians' briefs make, so a sign cannot disagree with what
    // the technician under it says.
    const number = bayNumber(tools[0])
    const tags = tools.map(tagOf).join('  ·  ')
    const tex = canvasTexture(512, 128, (ctx) => {
      ctx.fillStyle = '#1e2733'
      ctx.fillRect(0, 0, 512, 128)
      ctx.fillStyle = '#f5c542'
      ctx.fillRect(0, 0, 118, 128)
      ctx.fillStyle = '#1e2733'
      ctx.font = 'bold 82px system-ui, sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(number, 59, 68)
      ctx.fillStyle = '#eef2f6'
      ctx.font = '600 40px system-ui, sans-serif'
      ctx.fillText(tags, 315, 68, 380)
    })
    tex.colorSpace = THREE.SRGBColorSpace
    // Across the aisle, a face each way, so it reads - not mirrored - to a
    // student walking in either direction.
    const faceMat = new THREE.MeshBasicMaterial({ map: tex })
    for (const dir of [-1, 1]) {
      const face = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 0.4), faceMat)
      face.rotation.y = (dir * Math.PI) / 2
      face.position.set(x + dir * 0.006, H - 0.75, z)
      g.add(face)
    }
    const back = new THREE.Mesh(box(0.01, 0.4, 1.6), mats.conduitDark)
    back.position.set(x, H - 0.75, z)
    g.add(back)
    for (const dz of [-0.6, 0.6]) {
      const rod = new THREE.Mesh(cyl(0.012, 0.012, 0.55, 6), new THREE.MeshStandardMaterial({ color: 0x8a9099, metalness: 0.6, roughness: 0.4 }))
      rod.position.set(x, H - 0.28, z + dz)
      g.add(rod)
    }
  })
  return g
}

/**
 * The overhead hoist transport: the monorail loop that carries FOUPs from
 * tool to tool in any 300 mm fab, and the one thing in the picture that
 * says "modern fab" from across the room. A rail down each row of load
 * ports, joined by a half-loop at each end so the vehicles circulate,
 * hung from the ceiling on rods; the vehicles hang below it with a carrier
 * in the hoist. They move (see FabScene.animate) - a still rail is scenery,
 * a vehicle sliding past overhead while you stand at a load port is the
 * fab working around you.
 */
export interface OhtVehicle {
  /** Asset tag, the way a vehicle is stencilled: OHT-07. */
  id: string
  /** The carrier it is holding, and the lot inside. Fixed for the session. */
  carrier: string
  lot: string
  group: THREE.Group
  /** The carrier and its belts, which drop out of the body at a port. */
  hoist: THREE.Group
  belts: THREE.Mesh[]
  /** Position along the loop, metres. */
  s: number
  speed: number
  /** What the vehicle is doing, and for how long it has been doing it. */
  state: 'cruise' | 'lower' | 'dwell' | 'raise'
  /** Cruising but stopped behind the vehicle ahead. */
  holding: boolean
  timer: number
  /** How far the carrier has been let down, metres. */
  drop: number
  /** The `s` of the load port it is heading for. */
  nextStop: number
  /**
   * The port that stop is, or null on a loop that serves none. Carried on
   * the vehicle rather than looked up, because there are now two loops and
   * a stop's `s` means a different place on each of them.
   */
  dest: { tool: Tool; port: number } | null
}

/**
 * What a student reads off a vehicle or a robot they clicked: who it is,
 * what it carries, where it is going, what it is doing. Facts, not
 * strings - the card composes the words in the student's language.
 */
export interface VehicleStatus {
  kind: 'oht' | 'amr'
  id: string
  carrier: string
  lot: string
  /** The tool it is heading for, and which of its load ports. */
  destination: Tool | null
  port: number
  state: 'cruise' | 'hold' | 'transfer' | 'move' | 'deliver' | 'yield'
  /** Cruising speed, m/s. */
  speed: number
}

/** What a pick on a vehicle returns: enough to ask for its status later. */
export type VehicleRef = { kind: 'oht'; vehicle: OhtVehicle } | { kind: 'amr'; robot: Amr }

export interface Oht {
  group: THREE.Group
  vehicles: OhtVehicle[]
  /** Loop length, metres. */
  perimeter: number
  /** Move every vehicle on by `dt` seconds. */
  advance(dt: number): void
  status(v: OhtVehicle): VehicleStatus
}

/**
 * Rail geometry.
 *
 * The loop runs directly over the two rows of load ports, because that is
 * the only place it can run: the hoist lets a carrier straight down onto the
 * port beneath it, so the rail has to be above the port. With the faces 5.4
 * m apart that puts the two rails 5 m apart and 1.3 m over a walking head -
 * which is the whole of the reason a real bay reads as a fab the moment you
 * step into it. There was briefly a second loop down the middle of the aisle
 * to fix that, back when the aisle was 10 m wide and the rails were a detail
 * at the ceiling joint 4.7 m away on either side. Narrowing the hall fixed
 * it properly, and four parallel tracks over a 6.5 m room is a ceiling
 * nobody would build.
 *
 * It hangs low: 2.95 m to the rail, carriers passing at 2.1 m. That is where
 * it hangs in a real bay - comfortably over a head, and close enough to read
 * the lot card on the side of a vehicle going past.
 */
const OHT_R = BULKHEAD_Z - FACE_PROUD - 0.22
const OHT_Y = 2.95
let OHT_END = FAB.length / 2 - 9
/**
 * THE SWEPT ENVELOPE OF THE WHOLE TRANSPORT, beam and vehicle together -
 * what anything crossing its line has to open up for.
 *
 * This exists because the bay dividers were cut for the BEAM alone, and a
 * beam is the small half of the problem: the vehicle hangs 0.86 m under the
 * rail and is 0.75 m across the ribs, against the beam's 0.42. The dividers
 * opened a slot from 2.75 m to 3.16 m, the carriers cruise through at 2.09
 * to 2.97, and every vehicle on the floor drove through two thirds of a
 * metre of solid wall at each bay boundary.
 *
 * Derived from the geometry in `buildOhtVehicle`, and referenced rather
 * than re-guessed, so a change to the vehicle cannot silently put it back
 * inside the walls again.
 */
const OHT_SWEEP = {
  /** Lowest point of a cruising vehicle: the bottom of its skirts. */
  yLo: OHT_Y - 0.86,
  /** Highest: the top of the beam's lid, above the bogie mast. */
  yHi: OHT_Y + 0.136,
  /** Half-width across the hall, at the vehicle's corner ribs. */
  zHalf: 0.375,
} as const

/** Where a carrier hangs in a vehicle's frame, and how high a load port is. */
const CARRIER_Y = -0.66
const PORT_Y = 1.12

/**
 * A monorail beam and the hangers that carry it.
 *
 * The beam is a boxed aluminium section, drawn as a lid, two webs and the
 * shadowed slot between them rather than as a solid bar. The slot is the
 * whole point: a solid bar overhead reads as a pipe, and a fab ceiling is
 * already full of pipes. What says "track" is the dark opening the bogies
 * run inside, and the regular beat of the hangers going up to the deck.
 */
function buildOhtTrack(mats: FabMaterials, g: THREE.Group, r: number, y: number, end: number): void {
  const len = 2 * end
  const hangerH = FAB.ceilingHeight - y - 0.14
  const rods: THREE.Matrix4[] = []
  const brackets: THREE.Matrix4[] = []
  for (const side of [-1, 1]) {
    const lid = new THREE.Mesh(box(len, 0.055, 0.42), mats.conduit)
    lid.position.set(0, y + 0.108, side * r)
    g.add(lid)
    const slot = new THREE.Mesh(box(len, 0.17, 0.34), mats.conduitDark)
    slot.position.set(0, y + 0.005, side * r)
    g.add(slot)
    for (const dz of [-0.19, 0.19]) {
      const web = new THREE.Mesh(box(len, 0.2, 0.045), mats.conduit)
      web.position.set(0, y, side * r + dz)
      g.add(web)
    }
    // The sole: two plates closing the bottom of the section with a 120 mm
    // slit between them for the bogie's mast. This is the face the aisle
    // actually looks at, and left open the beam read as a dark bar - a duct.
    // A light underside with one dark line down it is what a track looks
    // like from below, and it is the line the eye follows to the far wall.
    for (const dz of [-0.145, 0.145]) {
      const sole = new THREE.Mesh(box(len, 0.03, 0.13), mats.conduit)
      sole.position.set(0, y - 0.093, side * r + dz)
      g.add(sole)
    }
    // The half-loop at each end. Rotated flat, then swung to point out of
    // the hall; the order matters, hence the explicit Euler order.
    const arc = new THREE.Mesh(new THREE.TorusGeometry(r, 0.15, 8, 44, Math.PI), mats.conduit)
    arc.rotation.order = 'YXZ'
    arc.rotation.x = Math.PI / 2
    arc.rotation.y = (side * Math.PI) / 2
    arc.position.set(side * end, y + 0.02, 0)
    g.add(arc)
    // Hangers every 3 m: a pair of drop rods and the beam bracket under
    // them, not the single wire the first version had. A 0.4 m beam on one
    // 20 mm wire is the one thing on this ceiling nobody would believe.
    for (let x = -end + 1.5; x < end; x += 3) {
      for (const dz of [-0.16, 0.16]) {
        rods.push(new THREE.Matrix4().makeTranslation(x, y + 0.14 + hangerH / 2, side * r + dz))
      }
      brackets.push(new THREE.Matrix4().makeTranslation(x, y + 0.15, side * r))
    }
  }
  const rod = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.022, 0.022, hangerH, 6), mats.conduit, rods.length)
  rods.forEach((m, i) => rod.setMatrixAt(i, m))
  rod.frustumCulled = false
  g.add(rod)
  const bracket = new THREE.InstancedMesh(new THREE.BoxGeometry(0.12, 0.04, 0.46), mats.conduitDark, brackets.length)
  brackets.forEach((m, i) => bracket.setMatrixAt(i, m))
  bracket.frustumCulled = false
  g.add(bracket)
}

/**
 * One vehicle: the shell that hangs under the beam, and the carrier in it.
 *
 * Shaped as a hood and two side skirts with THE ENDS LEFT OPEN, which is
 * both what the real ones look like and what makes them read: the carrier
 * inside is visible along the line of travel, so a vehicle sliding past
 * overhead is obviously carrying something. Closed into a box it was a
 * white brick, and a white brick against a white ceiling is nothing.
 */
function buildOhtVehicle(
  mats: FabMaterials,
  bodyMat: THREE.Material,
  lampMat: THREE.Material,
): { group: THREE.Group; hoist: THREE.Group; belts: THREE.Mesh[] } {
  const v = new THREE.Group()
  // The mast down from the bogie, which stays inside the beam's slot.
  const post = new THREE.Mesh(box(0.2, 0.32, 0.2), mats.conduitDark)
  post.position.y = -0.14
  v.add(post)
  const hood = new THREE.Mesh(box(1.12, 0.22, 0.72), bodyMat)
  hood.position.y = -0.41
  v.add(hood)
  for (const dz of [-0.335, 0.335]) {
    const skirt = new THREE.Mesh(box(1.12, 0.34, 0.05), bodyMat)
    skirt.position.set(0, -0.69, dz)
    v.add(skirt)
  }
  // A rib at each corner of each skirt, so the shell has a scale to read
  // against - a blank white panel 1.1 m long has no size from below.
  for (const dz of [-0.365, 0.365]) {
    for (const dx of [-0.4, 0.4]) {
      const rib = new THREE.Mesh(box(0.05, 0.34, 0.02), mats.conduitDark)
      rib.position.set(dx, -0.69, dz)
      v.add(rib)
    }
  }
  // The forward-facing indicator, so the direction of travel reads.
  const lamp = new THREE.Mesh(box(0.03, 0.07, 0.2), lampMat)
  lamp.position.set(0.57, -0.41, 0)
  v.add(lamp)
  mergeStatics(v, mats, [bodyMat, lampMat])

  // The hoist: a carrier gripped by its flange, on belts that pay out
  // when the vehicle lets it down to a port. Its own group, because it
  // moves against the body; its carrier merged to a few meshes.
  const hoist = new THREE.Group()
  const foup = buildFoup(mats)
  for (const part of [...foup.children]) {
    part.position.y += CARRIER_Y
    hoist.add(part)
  }
  mergeStatics(hoist, mats)
  v.add(hoist)
  const belts: THREE.Mesh[] = []
  for (const dx of [-0.12, 0.12]) {
    const belt = new THREE.Mesh(box(0.015, 1, 0.015), mats.conduitDark)
    belt.position.set(dx, -0.46, 0)
    belt.scale.y = 0.01
    v.add(belt)
    belts.push(belt)
  }
  return { group: v, hoist, belts }
}

/** One circulating loop of track: its vehicles, and its clock. */
interface OhtLoop {
  vehicles: OhtVehicle[]
  advance(dt: number): void
}

/**
 * Build a loop of track and populate it.
 *
 * `ports` is where along the loop a vehicle can dock. The interbay loop is
 * passed none, and its vehicles simply circulate - which is exactly what an
 * interbay vehicle does as far as anyone in this aisle can tell.
 */
function buildOhtLoop(
  mats: FabMaterials,
  g: THREE.Group,
  opt: { r: number; y: number; end: number; count: number; speed: number; index: number },
  ports: Array<{ s: number; tool: Tool; port: number }>,
): OhtLoop {
  buildOhtTrack(mats, g, opt.r, opt.y, opt.end)
  const straight = 2 * opt.end
  const arc = Math.PI * opt.r
  const perimeter = 2 * straight + 2 * arc

  const stops = ports.map((p) => p.s).sort((a, b) => a - b)
  const stopPort = new Map(ports.map((p) => [p.s, { tool: p.tool, port: p.port }]))
  const nextStopAfter = (s: number, skip: number): number => {
    if (stops.length === 0) return Number.POSITIVE_INFINITY
    let i = stops.findIndex((stop) => stop > s + 1)
    if (i < 0) i = 0
    return stops[(i + skip) % stops.length]
  }

  const vehicles: OhtVehicle[] = []
  const bodyMat = new THREE.MeshStandardMaterial({ color: 0xe4e8ec, roughness: 0.5, metalness: 0.2 })
  const lampMat = new THREE.MeshStandardMaterial({ color: 0x33d17a, emissive: 0x33d17a, emissiveIntensity: 1.6 })
  for (let i = 0; i < opt.count; i++) {
    const built = buildOhtVehicle(mats, bodyMat, lampMat)
    g.add(built.group)
    const s = (i / opt.count) * perimeter
    const nextStop = nextStopAfter(s, 2 + i)
    const vehicle: OhtVehicle = {
      id: `OHT-${String(opt.index + i + 1).padStart(2, '0')}`,
      ...lotIds(opt.index + i),
      group: built.group,
      hoist: built.hoist,
      belts: built.belts,
      s,
      speed: opt.speed,
      state: 'cruise',
      holding: false,
      timer: 0,
      drop: 0,
      nextStop,
      dest: stopPort.get(nextStop) ?? null,
    }
    built.group.userData.vehicleRef = { kind: 'oht', vehicle } satisfies VehicleRef
    vehicles.push(vehicle)
  }

  /** From the rail down to a docked carrier: rail height less the port's. */
  const DROP = opt.y + CARRIER_Y - PORT_Y
  const DROP_SPEED = 0.9

  const place = (v: OhtVehicle): void => {
    let s = v.s % perimeter
    if (s < 0) s += perimeter
    let x: number
    let z: number
    let dx: number
    let dz: number
    if (s < straight) {
      x = -opt.end + s
      z = opt.r
      dx = 1
      dz = 0
    } else if (s < straight + arc) {
      const a = (s - straight) / opt.r
      x = opt.end + opt.r * Math.sin(a)
      z = opt.r * Math.cos(a)
      dx = Math.cos(a)
      dz = -Math.sin(a)
    } else if (s < 2 * straight + arc) {
      x = opt.end - (s - straight - arc)
      z = -opt.r
      dx = -1
      dz = 0
    } else {
      const a = (s - 2 * straight - arc) / opt.r
      x = -opt.end - opt.r * Math.sin(a)
      z = -opt.r * Math.cos(a)
      dx = -Math.cos(a)
      dz = Math.sin(a)
    }
    v.group.position.set(x, opt.y, z)
    v.group.rotation.y = Math.atan2(-dz, dx)
  }

  const advance = (dt: number): void => {
    for (const v of vehicles) {
      v.timer += dt
      switch (v.state) {
        case 'cruise': {
          // Hold behind the vehicle ahead: two on one port is a collision.
          let gap = Infinity
          for (const o of vehicles) {
            if (o === v) continue
            const d = (o.s - v.s + perimeter) % perimeter
            if (d < gap) gap = d
          }
          v.holding = gap < 3.0
          if (v.holding) break
          // A loop with no ports on it never stops.
          if (!Number.isFinite(v.nextStop)) {
            v.s = (v.s + v.speed * dt) % perimeter
            break
          }
          // Ease into the stop over the last two metres, then land on it.
          const remaining = (v.nextStop - v.s + perimeter) % perimeter
          const step = v.speed * dt * Math.max(0.2, Math.min(1, remaining / 2))
          if (remaining <= step) {
            v.s = v.nextStop
            v.state = 'lower'
            v.timer = 0
          } else {
            v.s = (v.s + step) % perimeter
          }
          break
        }
        case 'lower':
          v.drop = Math.min(DROP, v.drop + DROP_SPEED * dt)
          if (v.drop >= DROP) {
            v.state = 'dwell'
            v.timer = 0
          }
          break
        case 'dwell':
          if (v.timer > 1.4) {
            v.state = 'raise'
            v.timer = 0
          }
          break
        case 'raise':
          v.drop = Math.max(0, v.drop - DROP_SPEED * dt)
          if (v.drop <= 0) {
            v.state = 'cruise'
            v.timer = 0
            // Some ports later - not the next one, or the aisle would be a
            // vehicle parked over every tool.
            v.nextStop = nextStopAfter(v.s, 2 + Math.floor(Math.random() * 5))
            v.dest = stopPort.get(v.nextStop) ?? null
          }
          break
      }
      v.hoist.position.y = -v.drop
      for (const belt of v.belts) {
        belt.scale.y = Math.max(0.01, v.drop)
        belt.position.y = -0.46 - v.drop / 2
      }
      place(v)
    }
  }

  for (const v of vehicles) place(v)
  return { vehicles, advance }
}

export function buildOht(mats: FabMaterials): Oht {
  const g = new THREE.Group()

  // Where the load ports are along the intrabay loop: the +z rail runs +x
  // past the +z row of tools, the -z rail runs -x past the other. A vehicle
  // heading for a port stops directly over it and lets the carrier down.
  const straight = 2 * OHT_END
  const arc = Math.PI * OHT_R
  const ports: Array<{ s: number; tool: Tool; port: number }> = []
  for (const tool of TOOLS) {
    const [x, , z] = tool.position
    if (z === 0 || tool.loadPorts === 0) continue
    const facing = aisleDirection(tool)
    for (let i = 0; i < tool.loadPorts; i++) {
      // Only the WORKING port - the one `buildTool` leaves empty. A hoist
      // used to let its carrier down onto a port that already had one
      // drawn on it, and two carriers in one place is a flicker.
      if (i !== workingPort(tool)) continue
      // Load ports are laid out in the tool's local x (`portX`), which the
      // facing yaw mirrors for the +z row.
      const localX = portX(tool, i)
      const worldX = x + (facing > 0 ? -localX : localX)
      const s = z > 0 ? worldX + OHT_END : straight + arc + (OHT_END - worldX)
      ports.push({ s, tool, port: i + 1 })
    }
  }

  // A real bay runs dozens; twenty on a 250 m loop is one every 12 m, busy
  // enough that a student at a load port sees one pass every few seconds and
  // queues form behind a vehicle that is working a port. Real hoists cruise
  // at 3-5 m/s; a little under, so they still read as machines gliding
  // rather than flashing past.
  const loop = buildOhtLoop(mats, g, { r: OHT_R, y: OHT_Y, end: OHT_END, count: 20, speed: 2.6, index: 0 }, ports)
  const vehicles = loop.vehicles

  // The ballroom's outer aisles get a loop each: one rail over the copies'
  // load ports, 0.22 m in front of their faces as the main loop's rails
  // are, the return rail toward the wall - a narrower loop, since the
  // aisle has tools on one side only. Its vehicles circulate without
  // stopping and cannot be picked: a stop needs a destination, and a
  // destination is a tool, which the copies are not (`buildSecondRow`).
  const outerLoops: OhtLoop[] = []
  if (BALLROOM) {
    const r = 2.0
    const inner = 2 * SECOND_ROW_PIVOT_Z - OHT_R
    for (const side of [-1, 1]) {
      const sub = new THREE.Group()
      sub.position.z = side * (inner + r)
      g.add(sub)
      outerLoops.push(buildOhtLoop(mats, sub, { r, y: OHT_Y, end: OHT_END, count: 10, speed: 2.6, index: 20 + outerLoops.length * 10 }, []))
    }
  }
  const advance = (dt: number): void => {
    loop.advance(dt)
    for (const o of outerLoops) o.advance(dt)
  }

  const status = (v: OhtVehicle): VehicleStatus => ({
    kind: 'oht',
    id: v.id,
    carrier: v.carrier,
    lot: v.lot,
    destination: v.dest?.tool ?? null,
    port: v.dest?.port ?? 0,
    state: v.state === 'cruise' ? (v.holding ? 'hold' : 'cruise') : 'transfer',
    speed: v.speed,
  })

  return { group: g, vehicles, perimeter: 2 * straight + 2 * arc, advance, status }
}

/**
 * A carrier and lot identity for the i-th vehicle: the shape of real ones
 * (a carrier serial, a lot number with its product letter), fixed per
 * vehicle so a student who reads a card twice reads the same lot.
 */
function lotIds(i: number): { carrier: string; lot: string } {
  return {
    carrier: `C${String(4100 + ((i * 137) % 5800)).padStart(4, '0')}`,
    lot: `L${String(24050 + i * 53)}${'ABCDEFGH'[i % 8]}`,
  }
}

/**
 * Autonomous mobile robots on the floor.
 *
 * The other moving machine in a current fab: a low, wheeled robot that
 * carries a carrier along the tool fronts, stops at a load port to hand it
 * over, and turns round at the end of the bay. Two lanes, one along each
 * row of tools, just outside the aisle's walkway stripe - which is where a
 * real fab paints them, and which is also where a student walks to reach
 * an HMI. So they do what the real ones do about people: a safety scanner
 * watches the floor ahead, and a person in that field stops the robot and
 * turns its light bar amber until they have moved on. The field is drawn
 * on the floor as the blue arc the newer machines project, so a student
 * can see why the robot stopped for them.
 */
export interface Amr {
  id: string
  carrier: string
  lot: string
  group: THREE.Group
  x: number
  /** The lane it runs in, and the way it is heading along it. */
  z: number
  dir: 1 | -1
  /** Yaw it is turning toward - across the aisle at the end of the lane. */
  heading: number
  /**
   * `cross`: driving across the aisle at the end of a lane to join the
   * other one - the lanes are one-way, +x on the +z side and -x on the
   * other, so a robot never meets one head on. `hold`: stopped behind the
   * robot ahead in the lane.
   */
  state: 'move' | 'deliver' | 'yield' | 'cross' | 'hold'
  timer: number
  /** The x it is heading to stop at, and the tool whose port that is. */
  target: number
  targetTool: Tool | null
  bar: THREE.MeshStandardMaterial
  field: THREE.LineDashedMaterial
  /**
   * The arm-equipped kind: it docks at the face and hands its carrier to
   * the load port itself. `dock` is how far it has left the lane toward
   * the face, `handoff` how far the carrier is along its path to the port.
   */
  arm: ArmRig | null
  dock: number
  handoff: number
}

/** The two-link arm and the carrier it moves, for the IK in `advance`. */
interface ArmRig {
  shoulder: THREE.Group
  upper: THREE.Group
  elbow: THREE.Group
  foup: THREE.Group
  /** Shoulder position and the carrier's resting place, in the robot's frame. */
  base: THREE.Vector3
  home: THREE.Vector3
}

export interface Amrs {
  group: THREE.Group
  robots: Amr[]
  /** Move every robot on by `dt` seconds, with the student at `eye`. */
  advance(dt: number, eye: THREE.Vector3): void
  status(r: Amr): VehicleStatus
}

/**
 * The lanes run inside the aisle stripe, where a real fleet's are painted,
 * clear of the technicians standing at the HMIs beyond it.
 */
const AMR_LANE_Z = 1.45
let AMR_END = FAB.length / 2 - 8
const AMR_SPEED = 1.3
const AMR_BLUE = 0x2f8dff
const AMR_AMBER = 0xffa62b
const AMR_GREEN = 0x33d17a
/** How far an arm robot leaves the lane to dock at a face: to 0.75 m off it. */
const AMR_DOCK = BULKHEAD_Z - FACE_PROUD - 0.75 - AMR_LANE_Z
const ARM_L1 = 0.5
const ARM_L2 = 0.5

/**
 * A robot lane: where it runs, which way, which lane it crosses to at the
 * end, where its stops are, and which way it leaves the lane to dock.
 *
 * The main aisle is one circuit of two lanes, +x along the +z row and -x
 * along the other, docking outward toward the faces. Fab B adds a circuit
 * per outer aisle: a lane along the copies' faces (docking INWARD, toward
 * the hall's centre, since that is where those faces are) and a return
 * lane by the wall with nothing to stop at.
 */
interface AmrLane {
  z: number
  dir: 1 | -1
  mate: number
  /** +1 / -1: the way `dock` moves the robot off the lane; 0 for a lane with no faces. */
  dockSign: number
  stops: 'tools' | 'copies' | 'none'
}

export function buildAmrs(mats: FabMaterials): Amrs {
  const g = new THREE.Group()

  const laneTable: AmrLane[] = [
    { z: AMR_LANE_Z, dir: 1, mate: -AMR_LANE_Z, dockSign: 1, stops: 'tools' },
    { z: -AMR_LANE_Z, dir: -1, mate: AMR_LANE_Z, dockSign: -1, stops: 'tools' },
  ]
  if (BALLROOM) {
    const copyLane = 2 * SECOND_ROW_PIVOT_Z - AMR_LANE_Z
    const wallLane = outerAisleZ() + OUTER_AISLE_HALF - 0.45
    for (const side of [1, -1] as const) {
      laneTable.push(
        { z: side * copyLane, dir: side, mate: side * wallLane, dockSign: -side, stops: 'copies' },
        { z: side * wallLane, dir: -side as 1 | -1, mate: side * copyLane, dockSign: 0, stops: 'none' },
      )
    }
  }
  const laneOf = (z: number): AmrLane => {
    let best = laneTable[0]
    for (const l of laneTable) if (Math.abs(l.z - z) < Math.abs(best.z - z)) best = l
    return best
  }
  const shell = new THREE.MeshStandardMaterial({ color: 0xe9ecef, roughness: 0.4, metalness: 0.15 })
  const armMat = new THREE.MeshStandardMaterial({ color: 0xf4f6f8, roughness: 0.35, metalness: 0.2 })
  const robots: Amr[] = []

  /** Where a lane's robots stop: at the working port of every tool on that side. */
  const stopsFor = (z: number): Array<{ x: number; tool: Tool }> => {
    const lane = laneOf(z)
    if (lane.stops === 'none') return []
    const copies = lane.stops === 'copies'
    return TOOLS.filter((t) => Math.sign(t.position[2]) === Math.sign(z) && workingPort(t) >= 0 && (!copies || hasSecondRowCopy(t)))
      .map((t) => {
        const localX = portX(t, workingPort(t))
        const dx = aisleDirection(t) > 0 ? -localX : localX
        // A copy's ports are the tool's mirrored in x about it; the card a
        // student reads off the robot names the copy, not the original.
        return copies
          ? { x: t.position[0] - dx, tool: { ...t, assetTag: secondTag(t.assetTag), position: [t.position[0], 0, 2 * Math.sign(t.position[2]) * SECOND_ROW_PIVOT_Z - t.position[2]] as THREE.Vector3Tuple } }
          : { x: t.position[0] + dx, tool: t }
      })
      .sort((a, b) => a.x - b.x)
  }

  // Lanes are one-way: +x on the +z side, -x on the -z side, joined by a
  // crossing at each end of the hall. Head-on meetings cannot happen.
  // Two of the six carry an arm.
  const lanes: Array<[number, number, 1 | -1, boolean]> = [
    [AMR_LANE_Z, -50, 1, false],
    [AMR_LANE_Z, -8, 1, true],
    [AMR_LANE_Z, 36, 1, false],
    [-AMR_LANE_Z, -30, -1, false],
    [-AMR_LANE_Z, 12, -1, true],
    [-AMR_LANE_Z, 52, -1, false],
  ]
  // Fab B's outer aisles: two robots along each copy row and one on the
  // return lane by the wall, on their own circuits (see `laneTable`).
  if (BALLROOM) {
    for (const side of [1, -1] as const) {
      const copyLane = laneTable.find((l) => l.stops === 'copies' && Math.sign(l.z) === side)!
      const wallLane = laneTable.find((l) => l.stops === 'none' && Math.sign(l.z) === side)!
      lanes.push([copyLane.z, -38, copyLane.dir, false], [copyLane.z, 18, copyLane.dir, true], [wallLane.z, -5, wallLane.dir, false])
    }
  }
  for (const [z, x0raw, dir, withArm] of lanes) {
    // Inside the lane's run, whatever the hall's length is this time.
    const x0 = Math.max(-AMR_END + 2, Math.min(AMR_END - 2, x0raw))
    const r = new THREE.Group()
    // Chassis: a dark base with the drive wheels just showing, a white
    // shell, the scanner puck at the front and the light bar around the
    // waist. The carrier rides on top in a kinematic cradle.
    const base = new THREE.Mesh(box(0.95, 0.18, 0.62), mats.conduitDark)
    base.position.y = 0.12
    r.add(base)
    for (const dx of [-0.3, 0.3]) {
      for (const dz of [-0.28, 0.28]) {
        const wheel = new THREE.Mesh(cyl(0.09, 0.09, 0.05, 12), mats.steel)
        wheel.rotation.x = Math.PI / 2
        wheel.position.set(dx, 0.09, dz)
        r.add(wheel)
      }
    }
    const body = new THREE.Mesh(box(0.9, 0.5, 0.58), shell)
    body.position.y = 0.47
    body.castShadow = true
    r.add(body)
    const scanner = new THREE.Mesh(cyl(0.05, 0.05, 0.06, 12), mats.conduitDark)
    scanner.position.set(0.47, 0.25, 0)
    r.add(scanner)
    // The arm robot carries its load aft, and the arm's turret forward.
    const home = new THREE.Vector3(withArm ? -0.2 : 0, 0.91, 0)
    const cradle = new THREE.Mesh(box(0.5, 0.03, 0.5), mats.steel)
    cradle.position.set(home.x, 0.735, 0)
    r.add(cradle)
    mergeStatics(r, mats, [shell])

    let arm: ArmRig | null = null
    const foup = buildFoup(mats)
    if (withArm) {
      // A two-link arm on a turret: base, shoulder, upper arm, elbow,
      // forearm, and the gripper that takes the carrier by its flange.
      const baseAt = new THREE.Vector3(0.25, 0.82, 0)
      const turret = new THREE.Mesh(cyl(0.11, 0.13, 0.2, 14), mats.conduitDark)
      turret.position.set(baseAt.x, 0.82, 0)
      r.add(turret)
      const shoulder = new THREE.Group()
      shoulder.position.set(baseAt.x, 0.95, 0)
      r.add(shoulder)
      const joint = new THREE.Mesh(cyl(0.07, 0.07, 0.14, 12), mats.conduitDark)
      joint.rotation.x = Math.PI / 2
      shoulder.add(joint)
      const upper = new THREE.Group()
      shoulder.add(upper)
      const upperArm = new THREE.Mesh(box(ARM_L1, 0.08, 0.08), armMat)
      upperArm.position.x = ARM_L1 / 2
      upper.add(upperArm)
      const elbow = new THREE.Group()
      elbow.position.x = ARM_L1
      upper.add(elbow)
      const elbowJoint = new THREE.Mesh(cyl(0.06, 0.06, 0.12, 12), mats.conduitDark)
      elbowJoint.rotation.x = Math.PI / 2
      elbow.add(elbowJoint)
      const forearm = new THREE.Mesh(box(ARM_L2, 0.07, 0.07), armMat)
      forearm.position.x = ARM_L2 / 2
      elbow.add(forearm)
      const gripper = new THREE.Mesh(box(0.12, 0.06, 0.16), mats.conduitDark)
      gripper.position.set(ARM_L2, -0.04, 0)
      elbow.add(gripper)
      // The carrier is its own object here: the arm carries it.
      foup.position.copy(home)
      r.add(foup)
      arm = { shoulder, upper, elbow, foup, base: shoulder.position.clone(), home }
    } else {
      for (const part of [...foup.children]) {
        part.position.y += home.y
        r.add(part)
      }
      mergeStatics(r, mats)
    }

    // The light bar: its own material, because its colour is the robot's
    // state. Blue moving, green delivering, amber stopped for a person.
    const bar = new THREE.MeshStandardMaterial({
      color: 0x101418,
      emissive: AMR_BLUE,
      emissiveIntensity: 2.0,
      roughness: 0.4,
    })
    const strip = new THREE.Mesh(box(0.92, 0.04, 0.6), bar)
    strip.position.y = 0.24
    r.add(strip)

    // The safety field's edge, projected on the floor ahead: a thin dashed
    // arc, the way the newer machines actually draw their warning zone,
    // rather than a filled pool. Same colours as the light bar.
    const field = new THREE.LineDashedMaterial({
      color: AMR_BLUE,
      dashSize: 0.16,
      gapSize: 0.1,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    })
    const pts: THREE.Vector3[] = []
    for (let i = 0; i <= 40; i++) {
      // From the robot's right round its front to its left, 1.9 m out.
      const a = -Math.PI / 2 + (i / 40) * Math.PI
      pts.push(new THREE.Vector3(0.5 + 1.9 * Math.cos(a), 0.012, 1.9 * Math.sin(a)))
    }
    const arc = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), field)
    arc.computeLineDistances()
    // Light on the floor, not the robot: a click on it is a click on the
    // floor, which is how a student dismisses a card.
    arc.raycast = () => {}
    // Shown only while the robot has stopped for someone: a real scanner's
    // field is invisible, and the arc's one job is to explain that stop to
    // the student standing in it. The rest of the time the light bar is
    // what a technician sees.
    arc.visible = false
    arc.name = 'field'
    r.add(arc)

    r.position.set(x0, 0, z)
    r.rotation.y = dir > 0 ? 0 : Math.PI
    g.add(r)

    const stops = stopsFor(z)
    const ahead = stops.filter((s) => (s.x - x0) * dir > 3)
    const robot: Amr = {
      id: `AMR-${String(robots.length + 1).padStart(2, '0')}`,
      ...lotIds(20 + robots.length),
      group: r,
      x: x0,
      z,
      dir,
      heading: r.rotation.y,
      state: 'move',
      timer: 0,
      target: ahead.length ? ahead[0].x : dir * AMR_END,
      targetTool: ahead.length ? ahead[0].tool : null,
      bar,
      field,
      arm,
      dock: 0,
      handoff: 0,
    }
    r.userData.vehicleRef = { kind: 'amr', robot } satisfies VehicleRef
    robots.push(robot)
  }

  const status = (r: Amr): VehicleStatus => {
    const tool = r.targetTool
    return {
      kind: 'amr',
      id: r.id,
      carrier: r.carrier,
      lot: r.lot,
      destination: tool,
      port: tool ? workingPort(tool) + 1 : 0,
      // Crossing the aisle is moving, as far as the card is concerned.
      state: r.state === 'cross' ? 'move' : r.state,
      speed: AMR_SPEED,
    }
  }

  /** The direction a lane runs: a loop, +x along one side, -x along the other. */
  const laneDir = (z: number): 1 | -1 => laneOf(z).dir
  const nextTarget = (r: Amr): void => {
    const ahead = stopsFor(r.z).filter((s) => (s.x - r.x) * r.dir > 3)
    const skip = Math.min(ahead.length - 1, Math.floor(Math.random() * 3))
    if (ahead.length) {
      r.target = ahead[skip].x
      r.targetTool = ahead[skip].tool
    } else {
      r.target = r.dir * AMR_END
      r.targetTool = null
    }
  }

  /**
   * Point the arm at its carrier: two-link planar IK in the vertical plane
   * through the shoulder and the load, the turret turned to that plane.
   */
  const scratch = new THREE.Vector3()
  const aimArm = (a: ArmRig): void => {
    // The gripper holds the flange on top of the carrier.
    const t = scratch.copy(a.foup.position).sub(a.base)
    t.y += 0.2
    const yaw = Math.atan2(-t.z, t.x)
    const reach = Math.hypot(t.x, t.z)
    const dist = Math.min(Math.hypot(reach, t.y), ARM_L1 + ARM_L2 - 0.01)
    const cosElbow = (ARM_L1 * ARM_L1 + ARM_L2 * ARM_L2 - dist * dist) / (2 * ARM_L1 * ARM_L2)
    const elbow = Math.PI - Math.acos(Math.max(-1, Math.min(1, cosElbow)))
    const cosShoulder = (ARM_L1 * ARM_L1 + dist * dist - ARM_L2 * ARM_L2) / (2 * ARM_L1 * dist)
    const pitch = Math.atan2(t.y, reach) + Math.acos(Math.max(-1, Math.min(1, cosShoulder)))
    a.shoulder.rotation.y = yaw
    a.upper.rotation.z = pitch
    a.elbow.rotation.z = -elbow
  }
  /** The carrier's path from its cradle to the port: a lift, a swing, a set-down. */
  const carrierAt = (a: ArmRig, u: number): void => {
    const port = scratch.set(0, 1.12, 0.75 - 0.22)
    const s = u * u * (3 - 2 * u)
    a.foup.position.lerpVectors(a.home, port, s)
    a.foup.position.y += 0.35 * Math.sin(Math.PI * u)
    // Turned to dock door-first, the way the port takes it.
    a.foup.rotation.y = Math.PI * s
  }

  const advance = (dt: number, eye: THREE.Vector3): void => {
    for (const r of robots) {
      r.timer += dt
      const crossing = r.state === 'cross'
      // The scanner: a person within reach of the front of the robot, the
      // way it is actually heading.
      const hx = crossing ? 0 : r.dir
      const hz = crossing ? -Math.sign(r.z) : 0
      const ax = r.group.position.x + hx * 1.3
      const az = r.group.position.z + hz * 1.3
      const dx = eye.x - ax
      const dz = eye.z - az
      const personAhead = eye.y > 0 && dx * dx + dz * dz < 2.3 * 2.3
      // The robot ahead in the same lane, close: hold behind it. Two
      // robots share a lane only heading the same way, so this is the one
      // way they can ever meet.
      let robotAhead = false
      if (!crossing) {
        for (const o of robots) {
          if (o === r || o.state === 'cross' || Math.sign(o.z) !== Math.sign(r.z)) continue
          const gap = (o.group.position.x - r.group.position.x) * r.dir
          if (gap > 0 && gap < 2.4) robotAhead = true
        }
      }

      if (r.state === 'yield') {
        if (!personAhead) {
          r.state = 'move'
          r.timer = 0
        }
      } else if (r.state === 'hold') {
        if (!robotAhead) r.state = 'move'
      } else if (personAhead && (r.state === 'move' || crossing)) {
        r.state = 'yield'
        r.timer = 0
      } else if (robotAhead && r.state === 'move') {
        r.state = 'hold'
      } else if (r.state === 'move') {
        const remaining = (r.target - r.x) * r.dir
        const step = AMR_SPEED * dt * Math.max(0.25, Math.min(1, remaining / 1.5))
        if (remaining <= step) {
          r.x = r.target
          if (Math.abs(r.target) >= AMR_END - 0.01) {
            // The end of the lane: cross the aisle to the other lane.
            r.state = 'cross'
            // Turned toward the lane it is crossing to.
            r.heading = laneOf(r.z).mate < r.z ? Math.PI / 2 : -Math.PI / 2
          } else {
            r.state = 'deliver'
            r.timer = 0
          }
        } else {
          r.x += r.dir * step
        }
      } else if (crossing) {
        const targetZ = laneOf(r.z).mate
        const dzc = targetZ - r.group.position.z
        const step = AMR_SPEED * dt
        if (Math.abs(dzc) <= step) {
          r.z = targetZ
          r.group.position.z = targetZ
          r.dir = laneDir(r.z)
          r.heading = r.dir > 0 ? 0 : Math.PI
          r.state = 'move'
          nextTarget(r)
        } else {
          r.group.position.z += Math.sign(dzc) * step
        }
      } else if (r.state === 'deliver') {
        if (r.arm) {
          // Leave the lane for the face, hand the carrier over, take it
          // back, return to the lane: seven seconds a technician can
          // watch from the HMI beside it.
          const t = r.timer
          const dockSpeed = AMR_DOCK / 1.5
          if (t < 1.5) {
            r.dock = Math.min(AMR_DOCK, r.dock + dockSpeed * dt)
            r.handoff = 0
          } else if (t < 3.0) {
            r.handoff = Math.min(1, (t - 1.5) / 1.5)
          } else if (t < 4.0) {
            r.handoff = 1
          } else if (t < 5.5) {
            r.handoff = Math.max(0, 1 - (t - 4.0) / 1.5)
          } else if (t < 7.2) {
            r.handoff = 0
            r.dock = Math.max(0, r.dock - dockSpeed * dt)
          } else {
            r.dock = 0
            r.state = 'move'
            r.timer = 0
            nextTarget(r)
          }
        } else if (r.timer > 4) {
          r.state = 'move'
          r.timer = 0
          nextTarget(r)
        }
      }

      const colour = r.state === 'yield' ? AMR_AMBER : r.state === 'deliver' ? AMR_GREEN : AMR_BLUE
      r.bar.emissive.set(colour)
      r.field.color.set(colour)
      const arc = r.group.getObjectByName('field')
      if (arc) arc.visible = r.state === 'yield'

      r.group.position.x = r.x
      if (!crossing) r.group.position.z = r.z + laneOf(r.z).dockSign * r.dock
      if (r.arm) {
        carrierAt(r.arm, r.handoff)
        aimArm(r.arm)
      }
      // Turn through the shortest way, at a rate a wheeled base can manage.
      let d = r.heading - r.group.rotation.y
      d = Math.atan2(Math.sin(d), Math.cos(d))
      r.group.rotation.y += Math.sign(d) * Math.min(Math.abs(d), 2.2 * dt)
    }
  }

  return { group: g, robots, advance, status }
}

/** Stack light: the first thing a technician reads walking into a bay. */
/** The tower's parts, shared by every tower on the floor. */
const TOWER_POST = new THREE.MeshStandardMaterial({ color: 0x8b929b, metalness: 0.7, roughness: 0.3 })
/**
 * The three lamps, green, amber, red, bottom to top. Unlit materials: a
 * lamp is a source, and the instanced towers light and darken each lamp
 * per tool through the instance colour (see `buildAttachments`), which an
 * emissive term would ignore. Lit is past 1.0 in linear light on purpose -
 * the high tier's bloom threshold is what tells a source from a surface,
 * and ACES compresses the excess on the lean tier.
 */
const TOWER_COLOURS = [new THREE.Color(0.2, 2.2, 0.9), new THREE.Color(2.4, 1.6, 0.25), new THREE.Color(2.4, 0.3, 0.3)]
const TOWER_DARK = TOWER_COLOURS.map((c) => c.clone().multiplyScalar(0.12))
const TOWER_LAMPS = TOWER_COLOURS.map(
  (c, i) => new THREE.MeshBasicMaterial({ color: i === 0 ? c : TOWER_DARK[i] }),
)

/** What a tower can say. `down` is red held; `alarm` is red flashing. */
export type TowerState = 'idle' | 'running' | 'alarm' | 'down'
/** The cap takes the tool's accent per instance; unlit, so it reads as lit. */
const TOWER_CAP = new THREE.MeshBasicMaterial({ color: 0xffffff })

function buildStatusTower(accent: number): THREE.Group {
  const g = new THREE.Group()
  // A real stack light is a hand's length of post and three 100 mm lenses -
  // about 0.55 m all told. At half a metre of post with 120 mm lenses this
  // stood a metre clear of the tool it belongs to and read, from the aisle,
  // as something hanging off the ceiling.
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.2, 8), TOWER_POST)
  post.position.y = 0.1
  g.add(post)

  TOWER_LAMPS.forEach((material, i) => {
    const lamp = new THREE.Mesh(new THREE.CylinderGeometry(0.062, 0.062, 0.1, 12), material)
    lamp.position.y = 0.25 + i * 0.105
    g.add(lamp)
  })

  const cap = new THREE.Mesh(
    new THREE.CylinderGeometry(0.066, 0.066, 0.03, 12),
    new THREE.MeshStandardMaterial({ color: accent, emissive: accent, emissiveIntensity: 0.6 }),
  )
  cap.position.y = 0.25 + 3 * 0.105
  g.add(cap)
  return g
}

/**
 * Which of a tool's load ports is left EMPTY - the working port the hoists
 * and the floor robots hand carriers to and take them from. Every other
 * port holds a carrier; a tool with one port keeps it full and is served
 * by nobody. Nor is an assembly tool: its loaders take magazines from an
 * operator's hands (see the load-port note in `buildTool`), and no OHT
 * loop or carrier robot runs into a back-end line.
 */
export const workingPort = (tool: Tool): number => (tool.loadPorts >= 2 && tool.zone !== 'assembly' ? 0 : -1)

/**
 * WHERE THE LOAD PORTS ARE, in the tool's local frame - and the one place
 * that answers it, because five places used to work it out for themselves.
 *
 * PORTS STAND SHOULDER TO SHOULDER. A 300 mm FOUP is 380 mm across and the
 * port module under it about half a metre wide, so two ports on a real tool
 * span a metre and a half whatever the mainframe behind them is. This file
 * used to spread them evenly across the whole body - `bodyW / loadPorts` -
 * which put IMP-10's two ports three metres apart on a six-metre face and
 * CLN-09's nearly three. The front-end module then stretched to follow
 * them, and a four-metre EFEM covered the tool's nameplate, its accent band
 * and every kind-specific thing SILHOUETTES had put on the face. Twenty
 * tools came out as the same white wall with two hatches in it.
 *
 * So: one group at a real pitch, at the OPERATOR'S END of the face - the end
 * the HMI hangs at, which is where a technician stands to load a lot. What
 * is left is mainframe skin, and that is where the tag, the band and the
 * kind's own hardware go.
 *
 * Past wafer sort there is no EFEM and no FOUP: an assembly machine's
 * magazine loaders flank the work area its operator watches, so those keep
 * the spread the body gives them.
 */
export const PORT_PITCH = 0.56
/** Face skin between the outermost port centre and the front end's own edge. */
const PORT_EDGE = 0.34

export function portX(tool: Tool, i: number): number {
  const w = toolSize(tool).w
  const n = Math.max(tool.loadPorts, 1)
  if (tool.zone === 'assembly') return -w / 2 + (w / n) * (i + 0.5)
  const left = w / 2 - PORT_EDGE - (n - 1) * PORT_PITCH
  // A group too wide for the face is centred on it instead.
  if (left < -w / 2 + PORT_EDGE) return (-(n - 1) * PORT_PITCH) / 2 + i * PORT_PITCH
  return left + i * PORT_PITCH
}

/** The glazed front end's local x extent, [left, right], for a tool that has one. */
export function efemSpan(tool: Tool): [number, number] {
  return [portX(tool, 0) - PORT_EDGE, portX(tool, tool.loadPorts - 1) + PORT_EDGE]
}

/**
 * The kinds whose process side really is a radial vacuum cluster - a
 * transfer module with chambers docked round it and load locks at the
 * front. Everything else with a `chambers` count has process modules of
 * some other shape entirely, and drawing a cluster for them put a
 * transfer module on the roof of the polisher.
 */
const CLUSTER_KINDS = new Set<ToolKind>(['etcher', 'deposition', 'epi'])
/** The kinds fed from chemical day tanks rather than a gas box. */
const WET_KINDS = new Set<ToolKind>(['wetbench', 'cmp', 'plating'])

/** Whether this tool shows the aisle a glazed front end at all. */
export const hasEfem = (tool: Tool): boolean =>
  tool.position[2] !== 0 && tool.kind !== 'gowning' && tool.loadPorts > 0 && tool.zone !== 'assembly'

/**
 * The mainframe skin the aisle sees beside the front end: local x, [left, right].
 *
 * The front end owns one end of every bulkheaded face; what is left is plain
 * skin, and everything the tool wears that is not part of the front end - the
 * accent band, the EMO, the printed labels, and the one panel that comes OFF
 * for a PM - is placed inside these two numbers. Exported because bays.ts
 * places that panel from out here and used to guess at it, which is how the
 * technician working it ended up standing behind the HMI screen.
 */
export function skinSpan(tool: Tool): [number, number] {
  const w = toolSize(tool).w
  return hasEfem(tool) ? [-w / 2, efemSpan(tool)[0]] : [-w / 2, w / 2]
}

/**
 * Where the HMI arm hangs on the face: local x. ONE definition, used by the
 * face builder, the floor-wear pass and by people.ts to stand the technician
 * in front of it - three hand copies of this line had already drifted once
 * (the people stood 1 to 2 m from their screens until 2026-09-17).
 */
export const hmiFaceX = (tool: Tool): number =>
  hasEfem(tool) ? efemSpan(tool)[0] - 0.42 : toolSize(tool).w / 2 - 0.4

/**
 * Where the EMO mushroom sits on the face: local x.
 *
 * Just inboard of the HMI, within reach of whoever is standing at it, which
 * makes it the outboard-most thing on the skin - so anything else that wants
 * skin has to finish before it.
 */
export function emoFaceX(tool: Tool): number {
  const w = toolSize(tool).w
  return hasEfem(tool) ? Math.max(efemSpan(tool)[0] - 0.95, -w / 2 + 0.15) : w / 2 - 1.15
}

/**
 * The front end's height, and WHAT SETS IT: the transport, not the tool.
 *
 * Its floor is the carrier - a FOUP on a 900 mm port tops out at 1.28 m, so
 * the glass starts just above that. Its roof is `OHT_SWEEP.yLo`, the bottom
 * of a cruising vehicle's skirts: the front end stands 0.3 m out into the
 * aisle from the face, which puts it under the monorail, and anything above
 * that line is something every carrier on the loop drives through. Written
 * as `bodyH + 0.06` capped at 2.16 it was 0.07 m inside the vehicles.
 *
 * So it is a constant, and a low mainframe simply gets a front end standing
 * proud of it, the way a real EFEM bolted to a 1.5 m wet bench does.
 */
export const EFEM_SILL = 1.32
export const EFEM_HEADER = 0.3
/** How far the front end stands out of the face. */
export const EFEM_DEPTH = 0.3
export const efemTop = (_bodyH: number): number => OHT_SWEEP.yLo - 0.07

/**
 * The small parts every tool carries in numbers - carriers on its load
 * ports, the EMO on its front - collected as world-space placements so
 * they can be drawn INSTANCED across the whole floor (`buildAttachments`)
 * instead of as a few hundred little meshes. Draw calls are what the
 * integrated GPU pays for, twice over on the high tier (the AO's G-buffer
 * pass redraws everything), and the first version of the carriers cost a
 * quarter of the frame rate on their own.
 */
export interface Attachments {
  /** One matrix per docked carrier: the FOUP's own frame, in world space. */
  foups: THREE.Matrix4[]
  /** One matrix per EMO: the button's frame, +z out of the panel face. */
  emos: THREE.Matrix4[]
  /** One per status tower: its base frame, the tool's accent for the cap, and whose it is. */
  towers: Array<{ frame: THREE.Matrix4; accent: number; toolId: string }>
  /**
   * Every printed label on every tool face, as a world frame and which tile
   * of the atlas it shows. Collected across all tools and merged into one
   * mesh - a few hundred quads is one draw, and drawn per tool it would be
   * twenty.
   */
  stickers: Array<{ frame: THREE.Matrix4; tile: number; size: number }>
}

export const newAttachments = (): Attachments => ({ foups: [], emos: [], towers: [], stickers: [] })

/**
 * Fab B's second row: every bay tool built again and turned half a turn
 * about the service aisle's centreline at its own x (see SECOND_ROW_PIVOT_Z
 * in layout.ts for what it is and why it is not a tool).
 *
 * `buildTool` places a face on the bulkhead face line whatever it is asked,
 * so the copy is built where the original stands and moved afterwards -
 * the group by its transform, and every instanced attachment it filed by
 * the same rigid motion, since those were filed in world space against the
 * original's frame. A half turn, not a mirror: a mirrored placard reads
 * backwards, and a turned one is just a tool that faces the other way.
 *
 * The one copy that would stand over the sub-fab stairwell is left out;
 * the mouth of a stair is the last place to park a mainframe.
 */
/** Whether this bay tool has a copy in Fab B's second row: all but the one over the sub-fab stairwell. */
export function hasSecondRowCopy(tool: Tool): boolean {
  if (tool.position[2] === 0) return false
  const [x, , z] = tool.position
  const stair = SUBFAB.stair
  const half = toolSize(tool).w / 2 + 1.2
  return !(z > 0 && x + half > stair.minX && x - half < stair.maxX)
}

export function buildSecondRow(mats: FabMaterials, attach: Attachments): THREE.Group {
  const g = new THREE.Group()
  for (const tool of TOOLS) {
    if (!hasSecondRowCopy(tool)) continue
    const [x, , z] = tool.position
    const side = Math.sign(z)
    const own = newAttachments()
    const copy = buildTool({ ...tool, id: `${tool.id}.b`, assetTag: secondTag(tool.assetTag) }, mats, own)
    // The motion: a half turn about the vertical through (x, pivot).
    const pz = side * SECOND_ROW_PIVOT_Z
    const motion = new THREE.Matrix4()
      .makeTranslation(x, 0, 2 * pz - z)
      .multiply(new THREE.Matrix4().makeRotationY(Math.PI))
      .multiply(new THREE.Matrix4().makeTranslation(-x, 0, -z))
    copy.position.set(x, 0, 2 * pz - z)
    copy.rotation.y += Math.PI
    // Not a tool: nothing may pick it, open it, or paint its screen.
    delete copy.userData.toolId
    for (const m of own.foups) attach.foups.push(m.premultiply(motion))
    for (const m of own.emos) attach.emos.push(m.premultiply(motion))
    for (const t of own.towers) attach.towers.push({ ...t, frame: t.frame.premultiply(motion) })
    for (const s of own.stickers) attach.stickers.push({ ...s, frame: s.frame.premultiply(motion) })
    g.add(copy)
  }
  return g
}

/**
 * Build one process tool: the face a bulkheaded tool shows the aisle.
 *
 * The mainframe is built at its full depth, through the bulkhead and into
 * the chase, and what the aisle sees is the front of it standing a little
 * proud of the wall: plinth, skins, the load ports with their carriers,
 * the HMI on its arm, the placard, the light tower, the EMO. The
 * kind-specific parts (SILHOUETTES) go on that face or stand beside it.
 */
export function buildTool(tool: Tool, mats: FabMaterials, attach?: Attachments): THREE.Group {
  const g = new THREE.Group()
  g.position.set(...tool.position)
  g.rotation.y = facingYaw(tool)
  g.userData.toolId = tool.id
  // The group's world frame, for placing instanced attachments.
  g.updateMatrix()
  const frame = g.matrix.clone()
  const local = new THREE.Matrix4()

  const { w: bodyW, h: bodyH, d: bodyD } = toolSize(tool)
  const axis = tool.position[2] === 0
  // Local z of the face: a free-standing tool's own front, or the line the
  // bulkheaded faces all stand on, whatever the tool's depth.
  const front = axis ? -bodyD / 2 : -(Math.abs(tool.position[2]) - toolFrontZ(tool))
  const proud = axis ? bodyD : FACE_PROUD
  const depth = axis ? bodyD : Math.max(bodyD, proud + 1.0)
  // The gowning station is not a machine and gets no mainframe: it is an
  // open island - bench, rail, dispensers - whose parts SILHOUETTES builds
  // in full. What the shared face still gives it is the tag, on a sign
  // hung over the bench, and the HMI, on a pedestal instead of an arm.
  const open = tool.kind === 'gowning'

  // The glazed front end, if this tool has one: where it starts and stops
  // along the face, and how tall it stands. Everything else on the face -
  // the accent band, the nameplate, the HMI arm, the EMO, the printed
  // labels, the light tower - is placed against these three numbers, so
  // the front end can no longer be built over the top of them.
  const efem = hasEfem(tool)
    ? { x0: efemSpan(tool)[0], x1: efemSpan(tool)[1], top: efemTop(bodyH) }
    : null
  /** The mainframe skin the aisle sees beside the front end. */
  const skin: [number, number] = skinSpan(tool)
  const skinW = skin[1] - skin[0]
  const skinMid = (skin[0] + skin[1]) / 2
  /** The HMI arm and the EMO, which three sections below all place against. */
  const hmiX = hmiFaceX(tool)
  const emoX = emoFaceX(tool)
  const foot: Footprint = { w: bodyW, h: bodyH, d: bodyD, front, proud, bulk: front + proud, skin }

  const accent = VENDOR_LIVERY[VENDOR_OF[tool.kind] ?? 1]
  // Accent band at eye level, the way vendors brand a tool. Only across the
  // skin: it is paint on the mainframe, and it used to run behind the front
  // end and come out the other side in two disconnected stubs.
  const band = new THREE.Mesh(box(skinW + 0.02, 0.12, proud + 0.02), accent)
  if (!open) {
    // --- mainframe ---
    const mainframe = new THREE.Mesh(box(bodyW, bodyH - 0.1, depth), mats.toolBody)
    mainframe.position.set(0, bodyH / 2 + 0.05, front + depth / 2)
    mainframe.castShadow = true
    mainframe.receiveShadow = true
    g.add(mainframe)

    band.position.set(skinMid, bodyH - 0.35, front + proud / 2)
    g.add(band)

    // Plinth: a dark recessed kick strip where the panels meet the floor. A
    // white box sitting on a white floor has no bottom edge - it floats - and
    // the shadow line a real tool's frame casts under its skins is what says
    // "standing on" rather than "placed on". Set in a few centimetres so it
    // reads as a reveal, not a stripe.
    //
    // Only as deep as the face in a bay-and-chase hall, where the rest of
    // the mainframe is behind a wall; the whole footprint in a ballroom,
    // where its sides and back stand in the open and would float the same
    // way the front did.
    const kick = BALLROOM ? depth : proud
    const plinth = new THREE.Mesh(box(bodyW - 0.06, 0.1, kick - 0.03), mats.conduitDark)
    plinth.position.set(0, 0.05, front + kick / 2 + 0.015)
    g.add(plinth)
  }

  // --- load ports, facing the aisle ---
  //
  // A 300 mm load port as it actually looks from the aisle: a CLOSED port
  // door in the face - a flat panel in the tool's own colour with a seam
  // round it, since the door only ever opens inward, behind a docked
  // carrier - the kinematic plate at 900 mm the carrier sits on, with its
  // three pins and the presence sensor, the port number over it, and the
  // load/unload indicator pair beside that. The first version drew the
  // door as a black rectangle, and every empty working port on the floor
  // read as a hole in the tool. The working port is left empty for the
  // hoists; every other port has a carrier docked door-to-door.
  //
  // Past wafer sort the carrier is not a FOUP. Dicing takes a cassette of
  // film frames, the bonder and the mould press take lead-frame or strip
  // magazines, final test takes tray stacks: what all of them show the
  // aisle is a MAGAZINE LOADER - an opening with the magazine's slotted
  // rails in it and the elevator under - labelled IN and OUT rather than
  // LP1 and LP2, and served by an operator, not a hoist. Drawing a FOUP
  // docked on a wire bonder taught the wrong thing.
  const magazines = tool.zone === 'assembly'
  for (let i = 0; i < tool.loadPorts; i++) {
    const x = portX(tool, i)
    if (magazines) {
      const mw = tool.kind === 'dicing' ? 0.42 : 0.3
      const opening = new THREE.Mesh(box(mw + 0.12, 0.62, 0.02), mats.conduitDark)
      opening.position.set(x, 1.12, front - 0.01)
      g.add(opening)
      const sill = new THREE.Mesh(box(mw + 0.2, 0.05, 0.3), mats.toolPanel)
      sill.position.set(x, 0.79, front - 0.15)
      g.add(sill)
      // The magazine on its elevator: two side plates and the slot rails
      // between them, a frame in every second slot.
      for (const dx of [-mw / 2, mw / 2]) {
        const plate = new THREE.Mesh(box(0.012, 0.42, 0.24), mats.steel)
        plate.position.set(x + dx, 1.05, front - 0.12)
        g.add(plate)
      }
      for (let r = 0; r < 8; r++) {
        const rail = new THREE.Mesh(box(mw, 0.006, 0.24), r % 2 === 0 ? mats.conduitDark : mats.steel)
        rail.position.set(x, 0.86 + r * 0.05, front - 0.12)
        g.add(rail)
      }
      const label = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.06), portLabelMaterial(i === 0 ? 'IN' : 'OUT'))
      label.rotation.y = Math.PI
      label.position.set(x - 0.1, 1.5, front - 0.006)
      g.add(label)
      face.leds(g, LED_GREEN, 0.04, x + 0.08, 1.5, front)
      continue
    }
    const seam = new THREE.Mesh(box(0.46, 0.5, 0.012), mats.conduitDark)
    seam.position.set(x, 1.2, front - 0.006)
    g.add(seam)
    const portDoor = new THREE.Mesh(box(0.42, 0.46, 0.014), mats.toolPanel)
    portDoor.position.set(x, 1.2, front - 0.014)
    g.add(portDoor)
    const plate = new THREE.Mesh(box(0.5, 0.08, 0.45), mats.toolPanel)
    plate.position.set(x, 0.92, front - 0.2)
    g.add(plate)
    // Kinematic coupling pins, in the triangle a carrier's base carries.
    for (const [px, pz] of [[-0.12, -0.1], [0.12, -0.1], [0, 0.12]] as const) {
      const pin = new THREE.Mesh(cyl(0.012, 0.012, 0.02, 8), mats.steel)
      pin.position.set(x + px, 0.97, front - 0.22 + pz)
      g.add(pin)
    }
    // The port number and its lamps, ON THE FRONT END'S SILL BAR - the
    // band of frame between the carrier and the glass - and NOT on the
    // face behind it. On the face at 1.52 m they were on the far side of
    // the mini-environment's glazing, which is 0.3 m nearer the aisle:
    // every port number on the floor was a grey smudge seen through a
    // pane, and the load and present lamps with them, when the port number
    // is the single thing a technician reads off a front end.
    const labelZ = efem ? front - EFEM_DEPTH - 0.005 : front - 0.006
    const labelY = efem ? EFEM_SILL : 1.52
    const label = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.055), portLabelMaterial(`LP${i + 1}`))
    label.rotation.y = Math.PI
    label.position.set(x - 0.12, labelY, labelZ)
    g.add(label)
    // The indicator pair: load (green) and the busy or present lamp, which
    // is lit on the ports that hold a carrier and dark on the empty one.
    face.leds(g, LED_GREEN, 0.035, x + 0.06, labelY, labelZ + 0.005)
    if (i !== workingPort(tool)) face.leds(g, LED_AMBER, 0.035, x + 0.12, labelY, labelZ + 0.005)
    if (i === workingPort(tool)) continue
    if (attach) {
      // Turned so the carrier's door faces the port it is docked against.
      local.makeRotationY(Math.PI).setPosition(x, 1.12, front - 0.22)
      attach.foups.push(frame.clone().multiply(local))
    } else {
      const foup = buildFoup(mats)
      foup.rotation.y = Math.PI
      foup.position.set(x, 1.12, front - 0.22)
      g.add(foup)
    }
  }

  // --- the EFEM: the glazed front end ---
  //
  // THE THING THAT MAKES A TOOL LOOK LIKE A TOOL. Photograph a 300 mm bay
  // and what you are looking at, over and over down both rows, is not the
  // mainframe - that is a white box behind a wall - it is the equipment
  // front-end module: a glazed enclosure standing proud of it with the load
  // ports under it, a dark mullion frame round the glass, and the
  // atmospheric robot visibly moving about inside. A tool front without one
  // is a cabinet with two doors in it, which is what these were.
  //
  // Built as a shallow hood 0.3 m proud rather than the 0.6 m a real one
  // stands: the carriers, the hoist rail above them and the robot lane are
  // all dimensioned off the face plane, and moving the ports out to a full
  // depth EFEM would drag the transport in with them and narrow the aisle
  // again. At 0.3 the ports sit in its lower opening, recessed, which is a
  // real arrangement in its own right and costs nothing else in the hall.
  //
  // Not on the assembly tools: past wafer sort a machine is fed magazines
  // by hand and has no mini-environment to glaze.
  if (efem) {
    const { x0, x1, top } = efem
    const w = x1 - x0
    const mid = (x0 + x1) / 2
    const D = EFEM_DEPTH
    const zf = front - D
    const sill = EFEM_SILL
    const glassTop = top - EFEM_HEADER
    const dark = mats.conduitDark

    // Uprights and header: the mullion frame the glass sits in. The header
    // is deep enough to carry the nameplate, which is where a real front
    // end wears it - see the asset-tag note below.
    for (const ux of [x0, x1]) {
      const post = new THREE.Mesh(box(0.075, top, D), dark)
      post.position.set(ux, top / 2, front - D / 2)
      post.castShadow = true
      g.add(post)
    }
    const header = new THREE.Mesh(box(w, EFEM_HEADER, D), mats.toolBody)
    header.position.set(mid, top - EFEM_HEADER / 2, front - D / 2)
    header.castShadow = true
    g.add(header)
    // The vendor's colour along the header's lower edge, so the front end
    // is liveried with the mainframe beside it rather than left plain white.
    const headerBand = new THREE.Mesh(box(w, 0.045, D + 0.01), accent)
    headerBand.position.set(mid, top - EFEM_HEADER + 0.023, front - D / 2)
    g.add(headerBand)
    // The sill the ports look out under, and the toe plate below them.
    const sillBar = new THREE.Mesh(box(w, 0.07, D), dark)
    sillBar.position.set(mid, sill, front - D / 2)
    g.add(sillBar)
    const toe = new THREE.Mesh(box(w, 0.24, D), dark)
    toe.position.set(mid, 0.12, front - D / 2)
    g.add(toe)

    // THE INTERIOR, seen through the glass: a dark back wall so the
    // enclosure has a depth to it, the handling robot's column, and the
    // aligner it swings a wafer over. Small and dark on purpose - what
    // reads through a pane at three metres is a silhouette and the fact
    // that something is in there, not a mechanism anyone can follow.
    const cavity = new THREE.Mesh(box(w - 0.08, glassTop - sill - 0.02, 0.01), CAVITY_DARK)
    cavity.position.set(mid, (sill + glassTop) / 2, front - 0.012)
    g.add(cavity)
    const column = new THREE.Mesh(cyl(0.075, 0.085, glassTop - sill - 0.1, 12), mats.steel)
    column.position.set(mid - 0.1, (sill + glassTop) / 2, front - 0.15)
    g.add(column)
    const shoulder = new THREE.Mesh(box(0.34, 0.07, 0.12), mats.toolPanel)
    shoulder.position.set(mid + 0.05, glassTop - 0.12, front - 0.15)
    g.add(shoulder)
    const aligner = new THREE.Mesh(cyl(0.12, 0.12, 0.035, 16), mats.steel)
    aligner.position.set(x1 - 0.3, sill + 0.1, front - 0.16)
    g.add(aligner)

    // The glazing last, so it is drawn over what it is meant to show.
    const pane = new THREE.Mesh(box(w - 0.09, glassTop - sill - 0.03, 0.008), mats.glass)
    pane.position.set(mid, (sill + glassTop) / 2, zf + 0.01)
    g.add(pane)
    // A mullion between panes: one per port, which is how the frame of a
    // real front end is divided, rather than one down an arbitrary middle.
    for (let i = 1; i < tool.loadPorts; i++) {
      const mullion = new THREE.Mesh(box(0.05, glassTop - sill - 0.03, D * 0.7), dark)
      mullion.position.set((portX(tool, i - 1) + portX(tool, i)) / 2, (sill + glassTop) / 2, front - D * 0.4)
      g.add(mullion)
    }
    // The interlocked door handle, on the pane's own frame.
    const handle = new THREE.Mesh(box(0.03, 0.2, 0.05), mats.steel)
    handle.position.set(x1 - 0.16, sill + 0.24, zf - 0.01)
    g.add(handle)
  }

  // --- the process side, in the chase ---
  //
  // What the viewing window beside the face looks onto: the chambers on
  // the transfer module's roof, the pump and gas box beside the mainframe,
  // the cable tray over it. Behind the wall for every tool in a bay - a
  // window shows the next bay's chase too.
  if (!axis) {
    const back = front + depth
    if (tool.chambers > 0 && CLUSTER_KINDS.has(tool.kind)) {
      // A cluster tool from behind: the transfer module in the middle of
      // the roof - a low hexagonal vacuum vessel - with the process
      // chambers docked round it, each a square body with a round lid
      // and its lid-lift post. The earlier version was a ring of drums
      // the height of a person, which is no chamber anyone has built.
      //
      // ONLY ON A CLUSTER TOOL. `chambers` counts process modules for
      // every kind that has any - CMP's platens, ECD's plating cells,
      // INSP-13's optics - and this built a radial vacuum cluster with
      // load locks for all of them. The chase is a place a student can
      // walk into, and what they found on the roof of the polisher was a
      // transfer module: the wrong machine, on the one tool whose whole
      // lesson is that it is wet and atmospheric.
      const cx0 = 0
      const cz0 = foot.bulk + Math.min(1.1, (depth - proud) / 2)
      const transfer = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.36, 6), mats.chamber)
      transfer.position.set(cx0, bodyH + 0.18, cz0)
      transfer.castShadow = true
      g.add(transfer)
      const n = tool.chambers
      for (let i = 0; i < n; i++) {
        // Round the back half of the module, the side that is not the
        // face: the load locks are at the front.
        const angle = Math.PI * (0.15 + (0.7 * (i + 0.5)) / n)
        const r = 0.95
        const cx = cx0 + Math.cos(angle) * r * Math.min(1, bodyW / 3.4)
        const cz = cz0 + Math.sin(angle) * 0.75
        if (cz > back - 0.35) continue
        const chamber = new THREE.Mesh(box(0.62, 0.42, 0.62), mats.chamber)
        chamber.position.set(cx, bodyH + 0.21, cz)
        chamber.rotation.y = -angle + Math.PI / 2
        chamber.castShadow = true
        g.add(chamber)
        const lid = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.28, 0.06, 20), mats.steel)
        lid.position.set(cx, bodyH + 0.45, cz)
        g.add(lid)
        const post = new THREE.Mesh(cyl(0.03, 0.03, 0.7, 8), mats.steel)
        post.position.set(cx + Math.cos(angle) * 0.34, bodyH + 0.35, cz + Math.sin(angle) * 0.34)
        g.add(post)
      }
      // The load locks, between the module and the face's back.
      for (const dx of [-0.42, 0.42]) {
        const lock = new THREE.Mesh(box(0.4, 0.3, 0.5), mats.chamber)
        lock.position.set(cx0 + dx, bodyH + 0.15, cz0 - 0.75)
        g.add(lock)
      }
    }
    // A wet tool's process side is not a vacuum cluster, it is CHEMISTRY:
    // the day tanks its baths are fed from, standing on a bunded pallet
    // with the feed lines up to the tool. This is what CLN-09, CMP-11 and
    // ECD-12 have in the chase instead.
    if (WET_KINDS.has(tool.kind)) {
      // BEHIND the mainframe, which runs back to `back`: on the roof line
      // beside it they would have been inside the cabinet.
      const tz = back + 0.65
      const pallet = new THREE.Mesh(box(1.9, 0.12, 0.9), mats.steel)
      pallet.position.set(0, 0.06, tz)
      g.add(pallet)
      for (let i = 0; i < 3; i++) {
        const dx = -0.6 + i * 0.6
        const drum = new THREE.Mesh(cyl(0.26, 0.26, 0.9, 14), mats.toolPanel)
        drum.position.set(dx, 0.57, tz)
        drum.castShadow = true
        g.add(drum)
        const lid = new THREE.Mesh(cyl(0.27, 0.27, 0.05, 14), mats.conduitDark)
        lid.position.set(dx, 1.04, tz)
        g.add(lid)
        // The feed up to the tool's own roof line and forward over it.
        const feed = new THREE.Mesh(cyl(0.03, 0.03, bodyH - 1.05, 8), mats.conduit)
        feed.position.set(dx, 1.05 + (bodyH - 1.05) / 2, tz)
        g.add(feed)
        const run = new THREE.Mesh(cyl(0.03, 0.03, 0.65, 8), mats.conduit)
        run.rotation.x = Math.PI / 2
        run.position.set(dx, bodyH, tz - 0.325)
        g.add(run)
      }
    }
    // The dry pump beside the mainframe is a chase thing: a ballroom's pumps
    // are in the sub-fab, and nothing but the tool and its gas box stands on
    // the clean floor behind the row.
    if (!BALLROOM) {
      const pumpBox = new THREE.Mesh(box(0.8, 1.6, 0.8), mats.toolPanel)
      pumpBox.position.set(bodyW / 2 + 0.55, 0.8, foot.bulk + 1.3)
      pumpBox.castShadow = true
      g.add(pumpBox)
    }
    const gasBox = new THREE.Mesh(box(0.7, 1.9, 0.6), mats.toolPanel)
    gasBox.position.set(-bodyW / 2 - 0.5, 0.95, foot.bulk + 1.1)
    g.add(gasBox)
    // The tool's own cable tray: on the bulkhead line in a bay-and-chase
    // hall, where the wall carries it; on the mainframe's roof in a
    // ballroom, where there is nothing else to carry it.
    const tray = new THREE.Mesh(box(bodyW + 1.6, 0.05, 0.3), mats.conduitDark)
    tray.position.set(0, BALLROOM ? bodyH + 0.03 : 2.9, foot.bulk + 0.6)
    g.add(tray)
  }

  // --- kind-specific face ---
  faceClaims.length = 0
  SILHOUETTES[tool.kind]?.(g, mats, foot, tool.accent)

  // --- asset-tag placard ---
  //
  // How a fab is really navigated: on the front of every tool facing the
  // aisle, so a student forms the habit of looking for it. Not localised -
  // it is stencilled on physical hardware.
  //
  // ON THE FRONT END'S HEADER when the tool has one, on the mainframe skin
  // when it does not. A real 300 mm tool wears its name across the top of
  // its front-end module, above the glass and below the light tower, and
  // putting it there is also the only place it survives: centred on the
  // body it sat squarely behind the front end, and on the 1.5 m wet bench
  // the sill bar cut the letters in half.
  const tagW = efem
    ? Math.min(efem.x1 - efem.x0 - 0.14, 1.5)
    : Math.min(bodyW * 0.55, 1.5, Math.max(bodyW - 2.0, 0.9))
  const tagX = efem ? (efem.x0 + efem.x1) / 2 : 0
  const tagY = efem ? efem.top - EFEM_HEADER / 2 + 0.02 : bodyH - 0.14
  const tagZ = efem ? front - 0.312 : front - 0.012
  const placard = new THREE.Mesh(
    new THREE.PlaneGeometry(tagW, tagW * 0.28),
    new THREE.MeshBasicMaterial({ map: assetTagTexture(tool.assetTag, tool.accent) }),
  )
  // A PlaneGeometry faces +Z. This one sits on the -Z side of the mainframe,
  // so without the half-turn it faces into the tool and is invisible from the
  // aisle - which is exactly what happened to the HMI screen below.
  placard.rotation.y = Math.PI
  placard.position.set(tagX, tagY, tagZ)
  g.add(placard)

  // A TOOL ON THE AISLE'S AXIS IS APPROACHED ALONG THE AISLE, not across
  // it, so its front is not the side anyone walks up to. GAS-19 stands at
  // the head of the hall with its tag on its +z face and a blank five
  // metres of cabinet turned to everybody coming up the aisle: the one
  // tool on the floor a student could not name from where they stood. It
  // gets a second tag on the end the aisle arrives at. (GOWN-00 is
  // excepted - its tag is on the sign hung over the bench.)
  if (axis && !open) {
    const endSide = Math.sign(tool.position[0]) || 1
    // High on the end face: the cabinets that stand beside the body reach
    // 2.2 m, and a tag centred where the front's is sat behind their tops.
    const endW = tagW * 0.8
    const endTag = new THREE.Mesh(
      new THREE.PlaneGeometry(endW, endW * 0.28),
      new THREE.MeshBasicMaterial({ map: assetTagTexture(tool.assetTag, tool.accent) }),
    )
    endTag.rotation.y = endSide > 0 ? Math.PI / 2 : -Math.PI / 2
    endTag.position.set(endSide * (bodyW / 2 + 0.012), bodyH - 0.03, 0)
    g.add(endTag)
  }

  // --- the paperwork ---
  //
  // A production tool front is covered in small printed things and a
  // rendered one is not, and that is most of what still read as rendered
  // once the room's proportions and light were right. See `stickerAtlas`.
  //
  // Placed in the two bands the face actually leaves free - under the load
  // ports, where the plinth and the frame are, and the strip between the
  // port labels and the asset tag - and kept off the HMI's side of the
  // face, since the bezel stands a shoulder proud of the wall and would
  // hide anything behind it. Which is also why a real tool has none there.
  //
  // Deterministic per tool, from the asset tag: the same machine carries
  // the same labels every session, because a technician who learns "the
  // one with the laser sticker" has learnt something about THAT tool.
  if (attach && !axis) {
    let seed = 0
    for (let i = 0; i < tool.assetTag.length; i++) seed = (seed * 31 + tool.assetTag.charCodeAt(i)) >>> 0
    const rnd = (): number => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296)
    const local = new THREE.Matrix4()
    const pos = new THREE.Vector3()
    const quat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI)
    const one = new THREE.Vector3(1, 1, 1)
    // WHAT IS ALREADY THERE. Stickers sit 2 mm proud of the face, so
    // anything they land on they cover: the first version put a clump at
    // `bodyH - 0.33` and the asset tag runs from 1.76 to 2.16, and the
    // result was an inspection dot stuck across the middle of PAT-21's
    // nameplate. Nobody labels over the one thing on a tool that has to
    // stay readable, and the tag is the thing this whole floor is navigated
    // by. Load ports go on the list for the same reason.
    const tagH = tagW * 0.28
    const occupied: Array<[number, number, number, number]> = [
      [tagX - tagW / 2, tagX + tagW / 2, tagY - tagH / 2, tagY + tagH / 2],
      // The HMI bezel and the EMO stand proud of the skin, so a label
      // behind either is a label nobody sees.
      [hmiX - 0.5, hmiX + 0.5, 1.14, 1.86],
      [emoX - 0.14, emoX + 0.14, 1.03, 1.33],
    ]
    // The front end is glass and frame end to end - nothing is stuck to it.
    if (efem) occupied.push([efem.x0 - 0.02, efem.x1 + 0.02, 0, efem.top])
    // Whatever the silhouette claimed: its placards and instrument fronts.
    for (const [x0, x1, y0, y1] of faceClaims) occupied.push([x0 - 0.03, x1 + 0.03, y0 - 0.03, y1 + 0.03])
    for (let i = 0; i < tool.loadPorts; i++) {
      const px = portX(tool, i)
      occupied.push([px - 0.32, px + 0.32, 0.8, 1.62])
    }
    const clearOf = (x: number, y: number, s: number): boolean =>
      occupied.every(([x0, x1, y0, y1]) => x + s / 2 < x0 || x - s / 2 > x1 || y + s / 2 < y0 || y - s / 2 > y1)

    const put = (x: number, y: number, tile: number, size: number): boolean => {
      if (!clearOf(x, y, size)) return false
      pos.set(x, y, front - 0.014)
      local.compose(pos, quat, one)
      attach.stickers.push({ frame: frame.clone().multiply(local), tile, size })
      return true
    }

    // The face's usable width: the mainframe skin, short of its own edges.
    const lo = skin[0] + 0.22
    const hi = skin[1] - (efem ? 0.12 : 0.95)
    if (hi > lo + 0.3) {
      // A rating plate and a barcode low down, where the frame is - the two
      // every tool carries whatever it does.
      put(lo + 0.11, 0.62, 4, 0.2)
      put(lo + 0.38, 0.59, 2, 0.18)

      const pool = [0, 1, 3, 5, 6, 7, 10]
      if (tool.kind === 'litho') pool.push(9, 9)
      if (tool.kind === 'furnace' || tool.kind === 'epi' || tool.kind === 'crystal') pool.push(11, 11)

      // CLUSTERED, not sprinkled. Labels on real hardware arrive in groups -
      // whoever was last inside a panel puts theirs next to the last one, so
      // a face ends up with two or three dense patches and a lot of clear
      // metal, never an even dusting. Evenly scattered they read as texture;
      // in clumps they read as history.
      const clump = (cx: number, cy: number, n: number, spread: number): void => {
        let placed = 0
        // Several attempts per sticker: a clump that lands on a load port or
        // the tag now gets rejected rather than drawn over it, and without
        // retries a tool with wide ports would simply lose its labels.
        for (let attempt = 0; attempt < n * 6 && placed < n; attempt++) {
          const x = cx + (rnd() - 0.5) * spread
          const y = cy + (rnd() - 0.5) * spread * 0.7
          if (x < lo || x > hi) continue
          if (put(x, y, pool[Math.floor(rnd() * pool.length)], 0.1 + rnd() * 0.06)) placed++
        }
      }
      const span = Math.max(hi - lo, 0.4)
      // Beside the load ports at working height, where a hand goes.
      clump(lo + 0.16 + rnd() * 0.1, 1.22, 3, 0.24)
      // On the high strip between the port labels and the tag.
      clump(lo + span * (0.25 + rnd() * 0.35), bodyH - 0.33, 3, 0.34)
      // And a last group low, off to one side.
      clump(lo + span * (0.55 + rnd() * 0.3), 0.6, 3, 0.3)
    }
  }

  // --- HMI: the screen the technician actually drives the tool from ---
  //
  // AT THE FRONT END'S OUTBOARD CORNER, on the tool that has one: a
  // technician drives a tool from where they load it, and the screen on a
  // real one swings on an arm off the end of the EFEM, clear of the port
  // doors. Hung at bodyW/2 - 0.4 it sat over the last port instead.
  if (open) {
    // Nothing to hang an arm from: a floor pedestal under the bezel.
    const post = new THREE.Mesh(box(0.08, 1.5, 0.08), mats.steel)
    post.position.set(hmiX, 0.75, front - 0.62)
    g.add(post)
    const base = new THREE.Mesh(box(0.4, 0.04, 0.4), mats.steel)
    base.position.set(hmiX, 0.02, front - 0.62)
    g.add(base)
  } else {
    const hmiArm = new THREE.Mesh(box(0.08, 0.08, 0.6), mats.steel)
    hmiArm.position.set(hmiX, 1.5, front - 0.3)
    g.add(hmiArm)
  }

  const bezel = new THREE.Mesh(box(0.86, 0.58, 0.05), mats.toolPanel)
  bezel.position.set(hmiX, 1.5, front - 0.62)
  bezel.rotation.x = -0.16
  g.add(bezel)

  /*
   * The screen sits ON the bezel, so its orientation is taken FROM the bezel
   * rather than written out again.
   *
   * Two bugs came out of doing it by hand. A PlaneGeometry faces +Z, so a
   * screen mounted on the aisle side without a half-turn faces into the tool
   * and is invisible - which is how every HMI in this fab stayed blank. And
   * guessing the tilt that survives that half-turn got the sign wrong, leaving
   * the screen and its bezel intersecting: they z-fought, and the top third of
   * the screen was eaten by the panel behind it.
   *
   * Copying the bezel's quaternion and turning it about its own axis makes both
   * exact by construction, and the standoff is then measured along the bezel's
   * own normal so it cannot depend on which way the tool faces.
   */
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.78, 0.5), mats.screen.clone())
  screen.quaternion.copy(bezel.quaternion)
  screen.rotateY(Math.PI)
  const outward = new THREE.Vector3(0, 0, -1).applyQuaternion(bezel.quaternion)
  screen.position.copy(bezel.position).addScaledVector(outward, 0.032)
  screen.userData.isHmiScreen = true
  g.add(screen)

  // --- status tower ---
  //
  // On the roof at the face's far end; on the HMI pedestal for an island
  // with no roof, the way a stack light shares a post with a panel.
  // ON THE ROOF AT THE FRONT END'S END, and BEHIND THE TRANSPORT'S LINE.
  //
  // A stack light belongs over the part of the tool a technician works at,
  // so it moved from the far corner to the front end's - but it cannot move
  // FORWARD with it. `front - 0.15` put it 2.55 m off the aisle centre, and
  // the vehicles sweep 2.105 to 2.855 (`OHT_SWEEP.zHalf` about `OHT_R`) at
  // 2.09 m and up: every carrier on the loop drove through every tower on
  // the floor. `front + 0.25` is 2.95 m out, which is where it was and why.
  const towerAt: [number, number, number] = open
    ? [hmiX, 1.82, front - 0.62]
    : efem
      ? [efem.x1 - 0.2, bodyH, front + 0.25]
      : [-bodyW / 2 + 0.25, bodyH, front + 0.25]
  if (attach) {
    local.makeTranslation(...towerAt)
    attach.towers.push({ frame: frame.clone().multiply(local), accent: tool.accent, toolId: tool.id })
  } else {
    const tower = buildStatusTower(tool.accent)
    tower.position.set(...towerAt)
    g.add(tower)
  }

  // --- EMO ---
  //
  // The emergency-off: a red mushroom on a yellow collar, on the front of
  // every tool at hand height. The one control a technician is trained to
  // find without looking, and the one thing every real tool front has that
  // a rendered box does not.
  // On the skin beside the HMI, within reach of whoever is standing at the
  // screen, and never on the front end's glass.
  if (tool.kind !== 'gowning') {
    if (attach) {
      // The button's frame: on the front face, its axis (+z) pointing out.
      local.makeRotationY(Math.PI).setPosition(emoX, 1.18, front)
      attach.emos.push(frame.clone().multiply(local))
    } else {
      const collar = new THREE.Mesh(cyl(0.075, 0.075, 0.016, 18), mats.levellingPad)
      collar.rotation.x = Math.PI / 2
      collar.position.set(emoX, 1.18, front - 0.008)
      g.add(collar)
      const mushroom = new THREE.Mesh(cyl(0.042, 0.048, 0.04, 18), EMO_RED)
      mushroom.rotation.x = Math.PI / 2
      mushroom.position.set(emoX, 1.18, front - 0.036)
      g.add(mushroom)
    }
  }

  // Pending tools read as unpowered: no glow, no FOUPs lit.
  if (tool.status === 'pending') {
    band.material = new THREE.MeshStandardMaterial({ color: 0xb9c0c8, roughness: 0.7 })
  }

  mergeStatics(g, mats, [UNLIT, GRATE, EMO_RED, CAVITY_DARK, GOLD_PLATE, CAN_ALU, SOURCE_PAINT, GAUGE_FACE, GAUGE_NEEDLE, HOSE_BLUE, HOSE_RED, ...VENDOR_LIVERY, ...placardCache.values()])
  return g
}

/**
 * Fold a tool's static parts into one mesh per shared material.
 *
 * A tool is thirty to forty little meshes - mainframe, shelves, chambers,
 * lids, arms, posts - and twenty-one tools in one frustum (the view down
 * the hall from either end) was over eight hundred draw calls before a
 * single wall was counted. Draw calls are what the integrated GPU pays
 * for, and that view ran at 43 fps on the lean tier. Everything that
 * shares one of the floor's common materials and never moves is merged
 * here, in the tool's own frame, so a tool becomes about five draws plus
 * the handful of parts that have to stay their own object: the HMI screen
 * (its own canvas), the placard and accent band (their own materials), the
 * status tower (per-tool lamps), and anything that spins.
 *
 * Picking is unaffected - it intersects the tool's group, and a merged
 * mesh is still inside it. Shadows follow the parts: a merged mesh casts if
 * any of its parts did.
 */
export function mergeStatics(g: THREE.Group, mats: FabMaterials, extra: THREE.Material[] = []): void {
  const shared = new Set<THREE.Material>([...Object.values(mats), ...extra])
  const byMaterial = new Map<THREE.Material, { geometries: THREE.BufferGeometry[]; meshes: THREE.Mesh[] }>()
  for (const child of g.children) {
    const mesh = child as THREE.Mesh
    if (!mesh.isMesh || (mesh as THREE.InstancedMesh).isInstancedMesh) continue
    if (Array.isArray(mesh.material) || !shared.has(mesh.material)) continue
    if (mesh.userData.spin !== undefined || mesh.userData.isHmiScreen) continue
    mesh.updateMatrix()
    const geometry = mesh.geometry.clone().applyMatrix4(mesh.matrix)
    const bucket = byMaterial.get(mesh.material) ?? { geometries: [], meshes: [] }
    bucket.geometries.push(geometry)
    bucket.meshes.push(mesh)
    byMaterial.set(mesh.material, bucket)
  }
  for (const [material, { geometries, meshes }] of byMaterial) {
    if (meshes.length < 2) continue
    const merged = mergeGeometries(geometries, false)
    if (!merged) continue
    const mesh = new THREE.Mesh(merged, material)
    mesh.castShadow = meshes.some((m) => m.castShadow)
    mesh.receiveShadow = meshes.some((m) => m.receiveShadow)
    for (const m of meshes) g.remove(m)
    g.add(mesh)
  }
}

/**
 * Every docked carrier and every EMO on the floor, as a handful of instanced
 * draws. The parts mirror `buildFoup` exactly, so a carrier on a load port
 * and one in a hoist are the same object; the EMO mirrors the per-tool
 * fallback in `buildTool`.
 */
export interface AttachmentsGroup {
  group: THREE.Group
  /** Light the tower of one tool for what it is doing. */
  setTower(toolId: string, state: TowerState): void
  /** Flash the alarms; once a frame. */
  animate(t: number): void
}

export function buildAttachments(mats: FabMaterials, attach: Attachments): AttachmentsGroup {
  const g = new THREE.Group()
  const instanced = (
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    frames: THREE.Matrix4[],
    offset: THREE.Matrix4,
    shadow = false,
  ): void => {
    if (frames.length === 0) return
    const mesh = new THREE.InstancedMesh(geometry, material, frames.length)
    const m = new THREE.Matrix4()
    frames.forEach((frame, i) => mesh.setMatrixAt(i, m.copy(frame).multiply(offset)))
    mesh.instanceMatrix.needsUpdate = true
    mesh.castShadow = shadow
    g.add(mesh)
  }
  const at = (x: number, y: number, z: number) => new THREE.Matrix4().makeTranslation(x, y, z)
  const facing = (x: number, y: number, z: number) =>
    new THREE.Matrix4().makeRotationX(Math.PI / 2).setPosition(x, y, z)

  // Every printed label in the building, merged into one mesh.
  //
  // Alpha-CUT rather than blended: each tile has transparent margins so a
  // triangle can be a triangle inside its square, but a cutout sorts like
  // an opaque surface and costs nothing extra, where a blended one would
  // have to be sorted against every other transparent thing in the hall.
  if (attach.stickers.length > 0) {
    const quads = attach.stickers.map((s) => stickerQuad(s.tile, s.size).applyMatrix4(s.frame))
    const merged = new THREE.Mesh(
      mergeGeometries(quads, false),
      // LIT, unlike every other printed mark in the hall. The hazard
      // placards stay unlit on purpose - a safety mark that goes grey in a
      // dim bay stops teaching - but a barcode, a calibration date or a
      // notice has no such claim, and an unlit label on a lit panel is the
      // last thing that says "decal": it never shades with the skin it is
      // stuck to, and under the amber safelight it stays paper-white while
      // the whole room turns. Matte paper, no metal.
      new THREE.MeshStandardMaterial({ map: stickerAtlas(), transparent: false, alphaTest: 0.5, roughness: 0.62, metalness: 0 }),
    )
    g.add(merged)
  }

  // Carriers.
  instanced(box(0.36, 0.32, 0.36), mats.foup, attach.foups, at(0, 0, 0), true)
  instanced(box(0.3, 0.26, 0.012), mats.conduitDark, attach.foups, at(0, -0.01, -0.185))
  instanced(cyl(0.07, 0.07, 0.02, 14), mats.steel, attach.foups, at(0, 0.19, 0))
  instanced(cyl(0.03, 0.03, 0.02, 10), mats.steel, attach.foups, at(0, 0.17, 0))
  for (const dx of [-1, 1]) {
    instanced(box(0.02, 0.05, 0.16), mats.toolPanel, attach.foups, at(dx * 0.19, 0.02, 0))
  }

  // EMOs: the frame's +z points out of the panel, and a cylinder's axis is
  // y, so each part is turned to face out and pushed proud of the skin.
  instanced(cyl(0.075, 0.075, 0.016, 18), mats.levellingPad, attach.emos, facing(0, 0, 0.008))
  instanced(cyl(0.042, 0.048, 0.04, 18), EMO_RED, attach.emos, facing(0, 0, 0.036))

  // Status towers, mirroring `buildStatusTower`; the cap carries each
  // tool's accent as an instance colour.
  const towerFrames = attach.towers.map((t) => t.frame)
  instanced(new THREE.CylinderGeometry(0.028, 0.028, 0.2, 8), TOWER_POST, towerFrames, at(0, 0.1, 0))
  // The lamps: one instanced mesh per level, each tool's lamp lit or dark
  // through its instance colour. Every tower starts amber - idle - which
  // is what a floor of tools waiting for lots looks like.
  const lampMeshes: THREE.InstancedMesh[] = []
  TOWER_LAMPS.forEach((material, i) => {
    if (towerFrames.length === 0) return
    const mesh = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.062, 0.062, 0.1, 12), material, towerFrames.length)
    const m = new THREE.Matrix4()
    towerFrames.forEach((frame, k) => {
      mesh.setMatrixAt(k, m.copy(frame).multiply(at(0, 0.25 + i * 0.105, 0)))
      mesh.setColorAt(k, i === 1 ? TOWER_COLOURS[1] : TOWER_DARK[i])
    })
    mesh.instanceMatrix.needsUpdate = true
    g.add(mesh)
    lampMeshes.push(mesh)
  })
  const towerIndex = new Map(attach.towers.map((t, i) => [t.toolId, i]))
  const towerStates = new Map<string, TowerState>()
  const lit = (index: number, level: number, on: boolean): void => {
    const mesh = lampMeshes[level]
    if (!mesh) return
    mesh.setColorAt(index, on ? TOWER_COLOURS[level] : TOWER_DARK[level])
    mesh.instanceColor!.needsUpdate = true
  }
  const setTower = (toolId: string, state: TowerState): void => {
    const index = towerIndex.get(toolId)
    if (index === undefined || towerStates.get(toolId) === state) return
    towerStates.set(toolId, state)
    lit(index, 0, state === 'running')
    lit(index, 1, state === 'idle')
    lit(index, 2, state === 'alarm' || state === 'down')
  }
  let flashPhase = false
  const animate = (t: number): void => {
    // Alarms flash at 1 Hz; a held red is a tool down, not an alarm.
    const phase = Math.floor(t * 2) % 2 === 0
    if (phase === flashPhase) return
    flashPhase = phase
    for (const [toolId, state] of towerStates) {
      if (state !== 'alarm') continue
      const index = towerIndex.get(toolId)
      if (index !== undefined) lit(index, 2, phase)
    }
  }
  if (attach.towers.length > 0) {
    const caps = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(0.08, 0.08, 0.03, 12),
      TOWER_CAP,
      attach.towers.length,
    )
    const m = new THREE.Matrix4()
    const colour = new THREE.Color()
    attach.towers.forEach((t, i) => {
      caps.setMatrixAt(i, m.copy(t.frame).multiply(at(0, 0.55 + 3 * 0.12, 0)))
      caps.setColorAt(i, colour.set(t.accent))
    })
    caps.instanceMatrix.needsUpdate = true
    g.add(caps)
  }

  return { group: g, setTower, animate }
}

/**
 * Interaction volume for a tool. Kept separate from the visual mesh so picking
 * stays cheap and forgiving - a student should not have to aim at a bezel.
 */
export function buildToolTrigger(tool: Tool): THREE.Mesh {
  const trigger = new THREE.Mesh(
    box(5.5, 2.6, 4.6),
    // DoubleSide matters: the raycaster honours material side, so a front-face
    // only volume is invisible to a ray cast from inside it. A student standing
    // right at the tool - the most likely place to press E - would be inside
    // this box and find the tool would not open.
    new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide }),
  )
  trigger.position.set(tool.position[0], 1.3, tool.position[2] + aisleDirection(tool) * 1.6)
  trigger.userData.toolId = tool.id
  return trigger
}

/** Gowning airlock at the head of the aisle. */
export function buildGowning(mats: FabMaterials, position: THREE.Vector3Tuple): THREE.Group {
  const g = new THREE.Group()
  g.position.set(...position)

  const bench = new THREE.Mesh(box(4.2, 0.45, 0.6), mats.toolPanel)
  bench.position.set(0, 0.45, -1.6)
  g.add(bench)

  // Gowning lockers.
  for (let i = 0; i < 6; i++) {
    const locker = new THREE.Mesh(box(0.55, 1.9, 0.5), mats.toolBody)
    locker.position.set(-1.6 + i * 0.6, 0.95, -2.4)
    g.add(locker)
    const handle = new THREE.Mesh(box(0.04, 0.16, 0.04), mats.steel)
    handle.position.set(-1.4 + i * 0.6, 1.0, -2.13)
    g.add(handle)
  }

  // Air shower doorway into the cleanroom proper.
  const frameMat = new THREE.MeshStandardMaterial({ color: 0x93a1b0, metalness: 0.6, roughness: 0.3 })
  for (const side of [-1, 1]) {
    const jamb = new THREE.Mesh(box(0.18, 2.4, 0.3), frameMat)
    jamb.position.set(side * 1.3, 1.2, 1.8)
    g.add(jamb)
  }
  const lintel = new THREE.Mesh(box(2.8, 0.3, 0.3), frameMat)
  lintel.position.set(0, 2.55, 1.8)
  g.add(lintel)

  return g
}

/**
 * Lighting rig. Litho bays get their characteristic yellow.
 *
 * Returns the per-bay downlights so the scene can cull them by distance. That
 * is not an optimisation for its own sake: the hall is now 136 m long with a
 * downlight over every bay, and a WebGL shader evaluates every enabled light
 * for every fragment whether or not it can reach it. Twenty point lights is a
 * real cost on the integrated graphics this project promises to run on, and
 * only the handful nearest the student can be seen at any moment.
 */
export interface LightingRig {
  hemi: THREE.HemisphereLight
  key: THREE.DirectionalLight
  lamps: THREE.PointLight[]
}

export function buildLighting(scene: THREE.Object3D, tools: Tool[]): LightingRig {
  // Colours and intensities here are placeholders: `FabScene.applyLighting`
  // repaints the whole rig from the active preset before the first frame.
  const hemi = new THREE.HemisphereLight(0xf4f7fa, 0x9aa3ad, 0.72)
  scene.add(hemi)

  const key = new THREE.DirectionalLight(0xffffff, 0.42)
  key.position.copy(KEY_OFFSET)
  key.castShadow = true
  key.shadow.mapSize.set(1024, 1024)
  key.shadow.camera.near = 1
  key.shadow.camera.far = 60
  const s = 26
  key.shadow.camera.left = -s
  key.shadow.camera.right = s
  key.shadow.camera.top = s
  key.shadow.camera.bottom = -s
  scene.add(key)

  // A soft downlight over each bay so tools read as lit from the FFU ceiling.
  const lamps: THREE.PointLight[] = []
  for (const st of tools) {
    const lamp = new THREE.PointLight(0xffffff, 11, 15, 2)
    // Over the bay floor in front of the face, not over the tool's own
    // centre: since the bulkhead the centre is behind the wall, and a lamp
    // behind the face plane lights nothing a student can see.
    const z = st.position[2] === 0 ? 0 : Math.sign(st.position[2]) * BAY_ROW_Z
    lamp.position.set(st.position[0], FAB.ceilingHeight - 0.4, z)
    // Marked so a preset change can repaint every lamp except this one: the
    // litho bay's amber is process physics (resist is blind to it), not mood,
    // and it stays amber under every sky.
    lamp.userData.litho = false
    scene.add(lamp)
    lamps.push(lamp)
  }

  // Real light down the aisle. The luminaire panels overhead are unlit
  // geometry - a few hundred true sources is not a thing forward-rendered
  // WebGL can afford - so every fifth fixture column over the walkway gets an
  // actual PointLight. These are what put the repeating pools of glare on the
  // polished floor as the student walks: reflections of light that exists,
  // not paint. Dimmer than the bay downlights (userData.dim) and culled on a
  // shorter radius (userData.cullRadius), so the per-fragment light count the
  // integrated-graphics budget was built around stays roughly where it was.
  const aisleZ = CEILING_MODULE_Z / 2
  // The ballroom's outer aisle is lit like the main one, both tiers.
  const aisleRows = [-aisleZ, aisleZ]
  if (BALLROOM) {
    const outer = outerAisleZ()
    aisleRows.push(-(outer - aisleZ), -(outer + aisleZ), outer - aisleZ, outer + aisleZ)
  }
  for (let x = -FAB.length / 2 + 1.2; x < FAB.length / 2; x += CEILING_MODULE_X * 5) {
    for (const z of aisleRows) {
      const lamp = new THREE.PointLight(0xffffff, 4, 10, 2)
      lamp.position.set(x, FAB.ceilingHeight - 0.25, z)
      lamp.userData.litho = false
      lamp.userData.dim = 0.5
      // Its falloff is 10 m; beyond that it is shaded for nothing.
      lamp.userData.cullRadius = 10.5
      scene.add(lamp)
      lamps.push(lamp)
    }
  }

  // The high-quality tier: extra downlights, built now and switched by the
  // graphics-quality governor rather than rebuilt - an invisible light costs
  // the GPU nothing, so the whole set can exist up front and a tier change
  // is a visibility flip. `userData.quality = 'high'` marks every lamp in
  // it; FabScene.updateLights keeps them dark on the lean tier.
  //
  //   - the aisle filled in to every second fixture column (skipping the
  //     columns the baseline set already covers), and
  //   - a sparse row down each bay, where the lean tier has only the one
  //     downlight per tool.
  const highLamp = (x: number, z: number, dim: number, cull: number): void => {
    const lamp = new THREE.PointLight(0xffffff, 4, 10, 2)
    lamp.position.set(x, FAB.ceilingHeight - 0.25, z)
    lamp.userData.litho = false
    lamp.userData.dim = dim
    lamp.userData.cullRadius = cull
    lamp.userData.quality = 'high'
    scene.add(lamp)
    lamps.push(lamp)
  }
  const cols = Math.floor(FAB.length / CEILING_MODULE_X)
  for (let c = 0; c < cols; c += 2) {
    if (c % 10 === 0) continue // the baseline set already sits here
    const x = -FAB.length / 2 + (c + 0.5) * CEILING_MODULE_X
    for (const z of aisleRows) highLamp(x, z, 0.35, 10.5)
  }
  const bayZ = BULKHEAD_Z - 1.0
  for (let c = 2; c < cols; c += 5) {
    const x = -FAB.length / 2 + (c + 0.5) * CEILING_MODULE_X
    highLamp(x, -bayZ, 0.35, 10.5)
    highLamp(x, bayZ, 0.35, 10.5)
  }

  return { hemi, key, lamps }
}
